import {
  Component,
  signal,
  computed,
  inject,
  OnInit,
  OnDestroy,
  effect,
  ElementRef,
  untracked,
  ViewChild,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  ReactiveFormsModule,
  FormsModule,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { forkJoin, of, timeout, TimeoutError, Subject } from 'rxjs';
import { catchError, map, finalize, takeUntil, switchMap } from 'rxjs/operators';
import { WizardStepService, WizardStep } from '../../../services/wizard-step.service';
import { MessageService } from 'primeng/api';
import { SupplierRegistryService } from '../../../services/supplier-registry.service';
import { ResponsibleRegistryService } from '../../../services/responsible-registry.service';
import { SubResponsibleRegistryService } from '../../../services/sub-responsible-registry.service';
import { DocumentTemplateV2Service } from '../../../services/document-template-v2.service';
import { ChecklistService } from '../../../services/checklist.service';
import { ProfessionalsService } from '../../../../core/services/professionals.service';
import { AuthorizationService } from '../../../../core/services/authorization.service';
import { AuthService } from '../../../../core/services/auth.service';
import { NavigationContextService } from '../../../../core/services/navigation-context.service';
import { AppRole } from '../../../../core/models/permissions.model';

import { Responsible } from '../../../models/responsible.model';
import { SubResponsible } from '../../../models/sub-responsible.model';
import { ContractChecklist, ChecklistRow } from '../../../models/checklist.model';
import { ResponsibleFormComponent } from '../../responsibles-registry/responsible-form/responsible-form.component';
import { SubResponsibleFormComponent } from '../../sub-responsibles-registry/sub-responsible-form/sub-responsible-form.component';
import { ContractRegistryService } from '../../../services/contract-registry.service';
import { ContractInvolvedEntity, ContractRequest } from '../../../models/contract.model';
import { isSelectableTemplate, toTemplateSelectionRow } from '../../../models/document-asset-labels';
import { RegistryTableComponent } from '../../../../core/registry-table/registry-table.component';
import { ColumnConfig, TABLE_CONFIG } from '../../dashboard/table-config';
import {
  FormDropdownService,
  FormDropdownItem,
} from '../../../../core/services/form-dropdown.service';
import { AxaEntity, AxaEntityService } from '../../../../core/services/axa-entity.service';
import { GeographyService, Country } from '../../../../core/services/geography.service';
import { FileValidationService } from '../../../../core/services/file-validation.service';
import { ComponentCanDeactivate } from '../../../../core/guards/unsaved-changes.guard';
import { NoSpecialCharsValidator, escapeHtml } from '../../../../core/validators/no-special-chars.validator';
import { ValidationErrorComponent } from '../../../../core/shared/validation-error/validation-error.component';

// PrimeNG Modules
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToastModule } from 'primeng/toast';
import { DatePickerModule } from 'primeng/datepicker';
import { TextareaModule } from 'primeng/textarea';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CheckboxModule } from 'primeng/checkbox';
import { MultiSelectModule } from 'primeng/multiselect';
import { FileUploadModule } from 'primeng/fileupload';
import { FileUpload } from 'primeng/fileupload';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DialogModule } from 'primeng/dialog';
import { DynamicDialogModule, DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { ConfirmationService } from 'primeng/api';
import { MenuItem } from 'primeng/api';
import { ConfirmationDialogService } from '../../../../core/services/confirmation-dialog.service';
import { PaymentRateComponent } from '../../../../components/payment-rate/payment-rate';

@Component({
  selector: 'app-contract-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    InputNumberModule,
    ToastModule,
    DatePickerModule,
    TextareaModule,
    BreadcrumbModule,
    RadioButtonModule,
    CheckboxModule,
    MultiSelectModule,
    FileUploadModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    RegistryTableComponent,
    DialogModule,
    DynamicDialogModule,
    TooltipModule,
    TagModule,
    TableModule,
    ValidationErrorComponent,
    PaymentRateComponent,
  ],
  providers: [MessageService, DialogService],
  templateUrl: './contract-form.component.html',
  styleUrls: ['./contract-form.component.scss'],
})
export class ContractFormComponent implements OnInit, OnDestroy, ComponentCanDeactivate {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private titleService = inject(Title);
  wizardStepService = inject(WizardStepService);
  private messageService = inject(MessageService);
  private supplierRegistryService = inject(SupplierRegistryService);
  private professionalsService = inject(ProfessionalsService);
  private authzService = inject(AuthorizationService);
  private authService = inject(AuthService);
  private navigationContext = inject(NavigationContextService);
  private responsibleRegistryService = inject(ResponsibleRegistryService);
  private subResponsibleRegistryService = inject(SubResponsibleRegistryService);
  private contractRegistryService = inject(ContractRegistryService);
  private documentTemplateV2Service = inject(DocumentTemplateV2Service);
  private checklistService = inject(ChecklistService);
  private confirmationService = inject(ConfirmationService);
  private confirmDialogService = inject(ConfirmationDialogService);
  private elementRef = inject(ElementRef);
  private formDropdownService = inject(FormDropdownService);
  private axaEntityService = inject(AxaEntityService);
  private dialogService = inject(DialogService);
  private geographyService = inject(GeographyService);
  private fileValidationService = inject(FileValidationService);

  // ViewChild for file upload
  @ViewChild('attachmentFileUpload') attachmentFileUpload!: FileUpload;

  // Subject for managing subscriptions lifecycle
  private destroy$ = new Subject<void>();

  // Signals for state management
  currentStep = signal(0);
  maxStepReached = signal(0); // Traccia il massimo step raggiunto nella sessione
  contractId = signal<string | null>(null);
  private isEditingExistingContract = false;
  contractSectionStatus = signal<'DRAFT' | 'FILLED' | null>(null); // Tracks status of Contract Section
  tariffeStatus = signal<'DRAFT' | 'FILLED' | null>(null); // Tracks status of Payment Rates Section
  attoDiNominaExists = signal<boolean>(false); // Tracks if Atto di Nomina already exists in DB
  attoDiNominaStatus = signal<'DRAFT' | 'FILLED' | null>(null); // Tracks status of Atto di Nomina
  private hasSuccessfullySaved = signal(false); // Track if form was successfully saved
  loadedContractData = signal<any | null>(null); // Store loaded contract data for step 3 validation
  suppliers = signal<any[]>([]);
  selectedSupplier = signal<any | null>(null);
  uploadedDocuments = signal<
    Array<{ title: string; file: File; s3Url?: string; uploadedAt?: Date }>
  >([]);
  currentDocumentTitle = signal<string>('');
  showUploadDialog = signal(false);
  isUploadingAttachment = signal(false);
  isLoading = signal<boolean>(false); // Spinner per chiamate API
  loadingMessage = signal<string>('Operazione in corso...'); // Messaggio dinamico dello spinner
  breadcrumbItems = signal<MenuItem[]>([
    { label: 'Registro Contratti', routerLink: `/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry` },
    { label: 'Nuovo Contratto' },
  ]);

  isBackofficeProfessional = computed(() => {
    if (this.navigationContext.isProfessionalContext()) {
      return true;
    }

    if (this.navigationContext.isSupplierContext()) {
      return false;
    }

    const roles = this.authzService.currentUser()?.roles ?? [];
    return (
      roles.includes(AppRole.BACKOFFICE_PROFESSIONAL) ||
      roles.includes('backoffice_professional' as AppRole) ||
      roles.includes('backoffice_professionals' as AppRole)
    );
  });

  // Check if the SELECTED ENTITY is a professional (not the user role)
  // This allows backoffice_admin to see professional-specific fields when working with professionals
  isSelectedEntityProfessional = computed(() => {
    const selected = this.selectedSupplier();
    if (selected) {
      // Primary: use the isProfessional marker from the loaded entity
      return selected.isProfessional === true;
    }
    
    // Fallback for edge cases: check loadedContractData
    // This should rarely be needed since we now set selectedSupplier in edit mode
    const loadedContract = this.loadedContractData();
    if (loadedContract?.generalInfo) {
      return !!loadedContract.generalInfo.professionalId;
    }
    
    // Ultimate fallback: use user role (should never reach here in normal flow)
    return this.isBackofficeProfessional();
  });

  selectionEntityLabel = computed(() =>
    this.isBackofficeProfessional() ? 'Fiduciario' : 'Fornitore',
  );

  selectionListTitle = computed(() =>
    this.isBackofficeProfessional() ? 'Lista Fiduciario' : 'Lista Fornitori',
  );

  selectionListIcon = computed(() =>
    this.isBackofficeProfessional() ? 'pi-users' : 'pi-building',
  );

  // Verifica se lo step corrente è completato
  isStep1Completed = computed(() => {
    const step = this.wizardStepService.steps().find(s => s.id === 1);
    return step?.completed ?? false;
  });

  // Step 2: Tariffe per professional, Atto di Nomina per supplier
  isStep2Completed = computed(() => {
    const stepId = this.isSelectedEntityProfessional() ? this.getTariffeStepId() : this.getAttoDiNominaStepId();
    const step = this.wizardStepService.steps().find(s => s.id === stepId);
    return step?.completed ?? false;
  });

  // Step 3: Atto di Nomina per professional, Gestione Documenti per supplier
  isStep3Completed = computed(() => {
    const stepId = this.isSelectedEntityProfessional() ? this.getAttoDiNominaStepId() : this.getGestioneDocumentiStepId();
    const step = this.wizardStepService.steps().find(s => s.id === stepId);
    return step?.completed ?? false;
  });

  // Step 4: Gestione Documenti per professional (non esiste per supplier)
  isStep4Completed = computed(() => {
    const step = this.wizardStepService.steps().find(s => s.id === 4);
    return step?.completed ?? false;
  });

  // Fiduciario selezionato: ID e Categoria (solo per professional)
  selectedProfessionalId = computed(() => {
    const selected = this.selectedSupplier();
    return selected?.id || '';
  });

  selectedProfessionalBusinessName = computed(() => {
    const selected = this.selectedSupplier();
    if (!selected) return '';

    return (
      selected.businessName ||
      selected.supplierName ||
      selected.displayName ||
      [selected.lastName, selected.firstName].filter(Boolean).join(' ')
    );
  });

  selectedProfessionalCategory = computed(() => {
    const selected = this.selectedSupplier();
    if (!selected?.type) return '';
    
    // Usa il servizio FormDropdownService per decodifica centralizzata
    return this.formDropdownService.decodeProfessionalTypeSync(selected.type);
  });

  // Fornitore selezionato: ragione sociale + sigla tipo società e ID (solo per supplier)
  private selectedSupplierHeader = signal<{ id: string; displayName: string } | null>(null);

  selectedSupplierId = computed(() => {
    const selected = this.selectedSupplier();
    if (!selected || selected.isProfessional === true || selected.id == null) return '';
    return String(selected.id);
  });

  selectedSupplierDisplayName = computed(() => {
    const header = this.selectedSupplierHeader();
    if (!header || header.id !== this.selectedSupplierId()) return '';
    return header.displayName;
  });

  // Computed properties per verificare se gli step sono accessibili
  canAccessStep1 = computed(() => {
    // Step 1 (Contratto) accessibile solo se è stato selezionato un fornitore/fiduciario
    return this.selectedSupplier() !== null;
  });

  canAccessTariffeStep = computed(() => {
    // Step Tariffe accessibile solo se:
    // 1. Fornitore/fiduciario selezionato
    // 2. Contratto salvato (DRAFT o FILLED)
    return this.selectedSupplier() !== null && this.contractSectionStatus() !== null;
  });

  canAccessAttoDiNominaStep = computed(() => {
    // Step Atto di Nomina accessibile solo se prerequisiti precedenti soddisfatti
    if (!this.selectedSupplier()) return false;
    
    if (this.isBackofficeProfessional()) {
      // Per professional: richiede solo Contratto salvato
      return this.contractSectionStatus() !== null;
    } else {
      // Per supplier: richiede Contratto salvato + GDPR abilitato
      return this.contractSectionStatus() !== null && this.shouldShowAttoDiNomina();
    }
  });

  canAccessGestioneDocumentiStep = computed(() => {
    // Step Gestione Documenti accessibile solo se:
    // 1. Fornitore/fiduciario selezionato
    // 2. Contratto salvato (DRAFT o FILLED)
    return this.selectedSupplier() !== null && this.contractSectionStatus() !== null;
  });

  // Salvataggio valori originali per ogni step
  private originalFormValues = new Map<number, any>();

  // Forms
  step0Form!: FormGroup; // Selezione Fornitore
  step1Form!: FormGroup; // Contratto
  step2Form!: FormGroup; // Atto di Nomina
  step3Form!: FormGroup; // Gestione Documenti

  // Options - Dynamic dropdowns loaded from database
  tipoContrattoOptions = signal<FormDropdownItem[]>([]);
  serviceTypes = signal<FormDropdownItem[]>([]);
  attivitaEsterOptions = signal<FormDropdownItem[]>([]);
  preavvisoRinnovoOptions = signal<FormDropdownItem[]>([]);
  statusOptions = signal<FormDropdownItem[]>([]);
  entityOptions = signal<AxaEntity[]>([]);
  sinergieCompagnieOptions = signal<FormDropdownItem[]>([]);
  tipoFornitoreOptions = signal<FormDropdownItem[]>([]);
  activityDescriptionOptions = signal<FormDropdownItem[]>([]);
  mainPurposeOptions = signal<FormDropdownItem[]>([]);
  toolGestionaliOptions = signal<FormDropdownItem[]>([]);
  networkOptions = signal<FormDropdownItem[]>([]);
  itSystemsUsedOptions = signal<FormDropdownItem[]>([]);

  // Template options (loaded dynamically from API)
  templateContrattoOptions = signal<Array<{ label: string; value: string }>>([]);
  templateAttoNominaOptions = signal<Array<{ label: string; value: string }>>([]);

  // Dialog template contratto
  showTemplateContrattoDialog = signal<boolean>(false);
  selectedTemplateContratto = signal<any>(null);

  // Dialog template atto di nomina
  showTemplateAttoNominaDialog = signal<boolean>(false);
  selectedTemplateAttoNomina = signal<any>(null);

  isGeneratingWord = signal(false);

  // Checklist dialog
  showChecklistDialog = signal<boolean>(false);
  checklistData = signal<ContractChecklist | null>(null);
  loadingChecklist = signal<boolean>(false);
  checklistHasUnsavedChanges = signal<boolean>(false);
  originalChecklistData = signal<ContractChecklist | null>(null);

  // Non-signal getter/setter for two-way binding with p-dialog
  get showChecklistDialogValue(): boolean {
    return this.showChecklistDialog();
  }
  set showChecklistDialogValue(value: boolean) {
    this.showChecklistDialog.set(value);
  }

  periodicitaFatturaOptions = signal<FormDropdownItem[]>([]);
  currencyOptions = signal<FormDropdownItem[]>([]);
  areaClaimsOptions = signal<FormDropdownItem[]>([]);
  casoUsoVRFOptions = signal<FormDropdownItem[]>([]);
  rinnovoTacitoOptions = signal<FormDropdownItem[]>([]);
  gestioneInCaricoOptions = signal<FormDropdownItem[]>([]);
  struttureCoinvolteOptions = signal<FormDropdownItem[]>([]);
  tipologiaRipartizioneOptions = signal<FormDropdownItem[]>([]);
  tipoTitolaritaOptions = signal<FormDropdownItem[]>([]);
  // controllerOwnershipTypeOptions - usa le stesse opzioni dei suppliers (tipoTitolaritaOptions)
  controllerOwnershipTypeOptions = computed(() => this.tipoTitolaritaOptions());
  luogoTrattamentoOptions = signal<FormDropdownItem[]>([]);

  // Atto di Nomina dropdown options
  deletionMethodOptions = signal<FormDropdownItem[]>([]);
  processedDataTypesOptions = signal<FormDropdownItem[]>([]);
  referenceControllerOptions = signal<FormDropdownItem[]>([]);
  responsibleOptions = signal<FormDropdownItem[]>([]); // Carica dal Registro Responsabili
  subResponsibleOptions = signal<FormDropdownItem[]>([]); // Carica dal Registro Sub-Responsabili
  responsibleTypeOptions = signal<FormDropdownItem[]>([]);
  criticalSupplierQualificationOptions = signal<FormDropdownItem[]>([]);
  subDataProcessingLocationOptions = signal<FormDropdownItem[]>([]);
  subjectCategoryOptions = signal<FormDropdownItem[]>([]);
  recipientCategoryOptions = signal<FormDropdownItem[]>([]);
  nonEuCountriesOptions = signal<FormDropdownItem[]>([]); // Paesi non-UE per sub-responsabili
  companyTypeOptions = signal<FormDropdownItem[]>([]);

  private readonly professionalProcessedDataTypesMap: Record<string, string[]> = {
    BODYSHOP: ['IDENTIFICATION_DATA', 'CONTACT_DATA', 'CLAIM_DATA'],
    NK_BODYSHOP: ['IDENTIFICATION_DATA', 'CONTACT_DATA', 'CLAIM_DATA'],
    HAIL_PROVIDER: ['IDENTIFICATION_DATA', 'CONTACT_DATA', 'CLAIM_DATA'],
    INVESTIGATOR: ['IDENTIFICATION_DATA', 'CONTACT_DATA', 'CLAIM_DATA', 'HEALTH_DATA'],
    LAWYER: ['IDENTIFICATION_DATA', 'CONTACT_DATA', 'CLAIM_DATA'],
    DOCTOR: ['IDENTIFICATION_DATA', 'CONTACT_DATA', 'HEALTH_DATA', 'CLAIM_DATA'],
    AUTO_EXPERT: ['IDENTIFICATION_DATA', 'CONTACT_DATA', 'CLAIM_DATA'],
    RE_EXPERT: ['IDENTIFICATION_DATA', 'CONTACT_DATA', 'CLAIM_DATA'],
  };

  private readonly professionalSubjectCategoriesMap: Record<string, string[]> = {
    BODYSHOP: ['CLIENTI', 'SOGGETTI_CORRELATI_AL_SINISTRO', 'PATROCINATORI'],
    NK_BODYSHOP: ['CLIENTI', 'SOGGETTI_CORRELATI_AL_SINISTRO', 'PATROCINATORI'],
    HAIL_PROVIDER: ['CLIENTI', 'SOGGETTI_CORRELATI_AL_SINISTRO', 'PATROCINATORI'],
    INVESTIGATOR: ['CLIENTI', 'SOGGETTI_CORRELATI_AL_SINISTRO', 'TESTIMONI', 'PATROCINATORI'],
    LAWYER: ['CLIENTI', 'SOGGETTI_CORRELATI_AL_SINISTRO', 'PATROCINATORI'],
    DOCTOR: ['CLIENTI', 'SOGGETTI_CORRELATI_AL_SINISTRO', 'PATROCINATORI'],
    AUTO_EXPERT: ['CLIENTI', 'SOGGETTI_CORRELATI_AL_SINISTRO', 'PATROCINATORI'],
    RE_EXPERT: ['CLIENTI', 'SOGGETTI_CORRELATI_AL_SINISTRO', 'TESTIMONI', 'PATROCINATORI'],
  };

  // Keep yesNoOptions as static for boolean values
  yesNoOptions = [
    { label: 'Sì', value: true },
    { label: 'No', value: false },
  ];

  // Colonne per tabella fornitori - stessa configurazione del registro fornitori
  supplierColumns: ColumnConfig[] = [];

  // Dialog responsabili - riuso componente registry-table
  showResponsibleDialog = signal<boolean>(false);
  selectedResponsible = signal<any>(null);
  responsibleColumns: ColumnConfig[] = [];
  responsibleTableData = signal<any[]>([]);
  loadingResponsibles = signal<boolean>(false);
  responsibleDialogRef: DynamicDialogRef | null = null;

  // Dialog sub-responsabili - stessa logica dei responsabili
  showSubResponsibleDialog = signal<boolean>(false);
  selectedSubResponsible = signal<any>(null);
  subResponsibleColumns: ColumnConfig[] = [];
  subResponsibleTableData = signal<any[]>([]);
  loadingSubResponsibles = signal<boolean>(false);
  subResponsibleDialogRef: DynamicDialogRef | null = null;
  currentSubResponsibleIndex: number | null = null; // Index per sostituzione sub-responsabile

  // Dialog template contratto - riuso componente registry-table
  templateContrattoColumns: ColumnConfig[] = [];
  templateContrattoTableData = signal<any[]>([]);

  // Dialog template atto di nomina - riuso componente registry-table
  templateAttoNominaColumns: ColumnConfig[] = [];
  templateAttoNominaTableData = signal<any[]>([]);

  constructor() {
    // Effect per sincronizzare currentStep con wizardStepService
    effect(() => {
      const step = this.currentStep();
      untracked(() => {
        this.wizardStepService.setStep(step);
      });
    });

    // Effect per caricare i dati di intestazione del fornitore selezionato (non attivo per i fiduciari)
    effect(() => {
      const supplierId = this.selectedSupplierId();
      const selected = this.selectedSupplier();
      untracked(() => {
        this.loadSelectedSupplierHeader(supplierId, selected);
      });
    });

    // Effect per aggiornare gli step in base agli status di salvataggio
    effect(() => {
      // Monitor tutti i computed che determinano l'accessibilità degli step
      const canAccessStep1 = this.canAccessStep1();
      const canAccessTariffe = this.canAccessTariffeStep();
      const canAccessAttoDiNomina = this.canAccessAttoDiNominaStep();
      const canAccessGestioneDocumenti = this.canAccessGestioneDocumentiStep();
      const showAttoDiNomina = this.shouldShowAttoDiNomina(); // Per GDPR check
      const hasSelectedSupplier = this.selectedSupplier() !== null;
      const isEditingContract = this.contractId() !== null;
      const entityType = this.isBackofficeProfessional() ? 'fiduciario' : 'fornitore';

      untracked(() => {
        const steps = this.wizardStepService.steps();
        const updatedSteps = steps.map((step) => {
          if (step.id === 0) {
            return {
              ...step,
              disabled: isEditingContract,
              tooltipMessage: isEditingContract
                ? `Il ${entityType} non può essere modificato per un contratto già creato`
                : undefined,
            };
          }

          // Step Contratto (id 1)
          if (step.id === 1) {
            return {
              ...step,
              disabled: !canAccessStep1,
              tooltipMessage: !canAccessStep1
                ? `Per accedere allo step Contratto devi prima selezionare il ${entityType}`
                : undefined,
            };
          }

          // Step Tariffe (id 2 per professional)
          if (step.id === this.getTariffeStepId() && this.isBackofficeProfessional()) {
            let tooltipMessage: string | undefined;
            if (!hasSelectedSupplier) {
              tooltipMessage = 'Per accedere allo step Tariffe devi prima selezionare il fiduciario';
            } else if (!canAccessTariffe) {
              tooltipMessage = 'Salva almeno in bozza il Contratto per accedere alle Tariffe';
            }
            
            return {
              ...step,
              disabled: !canAccessTariffe,
              tooltipMessage,
            };
          }

          // Step Atto di Nomina (id 3 per professional, id 2 per supplier)
          if (step.id === this.getAttoDiNominaStepId()) {
            let disabled = !canAccessAttoDiNomina;
            let tooltipMessage: string | undefined;

            // Priorità dei messaggi:
            // 1. Fornitore/fiduciario non selezionato
            if (!hasSelectedSupplier) {
              tooltipMessage = `Per accedere allo step Atto di Nomina devi prima selezionare il ${entityType}`;
            }
            // 2. GDPR disabilitato (solo per supplier)
            else if (!showAttoDiNomina && !this.isBackofficeProfessional()) {
              disabled = true;
              tooltipMessage = 'Abilita "Gestisce dati ai fini GDPR" nello step Contratto per accedere a questo step';
            }
            // 3. Prerequisiti non soddisfatti
            else if (!canAccessAttoDiNomina) {
              tooltipMessage = 'Salva almeno in bozza il Contratto per accedere all\'Atto di Nomina';
            }

            return {
              ...step,
              disabled,
              tooltipMessage,
            };
          }

          // Step Gestione Documenti (id 4 per professional, id 3 per supplier)
          if (step.id === this.getGestioneDocumentiStepId()) {
            let tooltipMessage: string | undefined;
            if (!hasSelectedSupplier) {
              tooltipMessage = `Per accedere allo step Gestione Documenti devi prima selezionare il ${entityType}`;
            } else if (!canAccessGestioneDocumenti) {
              tooltipMessage = 'Salva almeno in bozza il Contratto per accedere alla Gestione Documenti';
            }
            
            return {
              ...step,
              disabled: !canAccessGestioneDocumenti,
              tooltipMessage,
            };
          }

          return step;
        });

        this.wizardStepService.steps.set(updatedSteps);
      });
    }, { allowSignalWrites: true });

    // Effect per forzare lo scroll al top quando cambia step
    effect(
      () => {
        const step = this.currentStep();

        // Usa untracked per operazioni asincrone che non devono triggherare dependency tracking
        untracked(() => {
          setTimeout(() => {
            // Scroll dell'host element
            const hostElement = this.elementRef.nativeElement as HTMLElement;
            if (hostElement) {
              hostElement.scrollIntoView({ behavior: 'auto', block: 'start' });
            }

            // Scroll di window
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

            // Forza scroll diretto
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;

            // Trova e scrolla anche eventuali contenitori con overflow
            const containers = document.querySelectorAll('.wizard-container, .step-content');
            containers.forEach((container) => {
              (container as HTMLElement).scrollTop = 0;
            });
          }, 50);
        });
      },
      { allowSignalWrites: true },
    );

    // Effect per aggiornare disabled degli step quando cambia maxStepReached
    // DISABLED: Tutti gli step sono ora navigabili liberamente
    /*
    effect(() => {
      const maxReached = this.maxStepReached();
      
      untracked(() => {
        const steps = this.wizardStepService.steps();
        const updatedSteps = steps.map(step => ({
          ...step,
          disabled: step.id > maxReached
        }));
        
        this.wizardStepService.steps.set(updatedSteps);
      });
    }, { allowSignalWrites: true });
    */
  }

  /**
   * Check if step 2 (Atto di Nomina) should be visible based on GDPR setting
   */
  shouldShowAttoDiNomina(): boolean {
    // Always show Atto di Nomina for professional contracts (gdprDataProcessing field is hidden)
    if (this.isSelectedEntityProfessional()) {
      return true;
    }
    return this.step1Form?.get('gdprDataProcessing')?.value === true;
  }

  /**
   * Check if "Salva e Genera Contratto" button should be enabled
   * Requirements:
   * - Contract section must be in FILLED status
   * - If GDPR is enabled, Atto di Nomina must also be in FILLED status
   */
  canGenerateDocument = computed(() => {
    const contractStatus = this.contractSectionStatus();
    const isContractFilled = contractStatus === 'FILLED';
    const requiresAttoDiNomina = this.shouldShowAttoDiNomina();
    const isAttoDiNominaFilled = this.attoDiNominaStatus() === 'FILLED';

    if (!isContractFilled) {
      return false;
    }

    // If Atto di Nomina is required (GDPR enabled), it must also be FILLED
    if (requiresAttoDiNomina && !isAttoDiNominaFilled) {
      return false;
    }

    return true;
  });

  /**
   * Returns tooltip message explaining why "Salva e Genera Contratto" is disabled
   * For WCAG 2.1 AA compliance: users must understand why a control is disabled
   */
  generateDocumentDisabledReason = computed(() => {
    // Check template selection (Step 3 form validity)
    if (this.step3Form?.invalid) {
      const requiresAttoDiNomina = this.shouldShowAttoDiNomina();
      return requiresAttoDiNomina
        ? 'Seleziona entrambi i template (Contratto e Atto di Nomina)'
        : 'Seleziona il template del Contratto';
    }

    // Check contract validation
    const contractStatus = this.contractSectionStatus();
    const isContractFilled = contractStatus === 'FILLED';
    if (!isContractFilled) {
      return 'Salva e valida il Contratto (Step 1) prima di generare il documento';
    }

    // Check Atto di Nomina validation (if GDPR enabled)
    const requiresAttoDiNomina = this.shouldShowAttoDiNomina();
    if (requiresAttoDiNomina) {
      const isAttoDiNominaFilled = this.attoDiNominaStatus() === 'FILLED';
      if (!isAttoDiNominaFilled) {
        return "Salva e valida l'Atto di Nomina (Step 2) prima di generare il documento";
      }
    }

    // Button is enabled
    return 'Salva il contratto e genera il documento Word';
  });

  /**
   * Check if there is any atto di nomina data (saved in DB or compiled in form)
   */
  private hasAttoDiNominaData(): boolean {
    // Check if atto di nomina exists in backend
    if (this.attoDiNominaExists()) {
      return true;
    }

    // Check if step2 form has any meaningful data (compiled but not yet saved)
    const responsibleId = this.step2Form.get('responsibleId')?.value;
    const responsibleBusinessName = this.step2Form.get('responsibleBusinessName')?.value;
    const responsibleDpoName = this.step2Form.get('responsibleDpoName')?.value;
    const processDescription = this.step2Form.get('processDescription')?.value;
    const activityDescription = this.step2Form.get('activityDescription')?.value;
    const dataProcessingPurpose = this.step2Form.get('dataProcessingPurpose')?.value;
    const resolvedMainPurpose = this.resolveMainPurposeValue({
      mainPurpose: this.step2Form.get('mainPurpose')?.value,
      mainPurposeOther: this.step2Form.get('mainPurposeOther')?.value,
    });

    return !!(
      responsibleId ||
      (responsibleBusinessName && responsibleBusinessName.trim()) ||
      (responsibleDpoName && responsibleDpoName.trim()) ||
      (processDescription && processDescription.trim()) ||
      (activityDescription && activityDescription.length > 0) ||
      (dataProcessingPurpose && dataProcessingPurpose.trim()) ||
      (resolvedMainPurpose && resolvedMainPurpose.trim())
    );
  }

  private hasRequiredBackofficeResponsibleDraftData(): boolean {
    const responsibleBusinessName = this.step2Form.get('responsibleBusinessName')?.value;
    const responsibleDpoName = this.step2Form.get('responsibleDpoName')?.value;

    return !!(
      responsibleBusinessName &&
      responsibleBusinessName.trim() &&
      responsibleDpoName &&
      responsibleDpoName.trim()
    );
  }

  private resolveRecipientCategoryValue(
    value: string[] | string | null | undefined,
  ): string[] {
    return this.normalizeStringArrayValue(value);
  }

  private normalizeStringArrayValue(value: string[] | string | null | undefined): string[] {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value.map((item) => item?.trim()).filter((item): item is string => !!item);
    }

    const normalizedValue = value.trim();
    if (!normalizedValue) {
      return [];
    }

    if (normalizedValue.startsWith('[')) {
      try {
        const parsedValue = JSON.parse(normalizedValue);
        if (Array.isArray(parsedValue)) {
          return parsedValue
            .map((item) => `${item}`.trim())
            .filter((item): item is string => !!item);
        }
      } catch {
        // Fall back to comma-separated parsing below
      }
    }

    return normalizedValue.split(',').map((item) => item.trim()).filter((item) => item);
  }

  private coerceMultiSelectControlValue(controlName: string): string[] {
    const control = this.step2Form.get(controlName);
    const normalizedValue = this.normalizeStringArrayValue(control?.value);

    if (control && !Array.isArray(control.value)) {
      control.setValue(normalizedValue, { emitEvent: false });
    }

    return normalizedValue;
  }

  private resolveDropdownSelectionWithOther(
    value: string[] | string | null | undefined,
    options: FormDropdownItem[],
    explicitOtherText?: string | null,
  ): { selectedValues: string[]; otherText: string } {
    const parsedValues = this.normalizeStringArrayValue(value);
    const normalizedOtherText = explicitOtherText?.trim() || '';

    if (options.length === 0) {
      const selectedValues = Array.from(new Set(parsedValues));
      if (normalizedOtherText && !selectedValues.includes('OTHER')) {
        selectedValues.push('OTHER');
      }

      return {
        selectedValues,
        otherText: normalizedOtherText,
      };
    }

    const knownKeys = new Set(options.map((option) => option.valueKey).filter(Boolean));
    const standardValues = Array.from(
      new Set(parsedValues.filter((item) => item !== 'OTHER' && knownKeys.has(item))),
    );
    const customValues = parsedValues.filter((item) => item !== 'OTHER' && !knownKeys.has(item));
    const shouldIncludeOther =
      parsedValues.includes('OTHER') || customValues.length > 0 || !!normalizedOtherText;

    return {
      selectedValues: shouldIncludeOther ? [...standardValues, 'OTHER'] : standardValues,
      otherText: normalizedOtherText || customValues.join(', '),
    };
  }

  private updateMultiSelectOtherValidators(
    selectionControlName: 'category' | 'recipientCategory',
    otherControlName: 'categoryOther' | 'recipientCategoryOther',
  ): void {
    const selectionControl = this.step2Form.get(selectionControlName);
    const otherControl = this.step2Form.get(otherControlName);

    if (!selectionControl || !otherControl) {
      return;
    }

    const selectedValues = this.normalizeStringArrayValue(selectionControl.value);
    if (selectedValues.includes('OTHER')) {
      otherControl.setValidators([Validators.required, NoSpecialCharsValidator('free-text')]);
    } else {
      otherControl.setValidators([NoSpecialCharsValidator('free-text')]);
      if (otherControl.value) {
        otherControl.setValue('', { emitEvent: false });
      }
    }

    otherControl.updateValueAndValidity({ emitEvent: false });
  }

  private applyCategoryValue(
    value: string[] | string | null | undefined,
    otherText?: string | null,
  ): void {
    const resolvedValue = this.resolveDropdownSelectionWithOther(
      value,
      this.subjectCategoryOptions(),
      otherText,
    );

    this.step2Form.patchValue(
      {
        category: resolvedValue.selectedValues,
        categoryOther: resolvedValue.otherText,
      },
      { emitEvent: false },
    );

    this.updateMultiSelectOtherValidators('category', 'categoryOther');
  }

  private applyRecipientCategoryValue(
    value: string[] | string | null | undefined,
    otherText?: string | null,
  ): void {
    const resolvedValue = this.resolveDropdownSelectionWithOther(
      value,
      this.recipientCategoryOptions(),
      otherText,
    );

    this.step2Form.patchValue(
      {
        recipientCategory: resolvedValue.selectedValues,
        recipientCategoryOther: resolvedValue.otherText,
      },
      { emitEvent: false },
    );

    this.updateMultiSelectOtherValidators('recipientCategory', 'recipientCategoryOther');
  }

  private applyBackofficeControllerOwnershipTypeValue(
    controllerGroup: FormGroup,
    ownershipType: string | null | undefined,
  ): void {
    if (!this.isSelectedEntityProfessional()) {
      return;
    }

    const normalizedValue = ownershipType?.trim() || '';
    
    controllerGroup.patchValue(
      {
        controllerOwnershipType: normalizedValue || null,
      },
      { emitEvent: false },
    );
  }

  private normalizeInvolvedEntities(
    value: ContractInvolvedEntity[] | string[] | null | undefined,
  ): ContractInvolvedEntity[] {
    if (!value || !Array.isArray(value)) {
      return [];
    }

    return value.map((item) => {
      if (typeof item === 'string') {
        return {
          entityCode: item,
          signatoryName: '',
          signatoryRole: '',
        };
      }

      return {
        entityCode: item?.entityCode || '',
        signatoryName: item?.signatoryName || '',
        signatoryRole: item?.signatoryRole || '',
      };
    });
  }

  private setInvolvedEntitiesFormArray(
    involvedEntities: ContractInvolvedEntity[] | string[] | null | undefined,
  ): void {
    const involvedEntitiesArray = this.involvedEntitiesArray;
    involvedEntitiesArray.clear();

    const normalizedValues = this.normalizeInvolvedEntities(involvedEntities);
    if (normalizedValues.length === 0) {
      involvedEntitiesArray.push(this.createInvolvedEntityFormGroup());
      return;
    }

    normalizedValues.forEach((entity) => {
      involvedEntitiesArray.push(this.createInvolvedEntityFormGroup(entity));
    });
  }

  private resolveMainPurposeValue(step2Values: {
    mainPurpose?: string | null;
    mainPurposeOther?: string | null;
  }): string | null {
    if (!this.isBackofficeProfessional()) {
      return step2Values.mainPurpose?.trim() || null;
    }

    if (step2Values.mainPurpose === 'OTHER') {
      return step2Values.mainPurposeOther?.trim() || null;
    }

    return step2Values.mainPurpose?.trim() || null;
  }

  private applyBackofficeActivityDescriptionValue(value: string | string[] | null | undefined): void {
    if (!this.isBackofficeProfessional()) {
      return;
    }

    // value può essere: array nativo (da API), array JSON serializzato legacy "[\"Raccolta\",\"Uso\"]", stringa singola legacy, o null
    let parsed: string[] = [];
    if (!value) {
      parsed = [];
    } else if (Array.isArray(value)) {
      parsed = value;
    } else if (typeof value === 'string' && value.startsWith('[')) {
      try { parsed = JSON.parse(value); } catch { parsed = [value]; }
    } else {
      // Valore legacy stringa singola: wrap in array
      parsed = [value as string];
    }

    this.step2Form.patchValue({ activityDescription: parsed }, { emitEvent: false });
  }

  private applyBackofficeMainPurposeValue(value: string | null | undefined): void {
    if (!this.isBackofficeProfessional()) {
      return;
    }

    const normalizedValue = value?.trim() || '';
    const knownKeys = this.mainPurposeOptions().map((o) => o.valueKey);
    const isStandardOption = !!normalizedValue && knownKeys.includes(normalizedValue) && normalizedValue !== 'OTHER';

    this.step2Form.patchValue(
      {
        mainPurpose: normalizedValue ? (isStandardOption ? normalizedValue : 'OTHER') : null,
        mainPurposeOther: normalizedValue && !isStandardOption ? normalizedValue : '',
      },
      { emitEvent: false },
    );

    this.updateBackofficeMainPurposeOtherValidators(
      normalizedValue ? (isStandardOption ? normalizedValue : 'OTHER') : null,
    );
  }

  private updateBackofficeMainPurposeOtherValidators(selectedValue: string | null): void {
    const mainPurposeOtherControl = this.step2Form.get('mainPurposeOther');

    if (!mainPurposeOtherControl) {
      return;
    }

    if (this.isBackofficeProfessional() && selectedValue === 'OTHER') {
      mainPurposeOtherControl.setValidators([Validators.required]);
    } else {
      mainPurposeOtherControl.clearValidators();
      if (mainPurposeOtherControl.value) {
        mainPurposeOtherControl.setValue('', { emitEvent: false });
      }
    }

    mainPurposeOtherControl.updateValueAndValidity({ emitEvent: false });
  }

  private updateProcessedDataNotesValidators(
    selectedValues: string[] | string | null | undefined,
  ): void {
    const notesControl = this.step2Form.get('processedDataNotes');
    if (!notesControl) return;

    const normalizedValues = this.normalizeStringArrayValue(selectedValues);

    if (normalizedValues.includes('OTHER')) {
      notesControl.setValidators([Validators.required]);
    } else {
      notesControl.clearValidators();
    }
    notesControl.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Update wizard steps visibility based on GDPR setting
   * Called when gdprDataProcessing changes
   */
  private updateStepVisibility(): void {
    const showAttoDiNomina = this.shouldShowAttoDiNomina();
    const steps = this.wizardStepService.steps();
    const attoDiNominaStepId = this.getAttoDiNominaStepId();

    // Update Atto di Nomina step: disable it when GDPR is false, enable it when GDPR is true
    const updatedSteps = steps.map((step) => {
      if (step.id === attoDiNominaStepId) {
        return {
          ...step,
          disabled: !showAttoDiNomina, // Disabilita Atto di Nomina quando GDPR è false
          tooltipMessage: !showAttoDiNomina
            ? 'Abilita "Gestisce dati ai fini GDPR" nello step Contratto per accedere a questo step'
            : undefined, // Rimuovi tooltip quando GDPR è true
        };
      }
      return step;
    });

    this.wizardStepService.steps.set(updatedSteps);
  }

  /**
   * Helper method to set template fallback from options.
   * Il valore atteso è il DocumentAsset businessKey (VARCHAR) del template
   * contratto persistito su `contract_template_id`.
   */
  private setTemplateFallback(templateId: string): void {
    console.log('🔄 Attempting template fallback for businessKey:', templateId);
    console.log('📦 Current templateContrattoOptions:', this.templateContrattoOptions());

    // Try immediately
    let selectedTemplate = this.templateContrattoOptions().find(
      (t) => t.value === templateId,
    );

    if (selectedTemplate) {
      this.selectedTemplateContratto.set(selectedTemplate);
      console.log('✅ selectedTemplateContratto set from options (fallback):', selectedTemplate);
    } else {
      // If not found, retry after longer delay (options might still be loading)
      console.warn('⚠️ Template not found in options, retrying in 1000ms...', { templateId });
      setTimeout(() => {
        console.log('🔄 Retrying template fallback after delay...');
        console.log('📦 Updated templateContrattoOptions:', this.templateContrattoOptions());
        selectedTemplate = this.templateContrattoOptions().find(
          (t) => t.value === templateId,
        );
        if (selectedTemplate) {
          this.selectedTemplateContratto.set(selectedTemplate);
          console.log(
            '✅ selectedTemplateContratto set from options (delayed fallback):',
            selectedTemplate,
          );
        } else {
          console.error('❌ Template not found in options after retry:', {
            templateId,
            options: this.templateContrattoOptions(),
          });
        }
      }, 1000);
    }
  }

  /**
   * Helper method to set atto di nomina template fallback from options.
   * Il valore atteso è il DocumentAsset businessKey (VARCHAR) del template
   * atto di nomina persistito su `atto_di_nomina_template_id`.
   */
  private setAttoDiNominaTemplateFallback(templateId: string): void {
    // Try immediately
    let selectedTemplate = this.templateAttoNominaOptions().find(
      (t) => t.value === templateId,
    );

    if (selectedTemplate) {
      this.selectedTemplateAttoNomina.set(selectedTemplate);
      console.log('✅ selectedTemplateAttoNomina set from options (fallback):', selectedTemplate);
    } else {
      // If not found, retry after a short delay (options might still be loading)
      console.warn('⚠️ Atto di Nomina template not found in options, retrying in 500ms...', {
        templateId,
      });
      setTimeout(() => {
        selectedTemplate = this.templateAttoNominaOptions().find(
          (t) => t.value === templateId,
        );
        if (selectedTemplate) {
          this.selectedTemplateAttoNomina.set(selectedTemplate);
          console.log(
            '✅ selectedTemplateAttoNomina set from options (delayed fallback):',
            selectedTemplate,
          );
        } else {
          console.error('❌ Atto di Nomina template not found in options after retry:', {
            templateId,
            options: this.templateAttoNominaOptions(),
          });
        }
      }, 500);
    }
  }

  /**
   * Ritorna il `domainType` DocumentAsset ('SUPPLIERS' | 'PROFESSIONALS')
   * corrispondente al contesto di navigazione corrente. Usato per filtrare
   * i template contratto e atto di nomina caricati via v2.
   */
  private getTemplateDomainType(): string {
    return this.navigationContext.isSupplierContext() ? 'SUPPLIERS' : 'PROFESSIONALS';
  }

  private hasBackofficeProfessionalRole(roles: string[]): boolean {
    return (
      roles.includes(AppRole.BACKOFFICE_PROFESSIONAL) ||
      roles.includes('backoffice_professional') ||
      roles.includes('backoffice_professionals')
    );
  }

  /**
   * Helper methods per ottenere gli ID corretti degli step basati sul contesto (professional vs supplier)
   * In modalità professional, lo step Tariffe (2) è inserito tra Contratto (1) e Atto di Nomina (3)
   */
  private getTariffeStepId(): number {
    return 2; // Solo per professional
  }

  private getAttoDiNominaStepId(): number {
    return this.isBackofficeProfessional() ? 3 : 2;
  }

  private getGestioneDocumentiStepId(): number {
    return this.isBackofficeProfessional() ? 4 : 3;
  }

  /**
   * Find the first incomplete step in the wizard
   * @returns The step ID of the first incomplete step, or the last step if all are completed
   */
  private getFirstIncompleteStep(): number {
    const steps = this.wizardStepService.steps();
    
    // Find first step that is not completed and not disabled (skip step 0 - selection is always completed in edit mode)
    for (let i = 1; i < steps.length; i++) {
      const step = steps[i];
      if (!step.completed && !step.disabled) {
        return step.id;
      }
    }
    
    // If all steps are completed, return the last step (Gestione Documenti)
    return this.getGestioneDocumentiStepId();
  }

  ngOnInit(): void {
    this.titleService.setTitle('Form Contratto - PalEasy');
    
    // Pre-carica le categorie fiduciario per decodifica sincrona
    this.formDropdownService.preloadTrusteeCategories().subscribe();
    
    this.wizardStepService.reset();
    this.loadSupplierColumns();
    this.loadResponsibleColumns();
    this.loadSubResponsibleColumns();
    this.loadTemplateContrattoColumns();
    this.loadTemplateAttoNominaColumns();

    // Initialize forms FIRST before loading data
    this.initializeForms();
    this.initializeWizardSteps();
    this.updateStepVisibility(); // Update visibility based on role and GDPR setting
    this.setupConditionalValidation();
    this.applyBackofficeProfessionalStep1ValidationRules();
    this.applyBackofficeProfessionalStep2ValidationRules();
    // NON caricare il VRF automaticamente all'avvio
    // Verrà caricato solo se:
    // 1. Si sta creando un nuovo contratto (selectSupplier)
    // 2. Si sta modificando un contratto ma l'atto di nomina NON esiste (error handler loadAttoDiNominaData)
    
    // Load dropdowns with small delay to not block initial page load
    setTimeout(() => {
      this.loadAllDropdowns();
    }, 300);

    // Check if we're editing an existing contract
    // IMPORTANTE: caricare prima suppliers, poi il contratto per evitare timing issues
    this.route.params.subscribe((params) => {
      const contractId = params['id'];
      if (contractId) {
        this.isEditingExistingContract = true;
        this.contractId.set(contractId);
        this.checklistService.setChecklistAvailability(true);
        this.breadcrumbItems.set([
          { label: 'Registro Contratti', routerLink: `/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry` },
          { label: 'Modifica Contratto' },
        ]);
        
        // Carica prima i suppliers/professionals, poi il contratto
        this.loadSuppliersAndThenContract(contractId);
      } else {
        // Nuova creazione: carica solo suppliers
        // Se arrivato da review-professional con professionalId in queryParam, pre-selezioniamo il fiduciario
        const preselectedProfessionalId = this.route.snapshot.queryParamMap.get('professionalId');
        this.loadSuppliers(preselectedProfessionalId ?? undefined);
      }
    });

    // Subscribe to checklist open requests from navbar
    this.checklistService.openChecklistRequest$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.openChecklistDialog();
    });
  }

  isNumberOfRenewalsRequired(): boolean {
    return this.isSelectedEntityProfessional() && this.step1Form.get('automaticRenewal')?.value === 'YES';
  }

  isSlaKpiPersonRequired(): boolean {
    return !this.isOutsourcedActivityNo(this.step1Form?.get('outsourcedActivity')?.value);
  }

  // La chiave dell'opzione "No" arriva dal DB: riconosciuta anche tramite descrizione
  private isOutsourcedActivityNo(value: unknown): boolean {
    const normalize = (v: unknown) => String(v ?? '').trim().toUpperCase();
    const key = normalize(value);
    if (!key) {
      return false;
    }
    if (key === 'NO' || key === 'N') {
      return true;
    }
    const option = this.attivitaEsterOptions().find((o) => normalize(o.valueKey) === key);
    return normalize(option?.valueDescription) === 'NO';
  }

  private applySlaKpiPersonValidationRules(): void {
    // I fiduciari hanno già il campo nascosto e senza validatori
    if (this.isSelectedEntityProfessional() || this.isBackofficeProfessional()) {
      return;
    }
    const control = this.step1Form?.get('slaKpiPerson');
    control?.setValidators(
      this.isSlaKpiPersonRequired()
        ? [Validators.required, NoSpecialCharsValidator('name')]
        : [NoSpecialCharsValidator('name')],
    );
    control?.updateValueAndValidity({ emitEvent: false });
  }

  private applyNumberOfRenewalsValidationRules(): void {
    const numberOfRenewalsControl = this.step1Form.get('numberOfRenewals');
    numberOfRenewalsControl?.setValidators(this.isNumberOfRenewalsRequired()
      ? [Validators.required, Validators.min(2), Validators.pattern(/^\d+$/)]
      : []);
    numberOfRenewalsControl?.updateValueAndValidity({ emitEvent: false });
  }

  private applyBackofficeProfessionalStep1ValidationRules(): void {
    this.applyNumberOfRenewalsValidationRules();
    if (!this.isSelectedEntityProfessional()) {
      return;
    }

    // ✅ Campi obbligatori SOLO per backoffice professional
    const professionalRequiredControls = ['managementTools', 'network'];
    professionalRequiredControls.forEach((controlName) => {
      const control = this.step1Form.get(controlName);
      control?.setValidators([Validators.required]);
      control?.updateValueAndValidity({ emitEvent: false });
    });

    // ✅ Campi firma fiduciario: obbligatori con validazione specifica
    const professionalSignatoryControl = this.step1Form.get('professionalSignatory');
    professionalSignatoryControl?.setValidators([
      Validators.required,
      NoSpecialCharsValidator('name'),
    ]);
    professionalSignatoryControl?.updateValueAndValidity({ emitEvent: false });

    const professionalSignatoryRoleControl = this.step1Form.get('professionalSignatoryRole');
    professionalSignatoryRoleControl?.setValidators([
      Validators.required,
      NoSpecialCharsValidator('free-text'),
    ]);
    professionalSignatoryRoleControl?.updateValueAndValidity({ emitEvent: false });

    const hiddenRequiredControls = [
      'serviceType',
      'contractSubject',
      'outsourcedActivity',
      'slaKpiPerson',
      'supplierType',
      'frameworkAgreement',
      'rebates',
      'invoiceOnSol',
      'technicalAccount',
      'allocationMatrix',
      'expenseAccount',
      'currency',
      'invoiceFrequency',
      'managingArea',
      'claimsAreaReference',
      'eventualReferent',
      'invoiceRecipientEmail',
      'controlsOwner',
      'paymentAuthorizer',
      'documentationAtIP',
      'supplierSignatory',
      'supplierSignatoryRole',
      'vrfUseCase',
      'gdprDataProcessing',
      'dataTransferOutsideSee',
      'privacyResponsibleFunction', // Campo condizionale GDPR - rimosso per professional
    ];

    hiddenRequiredControls.forEach((controlName) => {
      const control = this.step1Form.get(controlName);
      control?.clearValidators();
      control?.updateValueAndValidity({ emitEvent: false });
    });

    const hiddenFrameworkControls = [
      'frameworkStartDate',
      'frameworkEndDate',
      'frameworkAutomaticRenewal',
      'frameworkRenewalNoticeDays',
      'expectedFee',
    ];

    hiddenFrameworkControls.forEach((controlName) => {
      const control = this.step1Form.get(controlName);
      control?.clearValidators();
      control?.updateValueAndValidity({ emitEvent: false });
    });

    this.step1Form.patchValue(
      {
        gdprDataProcessing: false,
        dataTransferOutsideSee: false,
        vrfUseCase: null,
        privacyResponsibleFunction: null,
      },
      { emitEvent: false },
    );

    this.step1Form.get('privacyResponsibleFunction')?.clearValidators();
    this.step1Form.get('privacyResponsibleFunction')?.updateValueAndValidity({ emitEvent: false });

    this.step3Form.get('templateAttoNomina')?.clearValidators();
    this.step3Form.get('templateAttoNomina')?.setValue(null, { emitEvent: false });
    this.step3Form.get('templateAttoNomina')?.updateValueAndValidity({ emitEvent: false });
  }

  private applyBackofficeProfessionalStep2ValidationRules(): void {
    if (!this.isBackofficeProfessional()) {
      return;
    }

    const hiddenRequiredControls = [
      'processDescription',
      'externalSysAdminsText',
      'responsibleId',
      'headquarters',
      'dataDeletionProgram',
      'criticalSupplierQualification',
    ];

    hiddenRequiredControls.forEach((controlName) => {
      const control = this.step2Form.get(controlName);
      control?.clearValidators();
      control?.updateValueAndValidity({ emitEvent: false });
    });

    this.step2Form.patchValue(
      {
        responsibleId: null,
        headquarters: '',
        responsibleType: null,
        dataProcessingLocation: null,
        dataDeletionProgram: null,
        criticalSupplierQualification: null,
      },
      { emitEvent: false },
    );

    this.step2Form.get('responsibleBusinessName')?.setValidators([Validators.required]);
    this.step2Form.get('responsibleBusinessName')?.updateValueAndValidity({ emitEvent: false });
  }

  private loadSupplierColumns(): void {
    const configKey = this.isBackofficeProfessional() ? 'elencoFiduciari' : 'elencoFornitori';
    const config = TABLE_CONFIG[configKey];
    if (config) {
      this.supplierColumns = config.columns;
    }
  }

  private loadResponsibleColumns(): void {
    const config = TABLE_CONFIG['elencoResponsabili'];
    if (config) {
      this.responsibleColumns = config.columns;
    }
  }

  private loadSubResponsibleColumns(): void {
    const config = TABLE_CONFIG['elencoSubResponsabili'];
    if (config) {
      this.subResponsibleColumns = config.columns;
    }
  }

  private loadTemplateContrattoColumns(): void {
    const config = TABLE_CONFIG['elencoTemplateContratti'];
    if (config) {
      this.templateContrattoColumns = config.columns;
    }
  }

  private loadTemplateAttoNominaColumns(): void {
    const config = TABLE_CONFIG['elencoTemplateAttiNomina'];
    if (config) {
      this.templateAttoNominaColumns = config.columns;
    }
  }

  private loadSuppliers(preselectedProfessionalId?: string): void {
    if (this.isBackofficeProfessional()) {
      this.professionalsService
        .getProfessionals(undefined, undefined, undefined, {
          step: 'CONTRACT_SENT',
          stepStatus: 'PENDING'
        })
        .pipe(timeout(10000))
        .subscribe({
          next: (response) => {
            if (response?.status && Array.isArray(response?.data?.content)) {
              const mappedData = response.data.content.map((item) => {
                // COALESCE: businessName (legal) OR "lastName firstName" (physical)
                let computedName = '';
                if (item.businessName) {
                  computedName = item.businessName;
                } else if (item.lastName || item.firstName) {
                  const parts = [item.lastName, item.firstName].filter(Boolean);
                  computedName = parts.join(' ');
                }
                
                // Per la tabella: mostra il valore calcolato o '-'
                const displayName = computedName || '-';
                
                // Per il popup di conferma: usa fallback se nome è vuoto o '-'
                const businessNameForPopup = computedName || `Fiduciario ${item.id || 'senza nome'}`;

                // Format createdAt as DD/MM/YYYY
                let formattedDate = '-';
                if (item.createdAt) {
                  try {
                    const date = new Date(item.createdAt);
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    formattedDate = `${day}/${month}/${year}`;
                  } catch (e) {
                    console.warn('Invalid date format:', item.createdAt);
                  }
                }

                return {
                  ...item,
                  displayName,
                  supplierName: displayName,
                  businessName: businessNameForPopup, // Usato nel popup di conferma
                  createdAt: formattedDate, // Data formattata per la tabella
                  email: '-', // Email not available in lightweight DTO
                  vatNumber: item.identificationCode || '-', // Use identificationCode (P.IVA or CF)
                  status: item.professionalStatus || 'NON_DEFINITO', // Use professionalStatus
                  type: item.type || '',
                  legalOrPhysical: item.legalOrPhysical || '-',
                  isProfessional: true, // Mark as professional entity
                };
              });

              this.suppliers.set(mappedData);

              // Auto-preselect professional if coming from review-professional
              if (preselectedProfessionalId) {
                this.applySupplierPreselection(preselectedProfessionalId);
              }

              if (mappedData.length === 0) {
                this.messageService.add({
                  severity: 'info',
                  summary: 'Nessun fiduciario',
                  detail: 'Non ci sono fiduciari registrati nel sistema',
                  life: 3000,
                });
              }
            } else {
              console.warn('Formato risposta API fiduciari inaspettato:', response);
              this.suppliers.set([]);
            }
          },
          error: (error) => {
            console.error('Errore caricamento fiduciari:', error);
            const isTimeout = error instanceof TimeoutError;
            this.suppliers.set([]);
            this.messageService.add({
              severity: 'error',
              summary: isTimeout ? 'Timeout' : 'Errore',
              detail: isTimeout
                ? 'Il caricamento dei fiduciari ha impiegato troppo tempo.'
                : 'Impossibile caricare i fiduciari. Verifica che il backend sia avviato.',
              life: 5000,
            });
          },
        });
      return;
    }

    this.supplierRegistryService
      .getSuppliers(true)
      .pipe(timeout(10000))
      .subscribe({
        next: (response) => {
          if (response?.status && response?.data?.content) {
            const mappedData = this.supplierRegistryService.mapSuppliersForDisplay(
              response.data.content,
            ).map(supplier => ({...supplier, isProfessional: false})); // Mark as supplier entity
            this.suppliers.set(mappedData);

            if (mappedData.length === 0) {
              this.messageService.add({
                severity: 'info',
                summary: 'Nessun fornitore',
                detail: 'Non ci sono fornitori registrati nel sistema',
                life: 3000,
              });
            }
          } else {
            console.warn('Formato risposta API inaspettato:', response);
            this.suppliers.set([]);
          }
        },
        error: (error) => {
          console.error('Errore caricamento fornitori:', error);
          const isTimeout = error instanceof TimeoutError;
          this.suppliers.set([]);
          this.messageService.add({
            severity: 'error',
            summary: isTimeout ? 'Timeout' : 'Errore',
            detail: isTimeout
              ? 'Il caricamento dei fornitori ha impiegato troppo tempo. Verifica che il backend sia avviato su localhost:8081'
              : 'Impossibile caricare i fornitori. Verifica che il backend sia avviato.',
            life: 5000,
          });
        },
      });
  }

  /**
   * Carica prima i suppliers/professionals, poi il contratto
   * Necessario per evitare timing issues: selectedSupplier dipende dalla lista suppliers
   */
  private loadSuppliersAndThenContract(contractId: string): void {
    if (this.isBackofficeProfessional()) {
      this.professionalsService
        .getProfessionals()
        .pipe(timeout(10000))
        .subscribe({
          next: (response) => {
            if (response?.status && Array.isArray(response?.data?.content)) {
              const mappedData = response.data.content.map((item) => {
                let computedName = '';
                if (item.businessName) {
                  computedName = item.businessName;
                } else if (item.lastName || item.firstName) {
                  const parts = [item.lastName, item.firstName].filter(Boolean);
                  computedName = parts.join(' ');
                }
                
                const displayName = computedName || '-';
                const businessNameForPopup = computedName || `Fiduciario ${item.id || 'senza nome'}`;

                let formattedDate = '-';
                if (item.createdAt) {
                  try {
                    const date = new Date(item.createdAt);
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    formattedDate = `${day}/${month}/${year}`;
                  } catch (e) {
                    console.warn('Invalid date format:', item.createdAt);
                  }
                }

                return {
                  ...item,
                  displayName,
                  supplierName: displayName,
                  businessName: businessNameForPopup,
                  createdAt: formattedDate,
                  email: '-',
                  vatNumber: item.identificationCode || '-',
                  status: item.professionalStatus || 'NON_DEFINITO',
                  type: item.type || '',
                  legalOrPhysical: item.legalOrPhysical || '-',
                  isProfessional: true,
                };
              });

              this.suppliers.set(mappedData);
              
              // Ora carica il contratto
              this.loadContractData(contractId);
            } else {
              console.warn('Formato risposta API fiduciari inaspettato:', response);
              this.suppliers.set([]);
              // Carica comunque il contratto anche se suppliers non è caricato
              this.loadContractData(contractId);
            }
          },
          error: (error) => {
            console.error('Errore caricamento fiduciari:', error);
            const isTimeout = error instanceof TimeoutError;
            this.suppliers.set([]);
            this.messageService.add({
              severity: 'error',
              summary: isTimeout ? 'Timeout' : 'Errore',
              detail: isTimeout
                ? 'Il caricamento dei fiduciari ha impiegato troppo tempo.'
                : 'Impossibile caricare i fiduciari.',
              life: 5000,
            });
            // Carica comunque il contratto anche se suppliers non è caricato
            this.loadContractData(contractId);
          },
        });
      return;
    }

    this.supplierRegistryService
      .getSuppliers(true)
      .pipe(timeout(10000))
      .subscribe({
        next: (response) => {
          if (response?.status && response?.data?.content) {
            const mappedData = this.supplierRegistryService.mapSuppliersForDisplay(
              response.data.content,
            ).map(supplier => ({...supplier, isProfessional: false}));
            this.suppliers.set(mappedData);
            
            // Ora carica il contratto
            this.loadContractData(contractId);
          } else {
            console.warn('Formato risposta API inaspettato:', response);
            this.suppliers.set([]);
            // Carica comunque il contratto
            this.loadContractData(contractId);
          }
        },
        error: (error) => {
          console.error('Errore caricamento fornitori:', error);
          const isTimeout = error instanceof TimeoutError;
          this.suppliers.set([]);
          this.messageService.add({
            severity: 'error',
            summary: isTimeout ? 'Timeout' : 'Errore',
            detail: isTimeout
              ? 'Il caricamento dei fornitori ha impiegato troppo tempo.'
              : 'Impossibile caricare i fornitori.',
            life: 5000,
          });
          // Carica comunque il contratto
          this.loadContractData(contractId);
        },
      });
  }

  // getStatusDisplay() rimosso - ora centralizzato in SupplierRegistryService

  private loadContractData(contractId: string): void {
    this.isLoading.set(true);
    this.loadingMessage.set('Caricamento contratto in corso...');

    this.contractRegistryService
      .getContractById(contractId)
      .pipe(timeout(15000))
      .subscribe({
        next: (response) => {
          console.log('📦 Contract loaded from API:', response);

          const contract = response.data;
          console.log('📋 Contract data structure:', {
            hasGeneralInfo: !!contract.generalInfo,
            contractTemplateId: contract?.generalInfo?.contractTemplateId,
            frameworkAgreement: contract?.frameworkAgreement,
            economics: contract?.economics,
            administration: contract?.administration,
            dataProcessing: contract?.dataProcessing,
          });

          // Save contract data for later validation in loadAttoDiNominaData
          this.loadedContractData.set(contract);

          // Parse dates from ISO string to Date objects for datepicker
          const parseDate = (dateStr: string | null) =>
            this.contractRegistryService.parseContractDate(dateStr);

          // Populate step0Form - Supplier selection
          // Use whichever ID is present (professional or supplier) - based on contract data, not user role
          const entityId = contract.generalInfo.professionalId || contract.generalInfo.supplierId;

          this.step0Form.patchValue({
            supplierId: entityId,
          });

          // Find and set the selected entity from the loaded suppliers list
          // This ensures isSelectedEntityProfessional() works correctly in edit mode
          const foundEntity = this.suppliers().find(
            (entity) => String(entity.id) === String(entityId),
          );
          if (foundEntity) {
            this.selectedSupplier.set(foundEntity);
            console.log('✅ Selected entity set in edit mode:', {
              id: foundEntity.id,
              name: foundEntity.supplierName || foundEntity.displayName,
              isProfessional: foundEntity.isProfessional
            });
            // Apply professional-specific validators if needed
            this.applyBackofficeProfessionalStep1ValidationRules();
          } else {
            const fallbackEntity = {
              id: entityId,
              displayName: `Fiduciario ${entityId}`,
              supplierName: `Fiduciario ${entityId}`,
              businessName: `Fiduciario ${entityId}`,
              type: '',
              status: 'NON_DEFINITO',
              isProfessional: !!contract.generalInfo.professionalId,
            };

            this.suppliers.update((entities) => [...entities, fallbackEntity]);
            this.selectedSupplier.set(fallbackEntity);
            this.applyBackofficeProfessionalStep1ValidationRules();

            console.warn('⚠️ Entity not found in suppliers list, loading it by ID:', entityId);

            if (contract.generalInfo.professionalId) {
              this.professionalsService
                .getProfessionals(0, 1, undefined, { id: String(entityId) })
                .pipe(catchError(() => of(null)))
                .subscribe((professionalResponse) => {
                  const professional = professionalResponse?.data?.content?.[0];
                  if (!professional) {
                    return;
                  }

                  const displayName =
                    professional.businessName ||
                    [professional.lastName, professional.firstName].filter(Boolean).join(' ') ||
                    `Fiduciario ${professional.id}`;
                  const hydratedEntity = {
                    ...professional,
                    displayName,
                    supplierName: displayName,
                    businessName: displayName,
                    status: professional.professionalStatus || 'NON_DEFINITO',
                    isProfessional: true,
                  };

                  this.suppliers.update((entities) =>
                    entities.map((entity) =>
                      String(entity.id) === String(entityId) ? hydratedEntity : entity,
                    ),
                  );
                  this.selectedSupplier.set(hydratedEntity);
                });
            }
          }

          // NON caricare il VRF qui - sarà caricato solo se l'atto di nomina non esiste
          // (vedi error handler di loadAttoDiNominaData)

          // Skip the supplier selection step if editing
          // Note: currentStep will be set later after all data is loaded (in loadAttoDiNominaData or at the end)
          // In edit mode, allow navigation to step 1 immediately
          // Step 2 and 3 will be enabled based on data existence
          this.maxStepReached.set(1);

          // Update wizard step service for sidebar navigation
          this.wizardStepService.markStepCompleted(0, true);

          // Populate step1Form - Contract data
          this.step1Form.patchValue({
            // General Info
            contractType: contract.generalInfo.contractType,
            serviceType: contract.generalInfo.serviceType,
            contractSubject: contract.generalInfo.contractSubject,
            outsourcedActivity: contract.generalInfo.outsourcedActivity,
            slaKpiPerson: contract.generalInfo.slaKpiPerson,
            startDate: parseDate(contract.generalInfo.startDate),
            endDate: parseDate(contract.generalInfo.endDate),
            numberOfRenewals: contract.generalInfo.numberOfRenewals ?? 5,
            automaticRenewal: contract.generalInfo.automaticRenewal,
            terminationNoticeDays: contract.generalInfo.terminationNoticeDays,
            renewalNoticeDays: contract.generalInfo.renewalNoticeDays,
            synergies: contract.generalInfo.synergies,
            synergiesCompanies: contract.generalInfo.synergiesCompanies || [],
            involvedSc: contract.generalInfo.involvedSc || '',
            supplierType: contract.generalInfo.supplierType,
            managementTools: contract.generalInfo.managementTools || [],
            network: contract.generalInfo.network || [],
            status: contract.generalInfo.status || null,
            contractInNumber: contract.generalInfo.contractInNumber || null,

            // Framework Agreement - handle null case
            frameworkAgreement: contract.frameworkAgreement?.frameworkAgreement || false,
            frameworkStartDate: contract.frameworkAgreement?.frameworkStartDate
              ? parseDate(contract.frameworkAgreement.frameworkStartDate)
              : null,
            frameworkEndDate: contract.frameworkAgreement?.frameworkEndDate
              ? parseDate(contract.frameworkAgreement.frameworkEndDate)
              : null,
            frameworkAutomaticRenewal: contract.frameworkAgreement?.automaticRenewal || false,
            frameworkRenewalNoticeDays: contract.frameworkAgreement?.renewalNoticeDays || null,

            // Economics
            expectedFee: contract.economics?.expectedFee || false,
            rebates: contract.economics?.rebates || false,
            rebatesNote: contract.economics?.rebatesNote || '',
            invoiceOnSol: contract.economics?.invoiceOnSol || false,
            technicalAccount: contract.economics?.technicalAccount || false,
            allocationMatrix: contract.economics?.allocationMatrix || null,
            expenseAccount: contract.economics?.expenseAccount || null,
            currency: contract.economics?.currency || 'EUR',
            totalContractValue: contract.economics?.totalContractValue || null,
            annualValue: contract.economics?.annualValue || null,
            invoiceFrequency: contract.economics?.invoiceFrequency || null,

            // Administration
            managingArea: contract.administration?.managingArea || null,
            claimsAreaReference: contract.administration?.claimsAreaReference || null,
            eventualReferent: contract.administration?.eventualReferent || null,
            buyer: contract.administration?.buyer || null,
            involvedStructures: contract.administration?.involvedStructures || [],
            invoiceRecipientEmail: contract.administration?.invoiceRecipientEmail || null,
            controlsOwner: contract.administration?.controlsOwner || null,
            paymentAuthorizer: contract.administration?.paymentAuthorizer || null,
            documentationAtIP: contract.administration?.documentationAtIP || false,
            supplierSignatory: contract.administration?.supplierSignatory || null,
            supplierSignatoryRole: contract.administration?.supplierSignatoryRole || null,
            professionalSignatory: contract.administration?.professionalSignatory || null,
            professionalSignatoryRole: contract.administration?.professionalSignatoryRole || null,

            // Data Processing
            vrfUseCase: contract.dataProcessing?.vrfUseCase || null,
            gdprDataProcessing: contract.dataProcessing?.gdprDataProcessing || false,
            privacyResponsibleFunction: contract.dataProcessing?.privacyResponsibleFunction || '',
            dataTransferOutsideSee: contract.dataProcessing?.dataTransferOutsideSee || false,
          });

          this.setInvolvedEntitiesFormArray(contract.generalInfo.involvedEntities);

          // ✅ Imposta 90 come valore di default per renewalNoticeDays se automaticRenewal è YES/ANNUAL e il campo è vuoto
          const automaticRenewalValue = this.step1Form.get('automaticRenewal')?.value;
          const renewalNoticeDaysValue = this.step1Form.get('renewalNoticeDays')?.value;
          if ((automaticRenewalValue === 'YES' || automaticRenewalValue === 'ANNUAL') && !renewalNoticeDaysValue) {
            this.step1Form.patchValue({ renewalNoticeDays: 90 });
          }

          // Save original values for comparison
          this.originalFormValues.set(0, this.step0Form.value);
          this.originalFormValues.set(1, this.step1Form.value);

          // Mark step 1 as completed if contractSectionStatus is FILLED
          if (contract.generalInfo.contractSectionStatus === 'FILLED') {
            this.wizardStepService.markStepCompleted(1, true);
            this.contractSectionStatus.set('FILLED');
          } else {
            this.contractSectionStatus.set('DRAFT');
          }

          // Mark tariffe step as completed if paymentRatesSectionStatus is FILLED (professional only)
          if (this.isSelectedEntityProfessional()) {
            const paymentRatesStatus = contract.paymentRatesSectionStatus;
            if (paymentRatesStatus) {
              this.tariffeStatus.set(paymentRatesStatus);
              const isTariffeCompleted = paymentRatesStatus === 'FILLED';
              const tariffeStepId = this.getTariffeStepId();
              this.wizardStepService.markStepCompleted(tariffeStepId, isTariffeCompleted);
            }
          }

          // Update step visibility based on GDPR setting
          this.updateStepVisibility();

          // Update templateAttoNomina validator based on GDPR setting
          const templateAttoNominaControl = this.step3Form.get('templateAttoNomina');
          if (contract.dataProcessing?.gdprDataProcessing) {
            templateAttoNominaControl?.setValidators([Validators.required]);
          } else {
            templateAttoNominaControl?.clearValidators();
          }
          templateAttoNominaControl?.updateValueAndValidity();

          // Populate step3Form with contract template data if available
          console.log('📋 Contract template data:', {
            contractTemplateId: contract.generalInfo.contractTemplateId,
            contractDocumentsNote: contract.generalInfo.contractDocumentsNote,
          });

          if (
            contract.generalInfo.contractTemplateId ||
            contract.generalInfo.contractDocumentsNote
          ) {
            console.log('✅ Entering template loading block');
            this.step3Form.patchValue({
              templateContratto: contract.generalInfo.contractTemplateId || null,
              documentNotes: contract.generalInfo.contractDocumentsNote || '',
            });
            console.log('✅ Step3Form populated with:', {
              templateContratto: contract.generalInfo.contractTemplateId,
              documentNotes: contract.generalInfo.contractDocumentsNote,
            });

            // Load full template details via API v2 (DocumentAsset businessKey lookup)
            if (contract.generalInfo.contractTemplateId) {
              console.log(
                '🔍 Loading template details for businessKey:',
                contract.generalInfo.contractTemplateId,
              );
              this.documentTemplateV2Service
                .getByBusinessKey(contract.generalInfo.contractTemplateId)
                .subscribe({
                  next: (asset) => {
                    console.log('📥 Template v2 asset:', asset);
                    if (asset) {
                      const selectedTemplate = {
                        label: asset.name || asset.businessKey,
                        value: asset.businessKey,
                      };
                      this.selectedTemplateContratto.set(selectedTemplate);
                      console.log(
                        '✅ selectedTemplateContratto loaded from API v2:',
                        selectedTemplate,
                      );
                    } else {
                      console.warn('⚠️ No template asset returned, using fallback');
                      if (contract.generalInfo.contractTemplateId) {
                        this.setTemplateFallback(contract.generalInfo.contractTemplateId);
                      }
                    }
                  },
                  error: (err) => {
                    console.error('❌ Error loading contract template details:', err);
                    // Fallback: try to find in options
                    if (contract.generalInfo.contractTemplateId) {
                      this.setTemplateFallback(contract.generalInfo.contractTemplateId);
                    }
                  },
                });
            }
          }

          // Try to load Atto di Nomina if:
          // - Professional contract (GDPR field doesn't exist, atto di nomina always required)
          // - OR supplier contract with GDPR enabled
          if (this.isSelectedEntityProfessional() || contract.dataProcessing?.gdprDataProcessing) {
            console.log('✅ Loading Atto di Nomina data (professional:', this.isSelectedEntityProfessional(), ', GDPR:', contract.dataProcessing?.gdprDataProcessing, ')');
            this.loadAttoDiNominaData(contractId);
          } else {
            console.log('ℹ️ Skipping Atto di Nomina loading (GDPR disabled for supplier contract)');
            
            // Check if step 3 has data to enable navigation
            this.checkAndEnableStep3Navigation();
            
            // Navigate to first incomplete step
            const targetStep = this.getFirstIncompleteStep();
            this.currentStep.set(targetStep);
            console.log('✅ Navigating to first incomplete step:', targetStep);
            
            this.isLoading.set(false);
          }

          // Load existing attachments in edit mode
          this.loadAttachments(contractId);
        },
        error: (err) => {
          console.error('Errore caricamento contratto:', err);
          this.isLoading.set(false);

          const isTimeout = err instanceof TimeoutError;
          this.messageService.add({
            severity: 'error',
            summary: isTimeout ? 'Timeout' : 'Errore',
            detail: isTimeout
              ? 'Il caricamento del contratto ha impiegato troppo tempo. Verifica che il backend sia avviato su localhost:8080'
              : 'Impossibile caricare i dati del contratto. Verifica che il backend sia avviato su localhost:8080',
            life: 7000,
          });

          // Navigate back to registry on error
          setTimeout(() => {
            console.log('🔙 Navigating back to registry...');
            this.router.navigate([`/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry`]);
          }, 2000);
        },
      });
  }

  /**
   * Pre-fills Atto di Nomina fields from VRF questionnaire + professional general data.
   * Only patches fields that are currently empty (does not overwrite saved values).
   * Called when creating a new contract or when no Atto di Nomina exists in DB.
   */
  private prefillAttoDiNominaFromVRF(professionalId: string): void {
    if (!professionalId || !this.isBackofficeProfessional()) return;

    forkJoin({
      vrf: this.professionalsService.getVRFQuestionnaire(professionalId).pipe(
        catchError(() => of(null)),
      ),
      generalData: this.professionalsService.getProfessionalGeneralData(professionalId).pipe(
        catchError(() => of(null)),
      ),
      countries: this.geographyService.getCountries().pipe(
        catchError(() => of([] as Country[])),
      ),
      itProgramsOptions: this.formDropdownService
        .getFormDropdownValues('VRF_QUESTIONNAIRE', 'IT_PROGRAMS')
        .pipe(catchError(() => of({ status: false, data: [] }))),
    }).subscribe(({ vrf, generalData, countries, itProgramsOptions }) => {
      const patch: Record<string, any> = {};

      // Ragione sociale del Responsabile
      if (vrf?.responsibleCompanyName) {
        patch['responsibleBusinessName'] = vrf.responsibleCompanyName;
      }
      // Nome del DPO
      if (vrf?.dpoFirstName) {
        patch['responsibleDpoName'] = vrf.dpoFirstName;
      }
      // Cognome del DPO
      if (vrf?.dpoLastName) {
        patch['responsibleDpoSurname'] = vrf.dpoLastName;
      }
      // Email DPO Responsabile
      if (vrf?.dpoEmail) {
        patch['responsibleDpoEmail'] = vrf.dpoEmail;
      }

      // Trasferimento extra UE: mappatura diretta boolean
      if (vrf?.dataTransferOutsideEu != null) {
        patch['transferOutsideEu'] = vrf.dataTransferOutsideEu;
      }

      // Programma di data deletion: se il VRF descrive un metodo di cancellazione → true
      if (vrf?.dataDeletionMethod) {
        patch['dataDeletionProgram'] = true;
      }

      // Sede del responsabile: concatena comune (decodificato dal codice), provincia (codice), stato (decode).
      // La decodifica del comune richiede l'anagrafica (caricata per provincia), quindi il campo
      // headquarters viene valorizzato in modo asincrono, solo se ancora vuoto.
      const legalAddress = generalData?.data?.legalAddress;
      if (legalAddress) {
        const province = legalAddress.province || '';
        const countryCode = legalAddress.country || 'IT';
        const countryName =
          (countries as Country[]).find((c) => c.code === countryCode)?.name || 'Italia';
        this.geographyService
          .resolveMunicipalityName(legalAddress.municipality, province)
          .subscribe((municipality) => {
            const parts = [
              municipality,
              province ? `(${province})` : '',
              countryName,
            ].filter((p) => p);
            const hqControl = this.step2Form.get('headquarters');
            if (parts.length > 0 && hqControl && !hqControl.value) {
              hqControl.setValue(parts.join(' '), { emitEvent: false });
            }
          });
      }

      // Destinatari categorie: VRF stores the selected keys as a JSON string.
      if (vrf?.dataRecipientsCategories) {
        const recipientCategories = this.normalizeStringArrayValue(vrf.dataRecipientsCategories);
        patch['recipientCategory'] = recipientCategories;

        if (recipientCategories.includes('OTHER') && vrf.dataRecipientsCategoriesOther?.trim()) {
          const recipientCategoryOther = vrf.dataRecipientsCategoriesOther.trim();
          patch['categoryOther'] = recipientCategoryOther;
          patch['recipientCategoryOther'] = recipientCategoryOther;
        }
      }

      // Strumenti IT: decodifica itSystemsUsed, gestisce OTHER separatamente
      if (vrf?.itSystemsUsed) {
        const itOptions: FormDropdownItem[] = (itProgramsOptions as any)?.data ?? [];
        const itKey = vrf.itSystemsUsed;
        if (itKey !== 'OTHER') {
          const decoded = itOptions.find((o) => o.valueKey === itKey)?.valueDescription ?? itKey;
          patch['otherTools'] = decoded;
        }
        // Se OTHER o se c'è testo libero aggiuntivo
        if (itKey === 'OTHER' && vrf.itSystemsOther?.trim()) {
          patch['otherTools'] = vrf.itSystemsOther.trim();
        } else if (itKey !== 'OTHER' && vrf.itSystemsOther?.trim()) {
          // itSystemsOther come integrazione al valore decodificato
          patch['otherTools'] = (patch['otherTools'] ? patch['otherTools'] + ', ' : '') + vrf.itSystemsOther.trim();
        }
      }

      // Misure di sicurezza: concatena i flag boolean messi a true
      const securityFlagLabels: Record<string, string> = {
        credentialsAccess:    'Accesso con credenziali',
        softwareUpdates:      'Aggiornamento software',
        passwordUpdates:      'Aggiornamento password',
        antivirusFirewall:    'Antivirus/Firewall',
        periodicBackup:       'Backup periodico',
        staffTraining:        'Formazione del personale',
        authorizedPersonnelList: 'Elenco personale autorizzato',
      };
      const activeMeasures = Object.entries(securityFlagLabels)
        .filter(([key]) => (vrf as any)?.[key] === true)
        .map(([, label]) => label);
      if (activeMeasures.length > 0) {
        patch['measure'] = activeMeasures.join(', ');
      }

      // Amministratori di sistema
      if (vrf?.systemAdministratorsInfo) {
        patch['adminsText'] = vrf.systemAdministratorsInfo;
      }

      // Periodo di conservazione: default 10 anni per professionisti
      patch['retentionPeriod'] = 10;

      // Modalità di cancellazione: da VRF "Modalità cancellazione dei dati AXA"
      if (vrf?.dataDeletionMethod) {
        patch['deletionMethod'] = vrf.dataDeletionMethod;
      }

      // Cloud
      if (vrf?.cloudStorageUsed != null) {
        patch['usesCloud'] = vrf.cloudStorageUsed;
      }
      // Cloud: the VRF now stores the user-entered text directly.
      if (vrf?.cloudType) {
        patch['cloudInfo'] = vrf.cloudType;
      }

      // Sub-responsabili
      if (vrf?.hasSubResponsibles != null) {
        patch['usesSubResponsibles'] = vrf.hasSubResponsibles;
      }

      // Prefill activityDescription e mainPurpose in base alla categoria professionale
      const professionalType = this.selectedSupplier()?.type;
      const defaultActivityDescription = ['COLLECTION', 'REGISTRATION', 'ORGANIZATION', 'STORAGE', 'CONSULTATION', 'USE', 'TRANSMISSION', 'DELETION'];
      const mainPurposeByType: Record<string, string> = {
        BODYSHOP:     'VEHICLE_REPAIR',
        NK_BODYSHOP:  'VEHICLE_REPAIR',
        HAIL_PROVIDER:'VEHICLE_REPAIR',
        INVESTIGATOR: 'INVESTIGATIVE_ASSIGNMENTS',
        LAWYER:       'LEGAL_DISPUTES',
        DOCTOR:       'MEDICAL_LEGAL_ASSIGNMENTS',
        AUTO_EXPERT:  'DAMAGE_ASSESSMENT',
        RE_EXPERT:    'PROPERTY_DAMAGE_ASSESSMENT',
      };
      if (professionalType) {
        const processedDataTypes = this.professionalProcessedDataTypesMap[professionalType];
        if (processedDataTypes?.length) {
          patch['processedDataTypes'] = [...processedDataTypes];
        }

        const subjectCategories = this.professionalSubjectCategoriesMap[professionalType];
        if (subjectCategories?.length) {
          patch['category'] = [...subjectCategories];
        }

        const purposeValue = mainPurposeByType[professionalType];
        if (purposeValue) {
          patch['mainPurpose'] = purposeValue;
        }
      }

      // Applica solo i campi attualmente vuoti per non sovrascrivere dati già inseriti
      const currentValues = this.step2Form.value;
      const booleanFields = new Set(['usesSubResponsibles', 'transferOutsideEu', 'dataDeletionProgram', 'usesCloud']);

      const filteredPatch: Record<string, any> = {};
      for (const key of Object.keys(patch)) {
        const currentVal = currentValues[key];
        if (booleanFields.has(key)) {
          // Per i boolean: applica sempre se il form è ancora al valore di default (false)
          if (currentVal === false) {
            filteredPatch[key] = patch[key];
          }
        } else {
          const isEmpty =
            currentVal === null ||
            currentVal === undefined ||
            currentVal === '' ||
            (Array.isArray(currentVal) && currentVal.length === 0);
          if (isEmpty) {
            filteredPatch[key] = patch[key];
          }
        }
      }

      if (Object.keys(filteredPatch).length > 0) {
        // Applica senza emitEvent per non triggerare side-effect multipli
        // ma usesSubResponsibles va applicato separatamente con emit per attivare la subscription
        // che rende visibile la sezione nell'HTML
        const { usesSubResponsibles: usesSubPatchValue, ...otherPatch } = filteredPatch;
        if (Object.keys(otherPatch).length > 0) {
          this.step2Form.patchValue(otherPatch, { emitEvent: false });
        }
        if (usesSubPatchValue != null) {
          this.step2Form.get('usesSubResponsibles')?.setValue(usesSubPatchValue);
        }
        if (filteredPatch['recipientCategory'] != null) {
          this.applyRecipientCategoryValue(
            filteredPatch['recipientCategory'],
            filteredPatch['recipientCategoryOther'],
          );
        }
        console.log('✅ Atto di Nomina pre-compilato da VRF:', filteredPatch);
      }

      // activityDescription: precompila con tutte le operazioni standard se il campo è vuoto
      if (professionalType) {
        const currentActivity = this.step2Form.get('activityDescription')?.value;
        if (!currentActivity || currentActivity.length === 0) {
          this.step2Form.patchValue({ activityDescription: defaultActivityDescription }, { emitEvent: false });
        }
      }

      // Prefill sub-responsabili dal VRF (solo se il FormArray è vuoto)
      if (vrf?.hasSubResponsibles && vrf.subResponsibles?.length > 0) {
        const subResponsiblesArray = this.step2Form.get('subResponsibles') as FormArray;
        if (subResponsiblesArray.length === 0) {
          for (const vrfSub of vrf.subResponsibles) {
            const group = this.createSubResponsibleFormGroup();

            // businessName
            if (vrfSub.companyName) {
              group.get('businessName')?.setValue(vrfSub.companyName, { emitEvent: false });
            }

            // headquarters: municipality (province) country
            const subMunicipality = vrfSub.operatingMunicipality || '';
            const subProvince = vrfSub.operatingProvince || '';
            const subCountryCode = vrfSub.headquartersCountry || '';
            const subCountryName =
              (countries as Country[]).find((c) => c.code === subCountryCode)?.name ||
              subCountryCode;
            const subHqParts = [
              subMunicipality,
              subProvince ? `(${subProvince})` : '',
              subCountryName,
            ].filter((p) => p);
            if (subHqParts.length > 0) {
              group.get('headquarters')?.setValue(subHqParts.join(' '), { emitEvent: false });
            }

            // dataProcessingLocation: VRF usa EU/EXTRA_EU, form usa UE/EXTRA_UE
            if (vrfSub.dataProcessingLocation) {
              const locationMap: Record<string, string> = { EU: 'UE', EXTRA_EU: 'EXTRA_UE' };
              const mappedLocation = locationMap[vrfSub.dataProcessingLocation] ?? vrfSub.dataProcessingLocation;
              group.get('dataProcessingLocation')?.setValue(mappedLocation, { emitEvent: false });
            }

            // dataProcessingCountry: usa il "luogo specifico in cui sono trattati i dati" dal VRF (country code)
            // Fallback su headquartersCountry se specificLocationLegalBasis non è valorizzato
            const processingCountry = vrfSub.specificLocationLegalBasis || vrfSub.headquartersCountry;
            if (processingCountry) {
              group.get('dataProcessingCountry')?.setValue(processingCountry, { emitEvent: false });
            }

            // nonEuSafeguard (garanzie extra UE)
            if (vrfSub.extraEuTransferSafeguards) {
              group.get('nonEuSafeguard')?.setValue(vrfSub.extraEuTransferSafeguards, { emitEvent: false });
            }

            // dataTypes
            if (vrfSub.dataTypesProcessed?.length) {
              group.get('dataTypes')?.setValue([...vrfSub.dataTypesProcessed], { emitEvent: false });
            }

            // dataTypesNotes (testo libero "Altro")
            if (vrfSub.dataTypesProcessedOther) {
              group.get('dataTypesNotes')?.setValue(vrfSub.dataTypesProcessedOther, { emitEvent: false });
            }

            // delegatedProcessesDescription
            if (vrfSub.processesDescription) {
              group.get('delegatedProcessesDescription')?.setValue(vrfSub.processesDescription, { emitEvent: false });
            }

            subResponsiblesArray.push(group);
          }
          console.log(`✅ ${vrf.subResponsibles.length} sub-responsabili pre-compilati da VRF`);
        }
      }
    });
  }

  private loadAttoDiNominaData(contractId: string): void {
    this.contractRegistryService
      .getAttoDiNomina(contractId)
      .pipe(
        timeout(15000), // 15 seconds timeout
      )
      .subscribe({
        next: (response: any) => {
          console.log('Atto di Nomina loaded:', response);
          this.attoDiNominaExists.set(true); // Atto exists in DB

          const attoDiNomina = response.data;

          // Load and set status from backend
          if (attoDiNomina.status) {
            this.attoDiNominaStatus.set(attoDiNomina.status);
            console.log('Atto di Nomina status loaded:', attoDiNomina.status);
          }

          // In edit mode, enable navigation to Atto di Nomina step only if status is not null
          // Gestione Documenti step is always disabled in edit mode
          const attoDiNominaStepId = this.getAttoDiNominaStepId();
          if (attoDiNomina.status !== null) {
            this.maxStepReached.update((max) => Math.max(max, attoDiNominaStepId));
          }

          // Mark Atto di Nomina step as completed only if status is not null, undefined or DRAFT
          if (attoDiNomina.status && attoDiNomina.status !== 'DRAFT') {
            this.wizardStepService.markStepCompleted(attoDiNominaStepId, true);
          }

          const responsible = attoDiNomina.responsible;

          // Populate step3Form with atto di nomina template if available
          if (attoDiNomina.attoDiNominaTemplateId) {
            this.step3Form.patchValue({
              templateAttoNomina: attoDiNomina.attoDiNominaTemplateId,
            });

            // Load full template details via API v2 (DocumentAsset businessKey lookup)
            this.documentTemplateV2Service
              .getByBusinessKey(attoDiNomina.attoDiNominaTemplateId)
              .subscribe({
                next: (asset) => {
                  if (asset) {
                    const selectedTemplate = {
                      label: asset.name || asset.businessKey,
                      value: asset.businessKey,
                    };
                    this.selectedTemplateAttoNomina.set(selectedTemplate);
                    console.log(
                      '✅ selectedTemplateAttoNomina loaded from API v2:',
                      selectedTemplate,
                    );
                  } else {
                    // No asset found, try to find in preloaded options
                    this.setAttoDiNominaTemplateFallback(attoDiNomina.attoDiNominaTemplateId);
                  }
                },
                error: (err) => {
                  console.error('Error loading atto di nomina template details:', err);
                  // Fallback: try to find in options
                  this.setAttoDiNominaTemplateFallback(attoDiNomina.attoDiNominaTemplateId);
                },
              });
          }

          // Populate controllers FormArray from backend data (now expecting array)
          const controllersArray = this.step2Form.get('controllers') as FormArray;
          controllersArray.clear();

          // Load all controllers from controllers array
          if (attoDiNomina.controllers && Array.isArray(attoDiNomina.controllers)) {
            attoDiNomina.controllers.forEach((ctrl: any) => {
              const controllerGroup = this.createControllerFormGroup();
              controllerGroup.patchValue({
                id: ctrl.id,
                controllerEntity: ctrl.controllerEntity,
                controllerDpoEmail: ctrl.controllerDpoEmail,
              });

              if (this.isSelectedEntityProfessional()) {
                this.applyBackofficeControllerOwnershipTypeValue(
                  controllerGroup,
                  ctrl.ownershipType ?? ctrl.controllerOwnershipType,
                );
              } else {
                controllerGroup.patchValue({
                  controllerOwnershipType: ctrl.controllerOwnershipType,
                });
              }

              controllersArray.push(controllerGroup);
            });
          }

          // Populate step2Form - Atto di Nomina data
          this.step2Form.patchValue({
            // Responsible data
            responsibleId: responsible.responsibleId,
            responsibleBusinessName: responsible.responsibleBusinessName,
            companyType: responsible.companyType || '',
            responsibleDpoName: responsible.responsibleDpoName || '',
            responsibleDpoSurname: responsible.responsibleDpoSurname || '',
            // intercompany e contractIn rimossi
            headquarters: attoDiNomina.responsible.headquarters || '',
            processedDataTypes: this.normalizeStringArrayValue(responsible.processedDataTypes),
            processedDataNotes: responsible.processedDataNotes || '',
            processDescription: responsible.processDescription,
            responsibleType: responsible.responsibleType,
            dataProcessingLocation: responsible.dataProcessingLocation,
            externalSysAdminsText: responsible.externalSysAdminsText,
            responsibleDpoEmail: responsible.responsibleDpoEmail,
            transferOutsideEu: responsible.transferOutsideEu,
            extraEuLegalBasis: responsible.extraEuLegalBasis || '',
            thirdCountry: responsible.thirdCountry || '',
            tiaDone: responsible.tiaDone || false,
            tiaOutcome: responsible.tiaOutcome || '',
            dataDeletionProgram: responsible.dataDeletionProgram,
            criticalSupplierQualification: responsible.criticalSupplierQualification,
            usesSubResponsibles: responsible.usesSubResponsibles,
            notes: responsible.notes || '',

            // Root-level fields
            measure: attoDiNomina.measure,
            activityDescription: this.isSelectedEntityProfessional()
              ? null
              : Array.isArray(attoDiNomina.activityDescription)
                ? attoDiNomina.activityDescription.join(', ')
                : attoDiNomina.activityDescription,
            mainPurpose: this.isSelectedEntityProfessional() ? null : attoDiNomina.mainPurpose,
            mainPurposeOther: '',
            recipientCategory: [],
            recipientCategoryOther: '',
            adminsText: attoDiNomina.adminsText,
            otherTools: attoDiNomina.otherTools,
            category: [],
            categoryOther: '',
            retentionPeriod: attoDiNomina.retentionPeriod
              ? parseInt(attoDiNomina.retentionPeriod)
              : null,
            retentionCriteria: attoDiNomina.retentionCriteria,
            deletionMethod: attoDiNomina.deletionMethod,
            usesCloud: attoDiNomina.usesCloud || false,
            cloudInfo: attoDiNomina.cloudInfo || '',
          });

          if (this.isSelectedEntityProfessional()) {
            this.applyBackofficeActivityDescriptionValue(attoDiNomina.activityDescription);
            this.applyBackofficeMainPurposeValue(attoDiNomina.mainPurpose);
          }

          this.applyCategoryValue(attoDiNomina.category, attoDiNomina.categoryOther);
          this.applyRecipientCategoryValue(
            attoDiNomina.recipientCategory,
            attoDiNomina.recipientCategoryOther,
          );

          // Prepare parallel requests for responsible and sub-responsibles
          const requests: any = {};

          // Get responsible details by ID from registry (ONLY for suppliers, NOT for professionals)
          if (responsible.responsibleId && !this.isBackofficeProfessional()) {
            requests.responsibleDetails = this.responsibleRegistryService
              .getResponsible(responsible.responsibleId.toString())
              .pipe(
                catchError((err) => {
                  console.error('Error loading responsible details:', err);
                  return of(null);
                }),
              );
          }

          // Get sub-responsibles details by ID from registry (ONLY for suppliers, NOT for professionals)
          if (responsible.subResponsibles && responsible.subResponsibles.length > 0) {
            responsible.subResponsibles.forEach((subResp: any, index: number) => {
              if (subResp.subResponsibleId && !this.isBackofficeProfessional()) {
                requests[`subResp_${index}`] = this.subResponsibleRegistryService
                  .getSubResponsible(subResp.subResponsibleId.toString())
                  .pipe(
                    map((subRespResponse) => ({
                      index,
                      attoDiNominaData: subResp,
                      registryData: subRespResponse.data,
                    })),
                    catchError((err) => {
                      console.error(
                        `Error loading sub-responsible ${subResp.subResponsibleId}:`,
                        err,
                      );
                      return of({
                        index,
                        attoDiNominaData: subResp,
                        registryData: null,
                      });
                    }),
                  );
              }
            });
          }

          // Execute all requests in parallel
          if (Object.keys(requests).length > 0) {
            forkJoin(requests)
              .pipe(
                timeout(10000), // 10 seconds timeout for registry data
              )
              .subscribe({
                next: (results: any) => {
                  // Set selectedResponsible with data from registry
                  if (results.responsibleDetails?.data) {
                    const registryResp = results.responsibleDetails.data;
                    this.selectedResponsible.set({
                      id: registryResp.responsibleId,
                      responsibleId: registryResp.responsibleId,
                      businessName: registryResp.businessName,
                      companyType: registryResp.companyType || '',
                      headquarters: registryResp.headquarters,
                      email: registryResp.email,
                    });
                  } else {
                    // Fallback to Atto di Nomina data if registry call failed
                    this.selectedResponsible.set({
                      id: responsible.responsibleId,
                      responsibleId: responsible.responsibleId,
                      businessName: responsible.responsibleBusinessName,
                      companyType: responsible.companyType || '',
                      headquarters: attoDiNomina.responsible.headquarters || '',
                      email: responsible.responsibleDpoEmail || '',
                    });
                  }

                  // Populate sub-responsibles FormArray with merged data
                  const subResponsiblesArray = this.step2Form.get('subResponsibles') as FormArray;
                  subResponsiblesArray.clear();

                  // Get all sub-responsible results and sort by index
                  const subRespResults = Object.entries(results)
                    .filter(([key]) => key.startsWith('subResp_'))
                    .map(([_, value]) => value as any)
                    .sort((a, b) => a.index - b.index);

                  subRespResults.forEach((result: any) => {
                    const subRespGroup = this.createSubResponsibleFormGroup();
                    const attoDiNominaSubResp = result.attoDiNominaData;
                    const registrySubResp = result.registryData;

                    subRespGroup.patchValue({
                      id: attoDiNominaSubResp.id,
                      subResponsibleId: attoDiNominaSubResp.subResponsibleId,
                      // Use registry data if available, otherwise use Atto di Nomina data
                      businessName:
                        registrySubResp?.businessName || attoDiNominaSubResp.businessName,
                      companyType:
                        registrySubResp?.companyType || attoDiNominaSubResp.companyType || '',
                      headquarters:
                        registrySubResp?.headquarters || attoDiNominaSubResp.headquarters,
                      email: registrySubResp?.email || attoDiNominaSubResp.email,
                      dataProcessingLocation: attoDiNominaSubResp.dataProcessingLocation,
                      dataProcessingCountry: attoDiNominaSubResp.dataProcessingCountry || '',
                      nonEuSafeguard: attoDiNominaSubResp.nonEuSafeguard || '',
                      dataTypes: attoDiNominaSubResp.dataTypes || [],
                      dataTypesNotes: attoDiNominaSubResp.dataTypesNotes || '',
                      delegatedProcessesDescription:
                        attoDiNominaSubResp.delegatedProcessesDescription,
                      notes: attoDiNominaSubResp.notes || '',
                      ...(this.isBackofficeProfessional() && {
                        vatNumber: attoDiNominaSubResp.vatNumber || '',
                        headquartersCountry: attoDiNominaSubResp.headquartersCountry || '',
                        operatingRegion: attoDiNominaSubResp.operatingRegion || '',
                        operatingProvince: attoDiNominaSubResp.operatingProvince || '',
                        headquartersPostalCode: attoDiNominaSubResp.headquartersPostalCode || '',
                        operatingMunicipality:
                          attoDiNominaSubResp.operatingMunicipality || '',
                        operatingStreetType: attoDiNominaSubResp.operatingStreetType || '',
                        operatingAddress: attoDiNominaSubResp.operatingAddress || '',
                        operatingStreetNumber:
                          attoDiNominaSubResp.operatingStreetNumber || '',
                        specificLocationLegalBasis:
                          attoDiNominaSubResp.specificLocationLegalBasis || '',
                      }),
                    });
                    subResponsiblesArray.push(subRespGroup);
                  });

                  // Save original values for comparison
                  this.originalFormValues.set(2, this.step2Form.value);
                  this.originalFormValues.set(3, this.step3Form.value);

                  // Check if step 3 data exists to enable navigation
                  const hasStep3Data = this.checkAndEnableStep3Navigation();

                  // Navigate to first incomplete step
                  const targetStep = this.getFirstIncompleteStep();
                  this.currentStep.set(targetStep);
                  this.wizardStepService.setStep(targetStep);
                  console.log('✅ Navigating to first incomplete step (with registry data):', targetStep);

                  this.isLoading.set(false);

                  this.messageService.add({
                    severity: 'success',
                    summary: 'Dati Caricati',
                    detail: hasStep3Data
                      ? 'Contratto, Atto di Nomina e Documenti caricati con successo'
                      : 'Contratto e Atto di Nomina caricati con successo',
                    life: 3000,
                  });
                },
                error: (err: any) => {
                  console.error('Error loading registry details:', err);

                  // Fallback: use Atto di Nomina data only
                  this.selectedResponsible.set({
                    id: responsible.responsibleId,
                    responsibleId: responsible.responsibleId,
                    businessName: responsible.responsibleBusinessName,
                    companyType: responsible.companyType || '',
                    headquarters: attoDiNomina.responsible.headquarters || '',
                    email: responsible.responsibleDpoEmail || '',
                  });

                  // Populate sub-responsibles with Atto di Nomina data only
                  if (responsible.subResponsibles && responsible.subResponsibles.length > 0) {
                    const subResponsiblesArray = this.step2Form.get('subResponsibles') as FormArray;
                    subResponsiblesArray.clear();

                    responsible.subResponsibles.forEach((subResp: any) => {
                      const subRespGroup = this.createSubResponsibleFormGroup();
                      subRespGroup.patchValue({
                        id: subResp.id,
                        subResponsibleId: subResp.subResponsibleId,
                        businessName: subResp.businessName,
                        companyType: subResp.companyType || '',
                        headquarters: subResp.headquarters,
                        email: subResp.email,
                        dataProcessingLocation: subResp.dataProcessingLocation,
                        dataProcessingCountry: subResp.dataProcessingCountry || '',
                        nonEuSafeguard: subResp.nonEuSafeguard || '',
                        dataTypes: subResp.dataTypes || [],
                        dataTypesNotes: subResp.dataTypesNotes || '',
                        delegatedProcessesDescription: subResp.delegatedProcessesDescription,
                        notes: subResp.notes || '',
                        ...(this.isSelectedEntityProfessional() && {
                          vatNumber: subResp.vatNumber || '',
                          headquartersCountry: subResp.headquartersCountry || '',
                          operatingRegion: subResp.operatingRegion || '',
                          operatingProvince: subResp.operatingProvince || '',
                          headquartersPostalCode: subResp.headquartersPostalCode || '',
                          operatingMunicipality: subResp.operatingMunicipality || '',
                          operatingStreetType: subResp.operatingStreetType || '',
                          operatingAddress: subResp.operatingAddress || '',
                          operatingStreetNumber: subResp.operatingStreetNumber || '',
                          specificLocationLegalBasis: subResp.specificLocationLegalBasis || '',
                        }),
                      });
                      subResponsiblesArray.push(subRespGroup);
                    });
                  }

                  this.originalFormValues.set(2, this.step2Form.value);
                  this.originalFormValues.set(3, this.step3Form.value);

                  // Check if step 3 data exists to enable navigation
                  const hasStep3Data = this.checkAndEnableStep3Navigation();

                  // Navigate to first incomplete step
                  const targetStep = this.getFirstIncompleteStep();
                  this.currentStep.set(targetStep);
                  this.wizardStepService.setStep(targetStep);
                  console.log('✅ Navigating to first incomplete step (error fallback):', targetStep);
                  
                  this.isLoading.set(false);

                  this.messageService.add({
                    severity: 'success',
                    summary: 'Dati Caricati',
                    detail: hasStep3Data
                      ? 'Contratto, Atto di Nomina e Documenti caricati con successo'
                      : 'Contratto e Atto di Nomina caricati con successo',
                    life: 3000,
                  });
                },
              });
          } else {
            // No registry requests - use data directly from atto di nomina
            // This happens for: 1) Professional contracts (always), 2) DRAFT supplier contracts (responsibleId null)
            console.log(
              `📋 Using data directly from Atto di Nomina (no registry lookup) - ${this.isBackofficeProfessional() ? 'Professional contract' : 'DRAFT supplier contract'}`,
            );
            this.selectedResponsible.set({
              id: responsible.responsibleId,
              responsibleId: responsible.responsibleId,
              businessName: responsible.responsibleBusinessName,
              companyType: responsible.companyType || '',
              headquarters: attoDiNomina.responsible.headquarters || '',
              email: responsible.responsibleDpoEmail || '',
            });

            // IMPORTANT: Populate sub-responsibles from Atto di Nomina data
            if (responsible.subResponsibles && responsible.subResponsibles.length > 0) {
              const subResponsiblesArray = this.step2Form.get('subResponsibles') as FormArray;
              subResponsiblesArray.clear();

              responsible.subResponsibles.forEach((subResp: any) => {
                const subRespGroup = this.createSubResponsibleFormGroup();
                subRespGroup.patchValue({
                  id: subResp.id,
                  subResponsibleId: subResp.subResponsibleId,
                  businessName: subResp.businessName,
                  companyType: subResp.companyType || '',
                  headquarters: subResp.headquarters,
                  email: subResp.email,
                  dataProcessingLocation: subResp.dataProcessingLocation,
                  dataProcessingCountry: subResp.dataProcessingCountry || '',
                  nonEuSafeguard: subResp.nonEuSafeguard || '',
                  dataTypes: subResp.dataTypes || [],
                  dataTypesNotes: subResp.dataTypesNotes || '',
                  delegatedProcessesDescription: subResp.delegatedProcessesDescription,
                  notes: subResp.notes || '',
                  ...(this.isBackofficeProfessional() && {
                    vatNumber: subResp.vatNumber || '',
                    headquartersCountry: subResp.headquartersCountry || '',
                    operatingRegion: subResp.operatingRegion || '',
                    operatingProvince: subResp.operatingProvince || '',
                    headquartersPostalCode: subResp.headquartersPostalCode || '',
                    operatingMunicipality: subResp.operatingMunicipality || '',
                    operatingStreetType: subResp.operatingStreetType || '',
                    operatingAddress: subResp.operatingAddress || '',
                    operatingStreetNumber: subResp.operatingStreetNumber || '',
                    specificLocationLegalBasis: subResp.specificLocationLegalBasis || '',
                  }),
                });
                subResponsiblesArray.push(subRespGroup);
              });
            }

            this.originalFormValues.set(2, this.step2Form.value);
            this.originalFormValues.set(3, this.step3Form.value);

            // Check if step 3 data exists to enable navigation
            const hasStep3Data = this.checkAndEnableStep3Navigation();

            // Navigate to first incomplete step
            const targetStep = this.getFirstIncompleteStep();
            this.currentStep.set(targetStep);
            this.wizardStepService.setStep(targetStep);
            console.log('✅ Navigating to first incomplete step:', targetStep);
            
            this.isLoading.set(false);

            this.messageService.add({
              severity: 'success',
              summary: 'Dati Caricati',
              detail: hasStep3Data
                ? 'Contratto, Atto di Nomina e Documenti caricati con successo'
                : 'Contratto e Atto di Nomina caricati con successo',
              life: 3000,
            });
          }
        },
        error: (err: any) => {
          // Atto di Nomina not found - stay on step 1 (Contract)
          console.log('Atto di Nomina not found or error loading:', err);
          this.attoDiNominaExists.set(false); // Atto doesn't exist in DB

          // Pre-compila i campi dell'Atto di Nomina dal questionario VRF (solo per professional, solo se non timeout)
          if (this.isBackofficeProfessional() && !(err instanceof TimeoutError)) {
            const professionalId = this.selectedProfessionalId();
            if (professionalId) {
              this.prefillAttoDiNominaFromVRF(professionalId);
            }
          }

          // Save original values for step 3 even if Atto di Nomina doesn't exist
          this.originalFormValues.set(3, this.step3Form.value);

          // Navigate to first incomplete step
          const targetStep = this.getFirstIncompleteStep();
          this.currentStep.set(targetStep);
          this.maxStepReached.set(1); // Only step 1 accessible initially
          console.log('✅ Navigating to first incomplete step (no Atto di Nomina):', targetStep);

          this.isLoading.set(false);

          const isTimeout = err instanceof TimeoutError;
          this.messageService.add({
            severity: isTimeout ? 'warn' : 'success',
            summary: isTimeout ? 'Timeout Atto di Nomina' : 'Contratto Caricato',
            detail: isTimeout
              ? "Il caricamento dell'Atto di Nomina ha impiegato troppo tempo. Contratto caricato correttamente."
              : 'Dati del contratto caricati con successo',
            life: isTimeout ? 7000 : 3000,
          });
        },
      });
  }

  /**
   * Check if step 3 (Gestione Documenti) has data and enable navigation
   * Called after loading contract and atto di nomina data
   * Returns true if step 3 should be enabled
   */
  private checkAndEnableStep3Navigation(): boolean {
    const contract = this.loadedContractData();
    const attoDiNominaTemplateId = this.step3Form.get('templateAttoNomina')?.value;
    const documentNotes = this.step3Form.get('documentNotes')?.value;
    const uploadedDocs = this.uploadedDocuments();

    // Check if any step 3 data exists:
    // 1. contractTemplateId from loaded contract
    // 2. attoDiNominaTemplateId from form
    // 3. contractDocumentsNote from loaded contract
    // 4. relatedDocuments from backend
    const hasContractTemplateId = contract?.generalInfo?.contractTemplateId;
    const hasAttoDiNominaTemplateId = attoDiNominaTemplateId;
    const hasDocumentNotes = contract?.generalInfo?.contractDocumentsNote || documentNotes;
    const hasRelatedDocuments = contract?.relatedDocuments && contract.relatedDocuments.length > 0;

    const hasStep3Data =
      hasContractTemplateId || hasAttoDiNominaTemplateId || hasDocumentNotes || hasRelatedDocuments;

    if (hasStep3Data) {
      console.log('✅ Step 3 data found, enabling navigation to Gestione Documenti');
      console.log('Step 3 data:', {
        hasContractTemplateId,
        hasAttoDiNominaTemplateId,
        hasDocumentNotes,
        hasRelatedDocuments,
      });

      // Enable step 3 navigation
      this.maxStepReached.update((max) => Math.max(max, 3));

      // Mark step 2 as completed only if atto di nomina status is not null, undefined or DRAFT
      const status = this.attoDiNominaStatus();
      if (status && status !== 'DRAFT') {
        this.wizardStepService.markStepCompleted(2, true);
      }

      // Mark step 3 as completed based on GDPR setting and templates selection
      const hasGdpr = this.shouldShowAttoDiNomina();
      let isStep3Complete = false;

      if (hasGdpr) {
        // Se GDPR abilitato, step 3 completo solo se entrambi i template sono selezionati
        isStep3Complete = hasContractTemplateId && hasAttoDiNominaTemplateId;
      } else {
        // Se GDPR disabilitato, step 3 completo se template contratto è selezionato
        isStep3Complete = !!hasContractTemplateId;
      }

      if (isStep3Complete) {
        this.wizardStepService.markStepCompleted(3, true);
        console.log('✅ Step 3 marked as completed', {
          hasGdpr,
          hasContractTemplateId,
          hasAttoDiNominaTemplateId,
        });
      } else {
        console.log('⚠️ Step 3 NOT marked as completed', {
          hasGdpr,
          hasContractTemplateId,
          hasAttoDiNominaTemplateId,
        });
      }

      return true;
    } else {
      console.log('ℹ️ No step 3 data found, step 3 remains disabled');
      return false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.checklistService.resetChecklistAvailability();
    this.wizardStepService.reset();
  }

  /**
   * Guard method to prevent navigation away from unsaved changes
   * Implements ComponentCanDeactivate interface
   */
  canDeactivate(): boolean | Promise<boolean> {
    // Se ha salvato con successo, permetti la navigazione
    if (this.hasSuccessfullySaved()) {
      return true;
    }

    // Se non ci sono form inizializzati, permetti la navigazione
    if (!this.step0Form || !this.step1Form || !this.step2Form || !this.step3Form) {
      return true;
    }

    // Controlla se almeno uno dei form è stato modificato (dirty)
    const hasUnsavedChanges =
      this.step0Form.dirty || this.step1Form.dirty || this.step2Form.dirty || this.step3Form.dirty;

    // Se ci sono modifiche non salvate, mostra il dialog di conferma
    if (hasUnsavedChanges) {
      return new Promise<boolean>((resolve) => {
        this.confirmDialogService.confirmCancelEdit(
          () => resolve(true), // accept: permetti navigazione
          () => resolve(false), // reject: blocca navigazione
        );
      });
    }

    return true;
  }

  /**
   * Avvisa l'utente se prova a chiudere il tab/finestra con modifiche non salvate
   */
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    // Se ha salvato con successo, permetti la chiusura
    if (this.hasSuccessfullySaved()) {
      return;
    }

    // Se non ci sono form inizializzati, permetti la chiusura
    if (!this.step0Form || !this.step1Form || !this.step2Form || !this.step3Form) {
      return;
    }

    // Controlla se almeno uno dei form è stato modificato (dirty)
    const hasUnsavedChanges =
      this.step0Form.dirty || this.step1Form.dirty || this.step2Form.dirty || this.step3Form.dirty;

    if (hasUnsavedChanges) {
      $event.returnValue = true;
    }
  }

  // ==================== CUSTOM VALIDATORS ====================

  private endDateAfterStartDate(control: AbstractControl): ValidationErrors | null {
    const startDate = control.get('startDate')?.value;
    const endDate = control.get('endDate')?.value;

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (end <= start) {
        return { endDateBeforeStart: true };
      }
    }
    return null;
  }

  // ==================== DROPDOWN LOADING METHODS ====================

  private loadAllDropdowns(): void {
    this.isLoading.set(true);
    this.loadingMessage.set('Caricamento dati in corso...');

    try {
      // Execute all dropdown loading calls in parallel
      const dropdownsObservable = forkJoin({
        tipoContratto: this.formDropdownService
          .getFormDropdownValues(
            'CONTRACT_FORM',
            this.navigationContext.isSupplierContext()
              ? 'SUPPLIER_CONTRACT_TYPE'
              : 'CONTRACT_TYPE',
          )
          .pipe(
            catchError((err) => {
              console.error('CONTRACT_TYPE fallito:', err);
              return of({ status: false, data: [] });
            }),
          ),
        serviceTypes: this.formDropdownService
          .getFormDropdownValues('CONTRACT_FORM', 'SERVICE_TYPE')
          .pipe(
            catchError((err) => {
              console.error('SERVICE_TYPE fallito:', err);
              return of({ status: false, data: [] });
            }),
          ),
        attivitaEster: this.formDropdownService
          .getFormDropdownValues('CONTRACT_FORM', 'OUTSOURCED_ACTIVITY')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        preavvisoRinnovo: this.formDropdownService
          .getFormDropdownValues('CONTRACT_FORM', 'RENEWAL_NOTICE')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        status: this.formDropdownService
          .getFormDropdownValues('CONTRACT_FORM', 'CONTRACT_STATUS')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        entity: this.axaEntityService
          .getAll()
          .pipe(catchError(() => of({ status: false, data: [] }))),
        sinergieCompagnie: this.formDropdownService
          .getFormDropdownValues('CONTRACT_FORM', 'SYNERGY_COMPANY')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        tipoFornitore: this.formDropdownService
          .getFormDropdownValues('CONTRACT_FORM', 'SUPPLIER_TYPE_CONTRACT')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        companyType: this.formDropdownService
          .getFormDropdownValues('LEGAL_PERSON', 'LEGAL_PERSON')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        currency: this.formDropdownService
          .getFormDropdownValues('CURRENCY', 'CURRENCY')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        periodicitaFattura: this.formDropdownService
          .getFormDropdownValues('CONTRACT_FORM', 'INVOICE_FREQUENCY')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        areaClaims: this.formDropdownService
          .getFormDropdownValues('CONTRACT_FORM', 'CLAIMS_AREA')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        casoUsoVRF: this.formDropdownService
          .getFormDropdownValues('CONTRACT_FORM', 'VRF_USE_CASE')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        rinnovoTacito: this.formDropdownService
          .getFormDropdownValues('CONTRACT_FORM', 'TACIT_RENEWAL')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        gestioneInCarico: this.formDropdownService
          .getFormDropdownValues('CONTRACT_FORM', 'MANAGEMENT_IN_CHARGE')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        struttureCoinvolte: this.formDropdownService
          .getFormDropdownValues('CONTRACT_FORM', 'INVOLVED_STRUCTURES')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        tipologiaRipartizione: this.formDropdownService
          .getFormDropdownValues('CONTRACT_FORM', 'DISTRIBUTION_TYPE')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        tipoTitolarita: this.formDropdownService
          .getFormDropdownValues('CONTRACT_FORM', 'OWNERSHIP_TYPE')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        toolGestionali: this.formDropdownService
          .getFormDropdownValues('CONTRACT_FORM', 'MANAGEMENT_TOOLS')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        network: this.formDropdownService
          .getFormDropdownValues('CONTRACT_FORM', 'NETWORK')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        itSystemsUsed: this.formDropdownService
          .getFormDropdownValues('VRF_QUESTIONNAIRE', 'IT_PROGRAMS')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        luogoTrattamento: this.geographyService.getCountries().pipe(
          map((countries) =>
            countries.map((country) => ({
              formId: 'GEO',
              fieldId: 'COUNTRY',
              valueKey: country.code,
              valueDescription: country.name,
            })),
          ),
          catchError(() => of([])),
        ),
        // Atto di Nomina dropdowns
        processedDataTypes: this.formDropdownService
          .getFormDropdownValues('ATTO_DI_NOMINA', 'PROCESSED_DATA_TYPES')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        referenceController: this.formDropdownService
          .getFormDropdownValues('ATTO_DI_NOMINA', 'REFERENCE_CONTROLLER_ENTITIES')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        responsibleType: this.formDropdownService
          .getFormDropdownValues('ATTO_DI_NOMINA', 'RESPONSIBLE_TYPE')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        criticalSupplierQualification: this.formDropdownService
          .getFormDropdownValues('ATTO_DI_NOMINA', 'CRITICAL_SUPPLIER_QUALIFICATION')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        subDataProcessingLocation: this.formDropdownService
          .getFormDropdownValues('ATTO_DI_NOMINA', 'SUB_DATA_PROCESSING_LOCATION')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        activityDescription: this.formDropdownService
          .getFormDropdownValues('ATTO_DI_NOMINA', 'ACTIVITY_DESCRIPTION')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        mainPurpose: this.formDropdownService
          .getFormDropdownValues('ATTO_DI_NOMINA', 'MAIN_PURPOSE')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        deletionMethod: this.formDropdownService
          .getFormDropdownValues('ATTO_DI_NOMINA', 'DELETION_METHOD')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        subjectCategory: this.formDropdownService
          .getFormDropdownValues('ATTO_DI_NOMINA', 'SUBJECT_CATEGORY')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        recipientCategory: this.formDropdownService
          .getFormDropdownValues('ATTO_DI_NOMINA', 'RECIPIENT_CATEGORY')
          .pipe(catchError(() => of({ status: false, data: [] }))),
        nonEuCountries: this.geographyService.getCountries(false).pipe(
          map((countries) =>
            countries.map((country) => ({
              formId: 'GEO',
              fieldId: 'NON_EU_COUNTRY',
              valueKey: country.code,
              valueDescription: country.name,
            })),
          ),
          catchError(() => of([])),
        ),
        // Registri - Non caricare responsible/sub-responsible se in contesto PROFESSIONAL
        responsibles: this.navigationContext.isSupplierContext()
          ? this.responsibleRegistryService.getResponsibles().pipe(
              map((response) => {
                const dataArray = this.responsibleRegistryService.extractResponsiblesArray(response);
                return {
                  options: dataArray.map((r) => ({
                    formId: 'REGISTRY',
                    fieldId: 'responsible',
                    valueKey: r.responsibleId || '',
                    valueDescription: r.businessName,
                  })),
                  tableData: this.responsibleRegistryService.mapResponsiblesForDisplay(dataArray),
                };
              }),
              catchError(() => of({ options: [], tableData: [] })),
            )
          : of({ options: [], tableData: [] }),
        subResponsibles: this.navigationContext.isSupplierContext()
          ? this.subResponsibleRegistryService.getSubResponsibles().pipe(
              map((response) => {
                const dataArray =
                  this.subResponsibleRegistryService.extractSubResponsiblesArray(response);
                return {
                  options: dataArray.map((sr) => ({
                    formId: 'REGISTRY',
                    fieldId: 'sub-responsible',
                    valueKey: sr.subResponsibleId || '',
                    valueDescription: sr.businessName,
                  })),
                  tableData: this.subResponsibleRegistryService.mapSubResponsiblesForDisplay(dataArray),
                };
              }),
              catchError(() => of({ options: [], tableData: [] })),
            )
          : of({ options: [], tableData: [] }),
        // Template loading (v2 DocumentAsset MASTER_TEMPLATE catalog).
        // Il filtro subDomainType è applicato client-side dal servizio.
        templateContratto: this.documentTemplateV2Service
          .listMasterTemplates(this.getTemplateDomainType(), 'CONTRACTS')
          .pipe(
            map((assets) => {
              const selectable = assets.filter(isSelectableTemplate);
              return {
                options: selectable.map((t) => ({
                  label: t.name || t.businessKey,
                  value: t.businessKey,
                })),
                tableData: selectable.map(toTemplateSelectionRow),
              };
            }),
            catchError((err) => {
              console.error('❌ Error loading Template Contratto (v2):', err);
              return of({ options: [], tableData: [] });
            }),
          ),
        templateAttoNomina: this.documentTemplateV2Service
          .listMasterTemplates(this.getTemplateDomainType(), 'ATTO_DI_NOMINA')
          .pipe(
            map((assets) => {
              const selectable = assets.filter(isSelectableTemplate);
              return {
                options: selectable.map((t) => ({
                  label: t.name || t.businessKey,
                  value: t.businessKey,
                })),
                tableData: selectable.map(toTemplateSelectionRow),
              };
            }),
            catchError((err) => {
              console.error('❌ Error loading Template Atto di Nomina (v2):', err);
              return of({ options: [], tableData: [] });
            }),
          ),
      });

      const timedObservable = dropdownsObservable.pipe(
        timeout(30000),
        finalize(() => this.isLoading.set(false)),
      );

      timedObservable.subscribe({
        next: (results) => {
          // Set all dropdown options
          if (results.tipoContratto.status && results.tipoContratto.data) {
            this.tipoContrattoOptions.set(results.tipoContratto.data);
          }
          if (results.serviceTypes.status && results.serviceTypes.data) {
            this.serviceTypes.set(results.serviceTypes.data);
          }
          if (results.attivitaEster.status && results.attivitaEster.data) {
            this.attivitaEsterOptions.set(results.attivitaEster.data);
            this.applySlaKpiPersonValidationRules();
          }
          if (results.preavvisoRinnovo.status && results.preavvisoRinnovo.data) {
            this.preavvisoRinnovoOptions.set(results.preavvisoRinnovo.data);
          }
          if (results.status.status && results.status.data) {
            this.statusOptions.set(results.status.data);
          }
          if (results.entity.status && results.entity.data) {
            this.entityOptions.set(results.entity.data);
          }
          if (results.sinergieCompagnie.status && results.sinergieCompagnie.data) {
            this.sinergieCompagnieOptions.set(results.sinergieCompagnie.data);
          }
          if (results.tipoFornitore.status && results.tipoFornitore.data) {
            this.tipoFornitoreOptions.set(results.tipoFornitore.data);
          }
          if (results.companyType.status && results.companyType.data) {
            this.companyTypeOptions.set(results.companyType.data);
          }
          if (results.currency.status && results.currency.data) {
            this.currencyOptions.set(results.currency.data);
          }
          if (results.periodicitaFattura.status && results.periodicitaFattura.data) {
            this.periodicitaFatturaOptions.set(results.periodicitaFattura.data);
          }
          if (results.areaClaims.status && results.areaClaims.data) {
            this.areaClaimsOptions.set(results.areaClaims.data);
          }
          if (results.casoUsoVRF.status && results.casoUsoVRF.data) {
            this.casoUsoVRFOptions.set(results.casoUsoVRF.data);
          }
          if (results.rinnovoTacito.status && results.rinnovoTacito.data) {
            this.rinnovoTacitoOptions.set(results.rinnovoTacito.data);
          }
          if (results.gestioneInCarico.status && results.gestioneInCarico.data) {
            this.gestioneInCaricoOptions.set(results.gestioneInCarico.data);
          }
          if (results.struttureCoinvolte.status && results.struttureCoinvolte.data) {
            this.struttureCoinvolteOptions.set(results.struttureCoinvolte.data);
            this.applyDefaultInvolvedStructuresForCreation();
          }
          if (results.tipologiaRipartizione.status && results.tipologiaRipartizione.data) {
            this.tipologiaRipartizioneOptions.set(results.tipologiaRipartizione.data);
          }
          if (results.tipoTitolarita.status && results.tipoTitolarita.data) {
            this.tipoTitolaritaOptions.set(results.tipoTitolarita.data);
            this.applyDefaultControllerOwnershipTypeForCreation();
          }
          if (results.toolGestionali.status && results.toolGestionali.data) {
            this.toolGestionaliOptions.set(results.toolGestionali.data);
          }
          if (results.network.status && results.network.data) {
            this.networkOptions.set(results.network.data);
          }
          if (results.itSystemsUsed.status && results.itSystemsUsed.data) {
            this.itSystemsUsedOptions.set(results.itSystemsUsed.data);
          }
          this.luogoTrattamentoOptions.set(results.luogoTrattamento);

          // Atto di Nomina dropdowns
          if (results.processedDataTypes.status && results.processedDataTypes.data) {
            this.processedDataTypesOptions.set(results.processedDataTypes.data);
          }
          if (results.referenceController.status && results.referenceController.data) {
            this.referenceControllerOptions.set(results.referenceController.data);
          }
          if (results.responsibleType.status && results.responsibleType.data) {
            this.responsibleTypeOptions.set(results.responsibleType.data);
          }
          if (
            results.criticalSupplierQualification.status &&
            results.criticalSupplierQualification.data
          ) {
            this.criticalSupplierQualificationOptions.set(
              results.criticalSupplierQualification.data,
            );
          }
          if (results.subDataProcessingLocation.status && results.subDataProcessingLocation.data) {
            this.subDataProcessingLocationOptions.set(results.subDataProcessingLocation.data);
          }
          if (results.activityDescription.status && results.activityDescription.data) {
            this.activityDescriptionOptions.set(results.activityDescription.data);
          }
          if (results.mainPurpose.status && results.mainPurpose.data) {
            this.mainPurposeOptions.set(results.mainPurpose.data);
          }
          if (results.deletionMethod.status && results.deletionMethod.data) {
            this.deletionMethodOptions.set(results.deletionMethod.data);
          }
          if (results.subjectCategory.status && results.subjectCategory.data) {
            this.subjectCategoryOptions.set(results.subjectCategory.data);
            this.applyCategoryValue(
              this.step2Form.get('category')?.value,
              this.step2Form.get('categoryOther')?.value,
            );
          }
          if (results.recipientCategory.status && results.recipientCategory.data) {
            this.recipientCategoryOptions.set(results.recipientCategory.data);
            this.applyRecipientCategoryValue(
              this.step2Form.get('recipientCategory')?.value,
              this.step2Form.get('recipientCategoryOther')?.value,
            );
          }
          this.nonEuCountriesOptions.set(results.nonEuCountries);

          // Registri
          this.responsibleOptions.set(results.responsibles.options);
          this.responsibleTableData.set(results.responsibles.tableData);
          this.subResponsibleOptions.set(results.subResponsibles.options);
          this.subResponsibleTableData.set(results.subResponsibles.tableData);

          // Template
          this.templateContrattoOptions.set(results.templateContratto.options);
          this.templateContrattoTableData.set(results.templateContratto.tableData);

          this.templateAttoNominaOptions.set(results.templateAttoNomina.options);
          this.templateAttoNominaTableData.set(results.templateAttoNomina.tableData);

          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('Errore caricamento dropdowns:', err);
          this.isLoading.set(false);

          const isTimeout = err instanceof TimeoutError;
          this.messageService.add({
            severity: 'error',
            summary: isTimeout ? 'Timeout' : 'Errore',
            detail: isTimeout
              ? 'Il caricamento dei dati ha impiegato troppo tempo. Verifica la connessione al server.'
              : 'Impossibile caricare alcuni dati. Riprova più tardi.',
            life: 7000,
          });
        },
      });
    } catch (error) {
      console.error('Errore critico in loadAllDropdowns:', error);
      this.isLoading.set(false);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore Critico',
        detail: 'Impossibile inizializzare il caricamento dati',
        life: 7000,
      });
    }
  }

  private applyDefaultInvolvedStructuresForCreation(): void {
    if (this.contractId() !== null || !this.isSelectedEntityProfessional()) {
      return;
    }

    const involvedStructuresControl = this.step1Form.get('involvedStructures');
    const currentStructures = involvedStructuresControl?.value;

    if (!involvedStructuresControl || !Array.isArray(currentStructures) || currentStructures.length > 0) {
      return;
    }

    const defaultOption = this.struttureCoinvolteOptions().find(
      (option) => option.valueKey === 'GESTIONE_FIDUCIARI_E_PRONTA_LIQUIDAZIONE',
    );

    if (defaultOption) {
      involvedStructuresControl.setValue([defaultOption.valueKey], { emitEvent: false });
    }
  }

  private applyDefaultControllerOwnershipTypeForCreation(): void {
    if (this.isEditingExistingContract) {
      return;
    }

    const defaultOwnershipType = this.getDefaultControllerOwnershipType();

    if (!defaultOwnershipType) {
      return;
    }

    this.controllersArray.controls.forEach((controller) => {
      const ownershipTypeControl = controller.get('controllerOwnershipType');
      if (ownershipTypeControl && !ownershipTypeControl.value && !ownershipTypeControl.dirty) {
        ownershipTypeControl.setValue(defaultOwnershipType, { emitEvent: false });
      }
    });
  }

  /**
   * Reload only responsibles list (used when opening dialog or after creating a new responsible)
   * Filtra il responsabile già selezionato dalla lista
   * NON carica se siamo nel contesto PROFESSIONAL (fiduciari)
   */
  private loadResponsibles(): void {
    // Skip loading if in PROFESSIONAL context
    if (!this.navigationContext.isSupplierContext()) {
      console.log('[Contract Form] Skipping responsibles load - PROFESSIONAL context');
      return;
    }
    
    this.loadingResponsibles.set(true);
    this.responsibleRegistryService
      .getResponsibles()
      .pipe(
        timeout(10000), // 10 seconds timeout
        map((response) => {
          const dataArray = this.responsibleRegistryService.extractResponsiblesArray(response);

          // Ottieni l'ID del responsabile già selezionato
          const selectedResponsibleId =
            this.selectedResponsible()?.id || this.step2Form.get('responsibleId')?.value;

          // Filtra il responsabile già utilizzato
          const filteredDataArray = selectedResponsibleId
            ? dataArray.filter((r) => r.responsibleId !== selectedResponsibleId)
            : dataArray;

          return {
            options: filteredDataArray.map((r) => ({
              formId: 'REGISTRY',
              fieldId: 'responsible',
              valueKey: r.responsibleId || '',
              valueDescription: this.getResponsibleDisplayName(r),
            })),
            tableData: this.responsibleRegistryService.mapResponsiblesForDisplay(filteredDataArray),
          };
        }),
        catchError((err) => {
          console.error('Error loading responsibles:', err);
          const isTimeout = err instanceof TimeoutError;
          this.messageService.add({
            severity: 'error',
            summary: isTimeout ? 'Timeout' : 'Errore',
            detail: isTimeout
              ? 'Il caricamento dei responsabili ha impiegato troppo tempo'
              : 'Impossibile caricare i responsabili dal registro',
          });
          return of({ options: [], tableData: [] });
        }),
      )
      .subscribe((result) => {
        this.responsibleOptions.set(result.options);
        this.responsibleTableData.set(result.tableData);
        this.loadingResponsibles.set(false);
      });
  }

  /**
   * Reload only sub-responsibles list (used when opening dialog or after creating a new sub-responsible)
   * Filtra i sub-responsabili già aggiunti dalla lista
   * NON carica se siamo nel contesto PROFESSIONAL (fiduciari)
   */
  private loadSubResponsibles(): void {
    // Skip loading if in PROFESSIONAL context
    if (!this.navigationContext.isSupplierContext()) {
      console.log('[Contract Form] Skipping sub-responsibles load - PROFESSIONAL context');
      return;
    }
    
    this.loadingSubResponsibles.set(true);
    this.subResponsibleRegistryService
      .getSubResponsibles()
      .pipe(
        timeout(10000), // 10 seconds timeout
        map((response) => {
          const dataArray =
            this.subResponsibleRegistryService.extractSubResponsiblesArray(response);

          // Ottieni gli ID dei sub-responsabili già aggiunti nel FormArray
          const subResponsiblesArray = this.step2Form.get('subResponsibles') as FormArray;
          const usedSubResponsibleIds = subResponsiblesArray.controls
            .map((control) => control.get('subResponsibleId')?.value)
            .filter((id) => id); // Rimuovi null/undefined

          // Filtra i sub-responsabili già utilizzati
          const filteredDataArray = dataArray.filter(
            (sr) => !usedSubResponsibleIds.includes(sr.subResponsibleId),
          );

          return {
            options: filteredDataArray.map((sr) => ({
              formId: 'REGISTRY',
              fieldId: 'sub-responsible',
              valueKey: sr.subResponsibleId || '',
              valueDescription: sr.businessName,
            })),
            tableData:
              this.subResponsibleRegistryService.mapSubResponsiblesForDisplay(filteredDataArray),
          };
        }),
        catchError((err) => {
          console.error('Error loading sub-responsibles:', err);
          const isTimeout = err instanceof TimeoutError;
          this.messageService.add({
            severity: 'error',
            summary: isTimeout ? 'Timeout' : 'Errore',
            detail: isTimeout
              ? 'Il caricamento dei sub-responsabili ha impiegato troppo tempo'
              : 'Impossibile caricare i sub-responsabili dal registro',
          });
          return of({ options: [], tableData: [] });
        }),
      )
      .subscribe((result) => {
        this.subResponsibleOptions.set(result.options);
        this.subResponsibleTableData.set(result.tableData);
        this.loadingSubResponsibles.set(false);
      });
  }

  // ==================== END DROPDOWN LOADING METHODS ====================

  private setupConditionalValidation(): void {
    // Fornitori: Owner SLA & KPI non obbligatorio se Attività esternalizzata = No
    this.step1Form.get('outsourcedActivity')?.valueChanges.subscribe(() => {
      this.applySlaKpiPersonValidationRules();
    });

    // Mostra/nascondi compagnie sinergie
    this.step1Form.get('synergies')?.valueChanges.subscribe((value) => {
      const synergiesCompaniesControl = this.step1Form.get('synergiesCompanies');
      if (value) {
        synergiesCompaniesControl?.setValidators([Validators.required]);
      } else {
        synergiesCompaniesControl?.clearValidators();
        synergiesCompaniesControl?.setValue([]);
      }
      synergiesCompaniesControl?.updateValueAndValidity();
    });

    // Validazione condizionale rinnovo tacito - mostra preavviso rinnovo se YES o ANNUAL
    this.step1Form.get('automaticRenewal')?.valueChanges.subscribe((value) => {
      this.applyNumberOfRenewalsValidationRules();
      const renewalNoticeDaysControl = this.step1Form.get('renewalNoticeDays');

      if (value === 'YES' || value === 'ANNUAL') {
        renewalNoticeDaysControl?.setValidators([Validators.required]);
        // ✅ Imposta 90 come valore di default se il campo è vuoto
        if (!renewalNoticeDaysControl?.value) {
          renewalNoticeDaysControl?.setValue(90);
        }
      } else {
        renewalNoticeDaysControl?.clearValidators();
        renewalNoticeDaysControl?.setValue(null);
      }
      renewalNoticeDaysControl?.updateValueAndValidity();
    });

    // Validazione condizionale accordo quadro - tutti i campi obbligatori se sì
    this.step1Form.get('frameworkAgreement')?.valueChanges.subscribe((value) => {
      const frameworkStartDateControl = this.step1Form.get('frameworkStartDate');
      const frameworkEndDateControl = this.step1Form.get('frameworkEndDate');
      const frameworkAutomaticRenewalControl = this.step1Form.get('frameworkAutomaticRenewal');
      const frameworkRenewalNoticeDaysControl = this.step1Form.get('frameworkRenewalNoticeDays');
      const expectedFee = this.step1Form.get('expectedFee');

      // For professional contracts, frameworkAgreement fields are always hidden and not required
      if (this.isSelectedEntityProfessional()) {
        frameworkStartDateControl?.clearValidators();
        frameworkEndDateControl?.clearValidators();
        frameworkAutomaticRenewalControl?.clearValidators();
        frameworkRenewalNoticeDaysControl?.clearValidators();
        expectedFee?.clearValidators();

        frameworkStartDateControl?.updateValueAndValidity();
        frameworkEndDateControl?.updateValueAndValidity();
        frameworkAutomaticRenewalControl?.updateValueAndValidity();
        frameworkRenewalNoticeDaysControl?.updateValueAndValidity();
        expectedFee?.updateValueAndValidity();
        return;
      }

      if (value) {
        // Tutti obbligatori quando accordo quadro = true
        frameworkStartDateControl?.setValidators([Validators.required]);
        frameworkEndDateControl?.setValidators([Validators.required]);
        frameworkAutomaticRenewalControl?.setValidators([Validators.required]);
        frameworkRenewalNoticeDaysControl?.setValidators([Validators.required]);
        expectedFee?.setValidators([Validators.required]);
      } else {
        // Rimuovi validatori e imposta a null i campi nascosti
        frameworkStartDateControl?.clearValidators();
        frameworkEndDateControl?.clearValidators();
        frameworkAutomaticRenewalControl?.clearValidators();
        frameworkRenewalNoticeDaysControl?.clearValidators();
        expectedFee?.clearValidators();
        frameworkStartDateControl?.setValue(null);
        frameworkEndDateControl?.setValue(null);
        frameworkAutomaticRenewalControl?.setValue(null);
        frameworkRenewalNoticeDaysControl?.setValue(null);
        expectedFee?.setValue(null);
      }
      frameworkStartDateControl?.updateValueAndValidity();
      frameworkEndDateControl?.updateValueAndValidity();
      frameworkAutomaticRenewalControl?.updateValueAndValidity();
      frameworkRenewalNoticeDaysControl?.updateValueAndValidity();
      expectedFee?.updateValueAndValidity();
    });

    // Mostra/nascondi note rebates
    this.step1Form.get('rebates')?.valueChanges.subscribe((value) => {
      const rebatesNoteControl = this.step1Form.get('rebatesNote');
      if (!value) {
        rebatesNoteControl?.setValue(null);
      }
    });

    // Mostra/nascondi funzione privacy se GDPR è No
    // Update validators based on GDPR setting
    this.step1Form.get('gdprDataProcessing')?.valueChanges.subscribe((value) => {
      const privacyResponsibleFunctionControl = this.step1Form.get('privacyResponsibleFunction');
      const templateAttoNominaControl = this.step3Form.get('templateAttoNomina');

      if (value) {
        // privacyResponsibleFunction is required only for suppliers, not for professionals
        if (!this.isSelectedEntityProfessional()) {
          privacyResponsibleFunctionControl?.setValidators([Validators.required]);
        }
        templateAttoNominaControl?.setValidators([Validators.required]);
      } else {
        privacyResponsibleFunctionControl?.clearValidators();
        privacyResponsibleFunctionControl?.setValue(null);
        templateAttoNominaControl?.clearValidators();
        templateAttoNominaControl?.setValue(null);
      }
      privacyResponsibleFunctionControl?.updateValueAndValidity();
      templateAttoNominaControl?.updateValueAndValidity();
    });

    // Step 2: Validazione condizionale trasferimento dati extra UE
    this.step2Form.get('transferOutsideEu')?.valueChanges.subscribe((value) => {
      const extraEuLegalBasisControl = this.step2Form.get('extraEuLegalBasis');
      const thirdCountryControl = this.step2Form.get('thirdCountry');
      const tiaDoneControl = this.step2Form.get('tiaDone');

      if (value) {
        // Per i fiduciari base legale e paese terzo restano opzionali
        if (!this.isBackofficeProfessional()) {
          extraEuLegalBasisControl?.setValidators([Validators.required]);
          thirdCountryControl?.setValidators([Validators.required]);
        } else {
          extraEuLegalBasisControl?.clearValidators();
          thirdCountryControl?.clearValidators();
        }
        tiaDoneControl?.setValidators([Validators.required]);
      } else {
        // Rimuovi validatori e imposta a null i campi nascosti
        extraEuLegalBasisControl?.clearValidators();
        thirdCountryControl?.clearValidators();
        tiaDoneControl?.clearValidators();
        extraEuLegalBasisControl?.setValue(null);
        thirdCountryControl?.setValue(null);
        tiaDoneControl?.setValue(null);
      }
      extraEuLegalBasisControl?.updateValueAndValidity();
      thirdCountryControl?.updateValueAndValidity();
      tiaDoneControl?.updateValueAndValidity();
    });

    // Step 2: Validazione condizionale TIA outcome quando tiaDone = true
    this.step2Form.get('tiaDone')?.valueChanges.subscribe((value) => {
      const tiaOutcomeControl = this.step2Form.get('tiaOutcome');

      if (value === true) {
        // Campo obbligatorio quando tiaDone = true
        tiaOutcomeControl?.setValidators([Validators.required, NoSpecialCharsValidator('free-text')]);
      } else {
        // Rimuovi validatori e imposta a null il campo nascosto
        tiaOutcomeControl?.clearValidators();
        tiaOutcomeControl?.setValue('');
      }
      tiaOutcomeControl?.updateValueAndValidity();
    });

    // Step 2: Gestione sezione sub-responsabili
    this.step2Form.get('usesSubResponsibles')?.valueChanges.subscribe((value) => {
      const subResponsiblesArray = this.step2Form.get('subResponsibles') as FormArray;

      if (value) {
        // Se abilitato, aggiungi validatore required
        subResponsiblesArray.setValidators([Validators.required]);
        subResponsiblesArray.updateValueAndValidity();
      } else {
        // Se disabilitato, rimuovi validatore
        subResponsiblesArray.clearValidators();
        subResponsiblesArray.updateValueAndValidity();

        // Se ci sono elementi, chiedi conferma prima di svuotare
        if (subResponsiblesArray.length > 0) {
          this.confirmDialogService.confirm({
            message:
              'Disabilitando i Sub-Responsabili, tutti i sub-responsabili aggiunti verranno rimossi. Vuoi continuare?',
            header: 'Conferma rimozione',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sì, rimuovi',
            rejectLabel: 'Annulla',
            accept: () => {
              // Svuota l'array di sub-responsabili
              subResponsiblesArray.clear();
              this.messageService.add({
                severity: 'info',
                summary: 'Sub-Responsabili rimossi',
                detail: 'Tutti i sub-responsabili sono stati rimossi',
                life: 3000,
              });
            },
            reject: () => {
              // Ripristina il valore precedente (Sì)
              this.step2Form.get('usesSubResponsibles')?.setValue(true, { emitEvent: false });
            },
          });
        }
      }
    });

    // Validazione condizionale activityDescriptionOther: obbligatorio quando OTHER è selezionato
    this.step2Form.get('activityDescription')?.valueChanges.subscribe((value: string[]) => {
      const otherControl = this.step2Form.get('activityDescriptionOther');
      if (!otherControl) return;
      if (this.isBackofficeProfessional() && value?.includes('OTHER')) {
        otherControl.setValidators([Validators.required]);
      } else {
        otherControl.clearValidators();
      }
      otherControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  private initializeForms(): void {
    // Step 0: Selezione Fornitore
    this.step0Form = this.fb.group({
      supplierId: ['', Validators.required],
    });

    // Step 1: Contratto - Tutti i dati del contratto
    this.step1Form = this.fb.group(
      {
        // Informazioni Generali
        contractType: ['', Validators.required],
        serviceType: ['', Validators.required],
        contractSubject: ['', [Validators.required, NoSpecialCharsValidator('free-text')]],
        outsourcedActivity: ['', Validators.required],
        slaKpiPerson: ['', [Validators.required, NoSpecialCharsValidator('name')]],
        startDate: ['', Validators.required],
        endDate: ['', Validators.required],
        numberOfRenewals: [5],
        automaticRenewal: ['', Validators.required],
        terminationNoticeDays: [90, Validators.required],
        renewalNoticeDays: [null], // Condizionale: obbligatorio solo se automaticRenewal = YES o ANNUAL
        involvedEntities: this.fb.array([this.createInvolvedEntityFormGroup()], Validators.required),
        synergies: [false, Validators.required],
        synergiesCompanies: [[]],
        involvedSc: [''],
        supplierType: ['', Validators.required],
        managementTools: [[]],  // Condizionale: obbligatorio solo per backoffice professional
        network: [[]],  // Condizionale: obbligatorio solo per backoffice professional
        status: [null], // Status generale contratto - gestito dal BE, rimandato tale e quale

        // Contratto Quadro
        frameworkAgreement: [false, Validators.required],
        frameworkStartDate: [null], // Condizionale: obbligatorio solo se frameworkAgreement = true
        frameworkEndDate: [null], // Condizionale: obbligatorio solo se frameworkAgreement = true
        frameworkAutomaticRenewal: [null], // Condizionale: obbligatorio solo se frameworkAgreement = true
        frameworkRenewalNoticeDays: [null], // Condizionale: obbligatorio solo se frameworkAgreement = true

        // Economics
        expectedFee: [null], // Condizionale: obbligatorio solo se frameworkAgreement = true
        rebates: [false, Validators.required],
        rebatesNote: [null, NoSpecialCharsValidator('free-text')], // Condizionale: visibile solo se rebates = true
        invoiceOnSol: [false, Validators.required],
        technicalAccount: [false, Validators.required],
        allocationMatrix: [''], // Non obbligatorio
        expenseAccount: [''], // Non obbligatorio
        currency: ['EUR'], // Non obbligatorio
        totalContractValue: [0],
        annualValue: [0],
        invoiceFrequency: [''],

        // Amministrazione
        managingArea: ['', Validators.required],
        claimsAreaReference: ['', Validators.required],
        eventualReferent: ['', [Validators.required, NoSpecialCharsValidator('name')]],
        buyer: ['', [Validators.required, NoSpecialCharsValidator('name')]],
        involvedStructures: [[], Validators.required],
        invoiceRecipientEmail: ['', [NoSpecialCharsValidator('free-text')]],
        controlsOwner: ['', [NoSpecialCharsValidator('name')]],
        paymentAuthorizer: ['', [NoSpecialCharsValidator('name')]],
        documentationAtIP: [false, Validators.required],
        supplierSignatory: ['', [Validators.required, NoSpecialCharsValidator('name')]],
        supplierSignatoryRole: ['', [Validators.required, NoSpecialCharsValidator('free-text')]],
        professionalSignatory: [''], // Condizionale: obbligatorio solo se professional context
        professionalSignatoryRole: [''], // Condizionale: obbligatorio solo se professional context

        // VRF - Trattamento Dati
        vrfUseCase: ['', Validators.required],
        gdprDataProcessing: [false, Validators.required],
        privacyResponsibleFunction: [null], // Condizionale: obbligatorio solo se gdprDataProcessing = true
        dataTransferOutsideSee: [false, Validators.required],

        // Contract-in Reference Section
        contractInNumber: [''], // n. reference di Contract-in (non obbligatorio)
      },
      { validators: this.endDateAfterStartDate.bind(this) },
    );

    // Step 2: Atto di Nomina - Allineato al mapping BE
    this.step2Form = this.fb.group({
      // TITOLARE DEL TRATTAMENTO - FormArray per gestire DPO e tipo titolarità per ogni entità
      controllers: this.fb.array([], Validators.required), // Array di {entity, dpoEmail, ownershipType}

      // RESPONSABILE DEL TRATTAMENTO (singolo)
      responsibleId: [null, Validators.required], // ID from Registro Responsabili
      responsibleBusinessName: ['', [Validators.required, NoSpecialCharsValidator('free-text')]], // Auto-populated
      companyType: [''],
      responsibleDpoName: ['', NoSpecialCharsValidator('name')],
      responsibleDpoSurname: ['', NoSpecialCharsValidator('name')], // Cognome DPO (solo professional)
      // intercompany e contractIn rimossi
      headquarters: ['', [Validators.required, NoSpecialCharsValidator('address')]], // Auto-populated (headquarters in BE)
      processedDataTypes: [[], Validators.required], // Multiplo: PERSONALI, SENSIBILI, GIUDIZIARI
      processedDataNotes: ['', NoSpecialCharsValidator('free-text')], // Note libere sui dati trattati
      processDescription: ['', [Validators.required, NoSpecialCharsValidator('free-text')]], // From contract subject
      responsibleType: [''], // AGENTE, BANCA, BROKER, etc.
      dataProcessingLocation: ['IT', [NoSpecialCharsValidator('address')]],
      externalSysAdminsText: ['', [Validators.required, NoSpecialCharsValidator('free-text')]],
      responsibleDpoEmail: ['', [Validators.required, Validators.email, NoSpecialCharsValidator('strict')]],
      transferOutsideEu: [false, Validators.required],
      extraEuLegalBasis: [null], // Condizionale: obbligatorio solo se transferOutsideEu=true
      thirdCountry: [null], // Condizionale: obbligatorio solo se transferOutsideEu=true
      tiaDone: [null], // Condizionale: obbligatorio solo se transferOutsideEu=true
      tiaOutcome: [''], // Condizionale: obbligatorio solo se tiaDone=true
      dataDeletionProgram: [false, Validators.required],
      criticalSupplierQualification: ['', Validators.required],
      usesSubResponsibles: [false, Validators.required],
      notes: ['', NoSpecialCharsValidator('free-text')], // Note per Registro Responsabili trattamento
      subResponsibles: this.fb.array([]), // Array of sub-responsibles

      // OPERAZIONI DI TRATTAMENTO
      activityDescription: [[], Validators.required], // MultiSelect: array di chiavi
      activityDescriptionOther: [''],

      // FINALITÀ DEL TRATTAMENTO
      mainPurpose: ['', Validators.required],
      mainPurposeOther: [''],

      // CATEGORIE DI SOGGETTI
      category: [[], Validators.required], // Array: CLIENTI, POTENZIALI_CLIENTI, DIPENDENTI
      categoryOther: ['', NoSpecialCharsValidator('free-text')],

      // DESTINATARI UE/extra UE
      recipientCategory: [[], Validators.required],
      recipientCategoryOther: ['', NoSpecialCharsValidator('free-text')],

      // CONSERVAZIONE E CANCELLAZIONE
      retentionPeriod: [null, this.isBackofficeProfessional() ? null : Validators.required], // Numerico
      retentionCriteria: [''],
      deletionMethod: ['', Validators.required],

      // STRUMENTI UTILIZZATI
      otherTools: ['', Validators.required],

      // MISURE DI SICUREZZA
      measure: ['', Validators.required],

      // AMMINISTRATORI DI SISTEMA
      adminsText: ['', Validators.required],

      // INFORMAZIONI SUL CLOUD
      usesCloud: [false],
      cloudInfo: [''],
    });

    if (this.isBackofficeProfessional()) {
      this.step2Form.get('mainPurpose')?.valueChanges.subscribe((value) => {
        this.updateBackofficeMainPurposeOtherValidators(value ?? null);
      });

      this.updateBackofficeMainPurposeOtherValidators(
        this.step2Form.get('mainPurpose')?.value ?? null,
      );
    }

    // Validazione condizionale processedDataNotes: obbligatorio quando OTHER è selezionato
    this.step2Form.get('processedDataTypes')?.valueChanges.subscribe((values) => {
      const normalizedValues = Array.isArray(values)
        ? values
        : this.coerceMultiSelectControlValue('processedDataTypes');
      this.updateProcessedDataNotesValidators(normalizedValues);
    });

    this.step2Form.get('category')?.valueChanges.subscribe((values) => {
      if (!Array.isArray(values)) {
        this.coerceMultiSelectControlValue('category');
      }
      this.updateMultiSelectOtherValidators('category', 'categoryOther');
    });

    this.step2Form.get('recipientCategory')?.valueChanges.subscribe((values) => {
      if (!Array.isArray(values)) {
        this.coerceMultiSelectControlValue('recipientCategory');
      }
      this.updateMultiSelectOtherValidators('recipientCategory', 'recipientCategoryOther');
    });

    this.coerceMultiSelectControlValue('processedDataTypes');
    this.coerceMultiSelectControlValue('category');
    this.coerceMultiSelectControlValue('recipientCategory');

    this.updateMultiSelectOtherValidators('category', 'categoryOther');
    this.updateMultiSelectOtherValidators('recipientCategory', 'recipientCategoryOther');

    // Gestione validazione condizionale cloudInfo basata su usesCloud
    this.step2Form.get('usesCloud')?.valueChanges.subscribe((value) => {
      const cloudInfoControl = this.step2Form.get('cloudInfo');
      if (value === true) {
        cloudInfoControl?.setValidators([Validators.required]);
      } else {
        cloudInfoControl?.clearValidators();
        cloudInfoControl?.setValue(null); // Reset campo quando non si usa il Cloud
      }
      cloudInfoControl?.updateValueAndValidity();
    });

    // Inizializza con un titolare vuoto per UX migliore
    this.addController();

    // Step 3: Gestione Documenti
    this.step3Form = this.fb.group({
      templateContratto: [null, Validators.required],
      templateAttoNomina: [null], // Required only if GDPR is enabled
      documentNotes: [''],
    });
  }

  private initializeWizardSteps(): void {
    const selectionLabel = this.isBackofficeProfessional()
      ? 'Selezione Fiduciario'
      : 'Selezione Fornitore';
    const entityLabel = this.isBackofficeProfessional() ? 'fiduciario' : 'fornitore';

    const steps: WizardStep[] = [
      {
        id: 0,
        label: selectionLabel,
        active: true,
        completed: false,
        icon: this.isBackofficeProfessional() ? 'pi pi-users' : 'pi pi-building',
        command: () => this.goToStep(0),
        disabled: false,
      },
      {
        id: 1,
        label: 'Contratto',
        active: false,
        completed: false,
        icon: 'pi pi-file-edit',
        command: () => this.goToStep(1),
        disabled: true, // Inizialmente disabilitato fino a selezione fornitore/fiduciario
        tooltipMessage: `Per accedere allo step Contratto devi prima selezionare il ${entityLabel}`,
      },
    ];

    // Step Tariffe (solo per professional)
    if (this.isBackofficeProfessional()) {
      steps.push({
        id: 2,
        label: 'Tariffe',
        active: false,
        completed: false,
        icon: 'pi pi-euro',
        command: () => this.goToStep(2),
        disabled: true, // Inizialmente disabilitato, verrà abilitato quando contratto è salvato
        tooltipMessage: 'Per accedere allo step Tariffe devi prima selezionare il fiduciario',
      });
    }

    // Atto di Nomina (id 2 per supplier, id 3 per professional)
    const attoDiNominaTooltip = `Per accedere allo step Atto di Nomina devi prima selezionare il ${entityLabel}`;

    steps.push({
      id: this.isBackofficeProfessional() ? 3 : 2,
      label: 'Atto di Nomina',
      active: false,
      completed: false,
      icon: 'pi pi-id-card',
      command: () => this.goToStep(this.isBackofficeProfessional() ? 3 : 2),
      disabled: true, // Inizialmente disabilitato
      tooltipMessage: attoDiNominaTooltip,
    });

    // Gestione Documenti (id 3 per supplier, id 4 per professional)
    steps.push({
      id: this.isBackofficeProfessional() ? 4 : 3,
      label: 'Gestione Documenti',
      active: false,
      completed: false,
      icon: 'pi pi-folder-open',
      command: () => this.goToStep(this.isBackofficeProfessional() ? 4 : 3),
      disabled: true, // Inizialmente disabilitato
      tooltipMessage: `Per accedere allo step Gestione Documenti devi prima selezionare il ${entityLabel}`,
    });

    this.wizardStepService.steps.set(steps);
  }

  // Metodo per creare un FormGroup per un sub-responsabile
  createSubResponsibleFormGroup(
    requireRegistrySelection = !this.isBackofficeProfessional(),
  ): FormGroup {
    const formGroup = this.fb.group({
      id: [null], // For edit mode
      subResponsibleId: [null], // From Registro Sub-Responsabili
      businessName: ['', Validators.required],
      companyType: [''],
      headquarters: ['', Validators.required], // headquarters in BE
      email: ['', Validators.email], // Non obbligatorio, ma validato se presente
      dataProcessingLocation: ['', Validators.required], // UE or EXTRA_UE
      dataProcessingCountry: [null], // Condizionale: obbligatorio quando dataProcessingLocation è valorizzato (UE o EXTRA_UE)
      nonEuSafeguard: [null], // Condizionale: obbligatorio solo se dataProcessingLocation=EXTRA_UE
      dataTypes: [[], Validators.required],
      dataTypesNotes: [''], // Note libere sui dati trattati
      delegatedProcessesDescription: ['', Validators.required],
      notes: [''],
      status: ['ACTIVE'],
    });

    if (requireRegistrySelection) {
      formGroup.get('subResponsibleId')?.setValidators([Validators.required]);
    }
    formGroup.get('subResponsibleId')?.updateValueAndValidity({ emitEvent: false });

    // Validazione condizionale per paese e garanzie
    formGroup.get('dataProcessingLocation')?.valueChanges.subscribe((value) => {
      const countryControl = formGroup.get('dataProcessingCountry');
      const safeguardControl = formGroup.get('nonEuSafeguard');

      if (value === 'UE' || value === 'EXTRA_UE') {
        // Paese sempre obbligatorio quando location è valorizzata
        countryControl?.setValidators([Validators.required]);
      } else {
        countryControl?.clearValidators();
        countryControl?.setValue(null);
      }

      if (value === 'EXTRA_UE') {
        // Garanzie obbligatorie solo per Extra UE
        safeguardControl?.setValidators([Validators.required]);
      } else {
        safeguardControl?.clearValidators();
        safeguardControl?.setValue(null);
      }

      countryControl?.updateValueAndValidity();
      safeguardControl?.updateValueAndValidity();
    });

    return formGroup;
  }

  // ==================== TITOLARE DEL TRATTAMENTO - FormArray Methods ====================

  private getDefaultControllerOwnershipType(): string | null {
    return (
      this.tipoTitolaritaOptions().find(
        (option) => option.valueDescription.trim().toLowerCase() === 'autonomo',
      )?.valueKey || null
    );
  }

  private getInvolvedEntityCodes(value: any): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((entity) => entity?.entityCode)
      .filter((entityCode): entityCode is string => !!entityCode);
  }

  private reconcileControllersWithInvolvedEntities(): void {
    const existingControllers = new Map(
      this.controllersArray.value
        .filter((controller: any) => controller?.controllerEntity)
        .map((controller: any) => [controller.controllerEntity, controller]),
    );

    this.controllersArray.clear({ emitEvent: false });

    this.getInvolvedEntityCodes(this.involvedEntitiesArray.value).forEach((entityCode) => {
      const existingController = existingControllers.get(entityCode);
      const entity = this.entityOptions().find((option) => option.entityCode === entityCode);
      const controllerGroup = this.createControllerFormGroup(entityCode);

      controllerGroup.patchValue(
        existingController || {
          controllerEntity: entityCode,
          controllerDpoEmail: entity?.dpoEmail || '',
          controllerOwnershipType: this.getDefaultControllerOwnershipType() || '',
        },
        { emitEvent: false },
      );
      this.controllersArray.push(controllerGroup, { emitEvent: false });
    });

    this.controllersArray.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Create FormGroup for a single controller entity
   */
  private createControllerFormGroup(entity?: string): FormGroup {
    return this.fb.group({
      id: [null],
      controllerEntity: [entity || '', Validators.required],
      controllerDpoEmail: ['', [Validators.required, Validators.email]],
      controllerOwnershipType: ['', Validators.required],
    });
  }

  private createInvolvedEntityFormGroup(entity?: Partial<ContractInvolvedEntity>): FormGroup {
    return this.fb.group({
      entityCode: [entity?.entityCode || '', Validators.required],
      signatoryName: [
        entity?.signatoryName || '',
        [Validators.required, NoSpecialCharsValidator('name')],
      ],
      signatoryRole: [
        entity?.signatoryRole || '',
        [Validators.required, NoSpecialCharsValidator('free-text')],
      ],
    });
  }

  get involvedEntitiesArray(): FormArray {
    return this.step1Form.get('involvedEntities') as FormArray;
  }

  getAvailableInvolvedEntityOptions(currentIndex: number): AxaEntity[] {
    const selectedEntities = this.involvedEntitiesArray.controls
      .map((control, index) => {
        if (index === currentIndex) return null;
        return control.get('entityCode')?.value;
      })
      .filter((value) => value !== null && value !== '');

    return this.entityOptions().filter((option) => !selectedEntities.includes(option.entityCode));
  }

  canAddMoreInvolvedEntities(): boolean {
    return this.involvedEntitiesArray.length < this.entityOptions().length;
  }

  addInvolvedEntity(): void {
    if (this.canAddMoreInvolvedEntities()) {
      this.involvedEntitiesArray.push(this.createInvolvedEntityFormGroup());
    }
  }

  removeInvolvedEntity(index: number): void {
    if (this.involvedEntitiesArray.length > 1) {
      this.involvedEntitiesArray.removeAt(index);
    }
  }

  getEntityLabel(entityCode: string | null | undefined): string {
    if (!entityCode) {
      return 'Entity selezionata';
    }

    return this.entityOptions().find((option) => option.entityCode === entityCode)?.entityDescription || entityCode;
  }

  onInvolvedEntityChange(index: number): void {
    const entityGroup = this.involvedEntitiesArray.at(index) as FormGroup;
    const entityCode = entityGroup.get('entityCode')?.value as string | null;
    const selectedEntity = this.entityOptions().find((entity) => entity.entityCode === entityCode);

    entityGroup.patchValue(
      {
        signatoryName: selectedEntity?.signatoryName || '',
        signatoryRole: selectedEntity?.signatoryRole || '',
      },
      { emitEvent: false },
    );
  }

  /**
   * Getter for controllers FormArray
   */
  get controllersArray(): FormArray {
    return this.step2Form.get('controllers') as FormArray;
  }

  /**
   * Get available controller entities from the entities selected in the contract.
   * Excludes entities already selected by other controllers.
   */
  getAvailableControllerEntityOptions(currentIndex: number): AxaEntity[] {
    const involvedEntityCodes = new Set(
      this.involvedEntitiesArray.controls
        .map((control) => control.get('entityCode')?.value)
        .filter((value): value is string => !!value),
    );
    const allOptions = involvedEntityCodes.size > 0
      ? this.entityOptions().filter((option) => involvedEntityCodes.has(option.entityCode))
      : this.entityOptions();
    const selectedEntities = this.controllersArray.controls
      .map((control, index) => {
        // Exclude current index to allow keeping the same selection
        if (index === currentIndex) return null;
        return control.get('controllerEntity')?.value;
      })
      .filter((value) => value !== null && value !== '');

    return allOptions.filter((option) => !selectedEntities.includes(option.entityCode));
  }

  /**
   * Check if more controllers can be added
   * Limited by the number of available entities
   */
  canAddMoreControllers(): boolean {
    const totalOptions = this.entityOptions().length;
    const currentCount = this.controllersArray.length;
    return currentCount < totalOptions;
  }

  /**
   * Add a new controller to the FormArray
   */
  addController(): void {
    if (this.canAddMoreControllers()) {
      this.controllersArray.push(this.createControllerFormGroup());
      this.applyDefaultControllerOwnershipTypeForCreation();
    }
  }

  /**
   * Remove a controller from the FormArray
   */
  removeController(index: number): void {
    this.controllersArray.removeAt(index);
  }

  onControllerEntityChange(index: number): void {
    const controllerGroup = this.controllersArray.at(index) as FormGroup;
    const entityCode = controllerGroup.get('controllerEntity')?.value as string | null;
    const selectedEntity = this.entityOptions().find((entity) => entity.entityCode === entityCode);

    controllerGroup.patchValue(
      { controllerDpoEmail: selectedEntity?.dpoEmail || '' },
      { emitEvent: false },
    );
  }

  // ==================== SUB-RESPONSABILI - FormArray Methods ====================

  // Metodo per aggiungere un sub-responsabile all'array
  addSubResponsible(): void {
    const subResponsiblesArray = this.step2Form.get('subResponsibles') as FormArray;
    subResponsiblesArray.push(this.createSubResponsibleFormGroup());
  }

  // Metodo per rimuovere un sub-responsabile dall'array
  removeSubResponsible(index: number): void {
    const subResponsiblesArray = this.step2Form.get('subResponsibles') as FormArray;
    const subResp = subResponsiblesArray.at(index);
    const businessName = subResp.get('businessName')?.value || 'questo sub-responsabile';

    this.confirmDialogService.confirm({
      message: `Vuoi rimuovere <strong>${escapeHtml(businessName)}</strong> dall'elenco dei Sub-Responsabili?`,
      header: 'Conferma rimozione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sì, rimuovi',
      rejectLabel: 'Annulla',
      accept: () => {
        subResponsiblesArray.removeAt(index);
        this.messageService.add({
          severity: 'info',
          summary: 'Sub-Responsabile rimosso',
          detail: `${businessName} è stato rimosso dall'elenco`,
          life: 3000,
        });
      },
    });
  }

  // Getter per accedere all'array di sub-responsabili nel template
  get subResponsiblesArray(): FormArray {
    return this.step2Form.get('subResponsibles') as FormArray;
  }

  /**
   * Recupera ragione sociale e descrizione tipo società del fornitore selezionato per l'header.
   * Se l'entità selezionata non contiene i dati necessari (es. fallback in edit mode), li carica da API.
   */
  private loadSelectedSupplierHeader(supplierId: string, selected: any): void {
    if (!supplierId) {
      this.selectedSupplierHeader.set(null);
      return;
    }

    const supplier$ =
      selected?.supplierTypeId && selected?.supplierName
        ? of(selected)
        : this.supplierRegistryService
            .getSupplier(supplierId)
            .pipe(map((response: any) => response?.data ?? response));

    supplier$
      .pipe(
        switchMap((supplier: any) =>
          this.formDropdownService.decodeSupplierType(supplier?.supplierTypeId).pipe(
            catchError(() => of('')),
            map((typeDescription) => {
              const name = supplier?.supplierName?.trim() || '';
              const companyTypeSuffix = typeDescription?.match(/\(([^()]+)\)\s*$/)?.[1]?.trim() || '';
              return {
                id: supplierId,
                displayName: name ? [name, companyTypeSuffix].filter(Boolean).join(' ') : '',
              };
            }),
          ),
        ),
        catchError((err) => {
          console.error('Errore caricamento dati fornitore per intestazione:', err);
          return of(null);
        }),
      )
      .subscribe((header) => {
        if (header && this.selectedSupplierId() === supplierId) {
          this.selectedSupplierHeader.set(header);
          const recipient = this.step1Form.get('invoiceRecipientEmail');
          if (
            this.contractId() === null &&
            !this.isSelectedEntityProfessional() &&
            !recipient?.value?.trim() &&
            header.displayName
          ) {
            recipient?.setValue(header.displayName);
          }
        }
      });
  }

  // Step 0: Selezione entità (Fornitore/Fiduciario)
  /**
   * Pre-selects a professional by ID without showing a confirmation dialog.
   * Used when navigating from review-professional with a professionalId queryParam.
   */
  private applySupplierPreselection(professionalId: string): void {
    const found = this.suppliers().find(s => s.id === professionalId);
    if (!found) {
      console.warn('⚠️ Pre-selected professional not found in suppliers list:', professionalId);
      return;
    }

    this.selectedSupplier.set(found);
    this.step0Form.patchValue({ supplierId: found.id });
    this.applyDefaultInvolvedStructuresForCreation();
    this.applyBackofficeProfessionalStep1ValidationRules();

    // Arrivando dal flusso fiduciario (review-professional), il tipo contratto è sempre "CONTRACT":
    // pre-compiliamo il campo (senza sovrascrivere un'eventuale scelta già presente).
    if (!this.step1Form.get('contractType')?.value) {
      this.step1Form.patchValue({ contractType: 'CONTRACT' }, { emitEvent: false });
    }

    if (this.isBackofficeProfessional()) {
      this.step1Form.patchValue({ gdprDataProcessing: true }, { emitEvent: false });
    }

    this.prefillProfessionalSignatory(found.id);

    this.wizardStepService.markStepCompleted(0, true);
    this.currentStep.set(1);
    this.maxStepReached.update((max) => Math.max(max, 1));
    this.originalFormValues.set(0, JSON.parse(JSON.stringify(this.step0Form.value)));

    // Pre-compila i campi dell'Atto di Nomina dal questionario VRF del fiduciario (solo se non esiste già un Atto salvato)
    if (this.isBackofficeProfessional() && found.id && !this.attoDiNominaExists()) {
      this.prefillAttoDiNominaFromVRF(found.id);
    }

    setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 200);
  }

  selectSupplier(supplier: any): void {
    // Flusso fiduciario: prima di procedere blocca la selezione se il fiduciario ha già un
    // contratto in fase di definizione (bozza/da attivare). Un fiduciario può avere un solo
    // contratto in definizione alla volta (mirror del vincolo lato BE, che altrimenti
    // restituisce "duplicate resource" solo al salvataggio bozza, con UX poco chiara).
    if (this.isBackofficeProfessional() && supplier?.id && this.contractId() === null) {
      this.contractRegistryService.getContractsByProfessional(supplier.id).subscribe({
        next: (response: any) => {
          const raw = Array.isArray(response?.data?.content)
            ? response.data.content
            : Array.isArray(response?.data)
              ? response.data
              : [];
          const existing = raw.find(
            (c: any) => c?.status === 'DRAFT' || c?.status === 'GENERATED',
          );
          if (existing) {
            this.showExistingContractBlock(supplier, existing);
            return;
          }
          this.confirmSupplierSelection(supplier);
        },
        error: () => {
          // Se il controllo fallisce proseguiamo: il BE resta l'ultima linea di difesa.
          this.confirmSupplierSelection(supplier);
        },
      });
      return;
    }

    this.confirmSupplierSelection(supplier);
  }

  /**
   * Blocca la selezione di un fiduciario che ha già un contratto in definizione (bozza/da attivare)
   * con un messaggio parlante: l'utente può aprire il contratto esistente oppure chiudere e
   * selezionare un altro fiduciario.
   */
  private showExistingContractBlock(supplier: any, existingContract: any): void {
    const entityLabel = this.selectionEntityLabel();
    const entityName =
      supplier.supplierName ||
      supplier.displayName ||
      supplier.businessName ||
      `${entityLabel} selezionato`;
    const existingId = existingContract?.id || existingContract?.contractId || '';
    const isDraft = existingContract?.status === 'DRAFT';
    const stateLabel = isDraft ? 'in bozza' : 'generato e da attivare';

    this.confirmDialogService.confirm({
      header: 'Contratto già presente',
      message:
        `Il fiduciario <strong>${escapeHtml(entityName)}</strong> ha già un contratto ${stateLabel}` +
        (existingId ? ` (<strong>${escapeHtml(existingId)}</strong>)` : '') +
        `.<br>Non è possibile crearne uno nuovo finché quello esistente non viene completato o eliminato.` +
        `<br><br>Vai al contratto esistente oppure chiudi e seleziona un altro fiduciario.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Vai al contratto',
      rejectLabel: 'Scegli un altro fiduciario',
      accept: () => {
        if (!existingId) return;
        const route = isDraft
          ? ['/axa/professional-contract-registration-wizard', existingId]
          : ['/axa/professional-contracts-registry', existingId];
        this.router.navigate(route);
      },
      reject: () => {
        // L'utente chiude: potrà selezionare un altro fiduciario.
      },
    });
  }

  private confirmSupplierSelection(supplier: any): void {
    const entityLabel = this.selectionEntityLabel();
    const entityName =
      supplier.supplierName ||
      supplier.displayName ||
      supplier.businessName ||
      `${entityLabel} selezionato`;

    this.confirmationService.confirm({
      header: 'Conferma',
      message: `Vuoi procedere con ${entityLabel.toLowerCase()} <strong>${escapeHtml(entityName)}</strong>?`,
      icon: 'pi pi-question-circle',
      acceptLabel: 'Sì, procedi',
      rejectLabel: 'Annulla',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        this.selectedSupplier.set(supplier);
        this.step0Form.patchValue({ supplierId: supplier.id });
        if (this.contractId() === null && !this.isSelectedEntityProfessional()) {
          for (const field of ['buyer', 'paymentAuthorizer', 'controlsOwner']) {
            const control = this.step1Form.get(field);
            if (!control?.value?.trim()) {
              control?.setValue(supplier.axaBuyer ?? '');
            }
          }
        }
        this.applyDefaultInvolvedStructuresForCreation();

        // Apply professional-specific validators if needed
        this.applyBackofficeProfessionalStep1ValidationRules();

        // Professional contracts always require GDPR data processing (set gdprDataProcessing to true)
        if (this.isBackofficeProfessional()) {
          this.step1Form.patchValue({ gdprDataProcessing: true }, { emitEvent: false });
        }

        this.prefillProfessionalSignatory(supplier.id);

        // Passa allo step 1 senza salvare
        this.wizardStepService.markStepCompleted(0, true);
        this.currentStep.set(1);
        this.maxStepReached.update((max) => Math.max(max, 1));

        // Salva i valori come originali per step 0
        this.originalFormValues.set(0, JSON.parse(JSON.stringify(this.step0Form.value)));

        // Pre-compila i campi dell'Atto di Nomina dal questionario VRF del fiduciario (solo se non esiste già un Atto salvato)
        if (this.isBackofficeProfessional() && supplier.id && !this.attoDiNominaExists()) {
          this.prefillAttoDiNominaFromVRF(supplier.id);
        }

        this.messageService.add({
          severity: 'success',
          summary: `${entityLabel} selezionato`,
          detail: `Procedi con i dati del contratto per ${entityName}`,
          life: 3000,
        });

        setTimeout(() => {
          window.scrollTo(0, 0);
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        }, 200);
      },
      reject: () => {
        // Utente ha annullato la selezione
      },
    });
  }

  private prefillProfessionalSignatory(professionalId: string): void {
    if (!professionalId || this.contractId() !== null || !this.isSelectedEntityProfessional()) {
      return;
    }

    const signatoryControl = this.step1Form.get('professionalSignatory');
    const signatoryRoleControl = this.step1Form.get('professionalSignatoryRole');
    if (!signatoryRoleControl?.value?.trim()) {
      signatoryRoleControl?.setValue('Legale Rappresentante', { emitEvent: false });
    }

    if (signatoryControl?.value?.trim()) {
      return;
    }

    this.professionalsService
      .getProfessionalGeneralData(professionalId)
      .pipe(catchError(() => of(null)))
      .subscribe((generalData) => {
      const data = generalData?.data ?? generalData;
      const firstName =
        data?.legalRepresentative?.firstname || data?.firstname || data?.companyData?.firstname || '';
      const lastName =
        data?.legalRepresentative?.lastname || data?.lastname || data?.companyData?.lastname || '';
      const professionalSignatory = `${lastName} ${firstName}`.trim();

      if (professionalSignatory && !signatoryControl?.value?.trim()) {
        signatoryControl?.setValue(professionalSignatory, { emitEvent: false });
      }
    });
  }

  saveStep0AndContinue(): void {
    if (this.step0Form.valid) {
      this.wizardStepService.markStepCompleted(0, true);
      this.currentStep.set(1);

      this.messageService.add({
        severity: 'success',
        summary: `${this.selectionEntityLabel()} selezionato`,
        detail: 'Procedi con i dati del contratto',
        life: 3000,
      });

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      this.markFormGroupTouched(this.step0Form);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Seleziona un fornitore',
        life: 3000,
      });
    }
  }

  // Step 2: Dialog per selezione Responsabile
  openResponsibleDialog(): void {
    // Ricarica la lista responsabili ogni volta che si apre il dialog
    this.loadResponsibles();
    this.showResponsibleDialog.set(true);
  }

  closeResponsibleDialog(): void {
    this.showResponsibleDialog.set(false);
  }

  selectResponsible(responsible: any): void {
    const responsibleDisplayName = this.getResponsibleDisplayName(responsible);

    this.confirmDialogService.confirm({
      message: `Vuoi selezionare <strong>${escapeHtml(responsibleDisplayName)}</strong> come Responsabile del Trattamento?`,
      header: 'Conferma selezione',
      icon: 'pi pi-question-circle',
      acceptLabel: 'Sì, seleziona',
      rejectLabel: 'Annulla',
      accept: () => {
        this.selectedResponsible.set({
          ...responsible,
          id: responsible.responsibleId || responsible.id,
        });

        // Auto-popola i campi del form con i dati del responsabile
        this.step2Form.patchValue({
          responsibleId: responsible.responsibleId || responsible.id,
          responsibleBusinessName: responsibleDisplayName,
          companyType: responsible.companyType || '',
          responsibleDpoName: '',
          headquarters: responsible.headquarters,
          responsibleDpoEmail: responsible.email,
        });

        this.closeResponsibleDialog();

        this.messageService.add({
          severity: 'success',
          summary: 'Responsabile selezionato',
          detail: `${responsibleDisplayName} è stato aggiunto come Responsabile del Trattamento`,
          life: 3000,
        });
      },
    });
  }

  clearResponsible(): void {
    this.confirmDialogService.confirm({
      message: 'Vuoi rimuovere il Responsabile del Trattamento selezionato?',
      header: 'Conferma rimozione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sì, rimuovi',
      rejectLabel: 'Annulla',
      accept: () => {
        this.selectedResponsible.set(null);
        this.step2Form.patchValue({
          responsibleId: '',
          responsibleBusinessName: '',
          companyType: '',
          responsibleDpoName: '',
          headquarters: '',
          responsibleEmail: '',
        });

        this.messageService.add({
          severity: 'info',
          summary: 'Responsabile rimosso',
          detail: 'La selezione è stata cancellata',
          life: 3000,
        });
      },
    });
  }

  syncResponsibleFromRegistry(): void {
    const currentResponsible = this.selectedResponsible();
    console.log('Syncing responsible from registry, current:', currentResponsible);

    const responsibleId = currentResponsible?.id || currentResponsible?.responsibleId;
    if (!responsibleId) {
      console.warn('No responsible ID found, cannot sync');
      this.messageService.add({
        severity: 'warn',
        summary: 'Impossibile sincronizzare',
        detail: 'Nessun responsabile selezionato',
        life: 3000,
      });
      return;
    }

    this.loadingResponsibles.set(true);
    console.log('Calling getResponsible with ID:', responsibleId);
    this.responsibleRegistryService.getResponsible(responsibleId.toString()).subscribe({
      next: (response) => {
        if (response?.data) {
          const updated = response.data as Responsible;
          const responsibleDisplayName = this.getResponsibleDisplayName(updated);

          this.selectedResponsible.set({
            id: updated.responsibleId || responsibleId,
            responsibleId: updated.responsibleId || responsibleId,
            businessName: responsibleDisplayName,
            companyType: updated.companyType,
            headquarters: updated.headquarters,
            email: updated.email,
          });

          this.step2Form.patchValue({
            responsibleBusinessName: responsibleDisplayName,
            companyType: updated.companyType || '',
            responsibleDpoName: '',
            headquarters: updated.headquarters,
            responsibleDpoEmail: updated.email,
          });

          this.messageService.add({
            severity: 'success',
            summary: 'Sincronizzazione completata',
            detail: 'I dati del responsabile sono stati aggiornati dal registro',
            life: 3000,
          });
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'Responsabile non trovato',
            detail: 'Il responsabile potrebbe essere stato rimosso dal registro',
            life: 3000,
          });
        }
        this.loadingResponsibles.set(false);
      },
      error: (err: any) => {
        console.error('Error syncing responsible:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore sincronizzazione',
          detail: 'Impossibile aggiornare i dati del responsabile',
          life: 3000,
        });
        this.loadingResponsibles.set(false);
      },
    });
  }

  syncSubResponsibleFromRegistry(index: number): void {
    const subResponsiblesArray = this.step2Form.get('subResponsibles') as FormArray;
    const subRespGroup = subResponsiblesArray.at(index) as FormGroup;
    const subResponsibleId = subRespGroup.get('subResponsibleId')?.value;

    if (subResponsibleId) {
      this.loadingSubResponsibles.set(true);
      this.subResponsibleRegistryService.getSubResponsible(subResponsibleId.toString()).subscribe({
        next: (response) => {
          if (response?.data) {
            const updated = response.data as SubResponsible;

            // Aggiorna solo i campi dal registro, mantiene i campi editabili
            subRespGroup.patchValue({
              businessName: updated.businessName,
              headquarters: updated.headquarters,
              email: updated.email,
            });

            this.messageService.add({
              severity: 'success',
              summary: 'Sincronizzazione completata',
              detail: `Sub-Responsabile #${index + 1} aggiornato dal registro`,
              life: 3000,
            });
          } else {
            this.messageService.add({
              severity: 'warn',
              summary: 'Sub-Responsabile non trovato',
              detail: 'Il sub-responsabile potrebbe essere stato rimosso dal registro',
              life: 3000,
            });
          }
          this.loadingSubResponsibles.set(false);
        },
        error: (err: any) => {
          console.error('Error syncing sub-responsible:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore sincronizzazione',
            detail: 'Impossibile aggiornare i dati del sub-responsabile',
            life: 3000,
          });
          this.loadingSubResponsibles.set(false);
        },
      });
    }
  }

  // Gestione creazione nuovo responsabile usando il form component esistente
  openCreateResponsibleDialog(): void {
    // Chiudi dialog selezione
    this.showResponsibleDialog.set(false);

    // Apri ResponsibleFormComponent come dialog
    this.responsibleDialogRef = this.dialogService.open(ResponsibleFormComponent, {
      header: 'Crea Nuovo Responsabile',
      width: '90vw',
      modal: true,
      closable: true,
      styleClass: 'responsible-creation-dialog',
    });

    // Listener per quando il dialog viene chiuso
    if (this.responsibleDialogRef) {
      this.responsibleDialogRef.onClose.subscribe((createdResponsible: Responsible | null) => {
        if (createdResponsible && createdResponsible.responsibleId) {
          const responsibleDisplayName = this.getResponsibleDisplayName(createdResponsible);

          // Il responsabile è stato creato con successo
          const newResponsible = {
            id: createdResponsible.responsibleId,
            businessName: responsibleDisplayName,
            companyType: createdResponsible.companyType,
            headquarters: createdResponsible.headquarters,
            email: createdResponsible.email,
          };

          // Auto-seleziona il responsabile creato
          this.selectedResponsible.set(newResponsible);

          // Auto-popola il form
          this.step2Form.patchValue({
            responsibleId: newResponsible.id,
            responsibleBusinessName: responsibleDisplayName,
            companyType: newResponsible.companyType || '',
            responsibleDpoName: '',
            headquarters: newResponsible.headquarters,
            responsibleDpoEmail: newResponsible.email,
          });

          // Ricarica la lista responsabili
          this.loadResponsibles();

          this.messageService.add({
            severity: 'success',
            summary: 'Responsabile creato',
            detail: `${responsibleDisplayName} è stato creato e selezionato`,
            life: 3000,
          });
        } else {
          // Dialog chiuso senza salvare, riapri dialog selezione
          this.showResponsibleDialog.set(true);
        }
      });
    }
  }

  private getResponsibleDisplayName(responsible: any): string {
    return this.responsibleRegistryService.formatResponsibleBusinessName(responsible);
  }

  decodeCompanyType(companyType: string | null | undefined): string {
    if (!companyType) {
      return '';
    }

    const localMatch = this.companyTypeOptions().find((option) => option.valueKey === companyType);
    if (localMatch) {
      return localMatch.valueDescription;
    }

    return this.formDropdownService.decodeSupplierTypeSync(companyType);
  }

  // ==================== SUB-RESPONSABILI DIALOG ====================

  /**
   * Apre dialog per selezionare sub-responsabile dal registro
   */
  openSubResponsibleSelectionDialog(): void {
    if (this.isBackofficeProfessional()) {
      this.addSubResponsible();
      return;
    }

    // Reset dell'indice - stiamo aggiungendo un nuovo sub-responsabile
    this.currentSubResponsibleIndex = null;
    // Ricarica la lista sub-responsabili ogni volta che si apre il dialog
    this.loadSubResponsibles();
    this.showSubResponsibleDialog.set(true);
  }

  /**
   * Apre dialog per cambiare un sub-responsabile esistente
   */
  openSubResponsibleChangeDialog(index: number): void {
    // Salva l'indice del sub-responsabile da sostituire
    this.currentSubResponsibleIndex = index;
    // Ricarica la lista sub-responsabili ogni volta che si apre il dialog
    this.loadSubResponsibles();
    this.showSubResponsibleDialog.set(true);
  }

  /**
   * Gestione selezione sub-responsabile dalla tabella
   */
  onSubResponsibleSelect(subResponsible: any): void {
    const isReplacing = this.currentSubResponsibleIndex !== null;
    const actionText = isReplacing ? 'sostituire' : 'aggiungere';
    const actionLabel = isReplacing ? 'Sì, sostituisci' : 'Sì, aggiungi';

    this.confirmDialogService.confirm({
      message: `Vuoi ${actionText} <strong>${escapeHtml(subResponsible.businessName)}</strong> ${isReplacing ? 'al' : "all'elenco dei"} Sub-Responsabil${isReplacing ? 'e' : 'i'}?`,
      header: 'Conferma selezione',
      icon: 'pi pi-question-circle',
      acceptLabel: actionLabel,
      rejectLabel: 'Annulla',
      accept: () => {
        const subResponsiblesArray = this.step2Form.get('subResponsibles') as FormArray;

        if (isReplacing && this.currentSubResponsibleIndex !== null) {
          // Sostituisci sub-responsabile esistente
          const existingSubResponsible = subResponsiblesArray.at(this.currentSubResponsibleIndex);

          // Mantieni i dati editabili dall'utente se presenti
          const dataProcessingLocation =
            existingSubResponsible.get('dataProcessingLocation')?.value;
          const dataTypes = existingSubResponsible.get('dataTypes')?.value;
          const dataProcessingCountry = existingSubResponsible.get('dataProcessingCountry')?.value;
          const nonEuSafeguard = existingSubResponsible.get('nonEuSafeguard')?.value;
          const delegatedProcessesDescription = existingSubResponsible.get(
            'delegatedProcessesDescription',
          )?.value;

          existingSubResponsible.patchValue({
            subResponsibleId: subResponsible.id,
            businessName: subResponsible.businessName,
            companyType: subResponsible.companyType || '',
            headquarters: subResponsible.headquarters,
            email: subResponsible.email,
            // Mantieni i campi editabili esistenti
            dataProcessingLocation: dataProcessingLocation || '',
            dataTypes: dataTypes || [],
            dataProcessingCountry: dataProcessingCountry || null,
            nonEuSafeguard: nonEuSafeguard || null,
            delegatedProcessesDescription: delegatedProcessesDescription || '',
          });

          this.messageService.add({
            severity: 'success',
            summary: 'Sub-Responsabile sostituito',
            detail: `${subResponsible.businessName} ha sostituito il precedente sub-responsabile`,
            life: 3000,
          });
        } else {
          // Aggiungi nuovo sub-responsabile al FormArray
          const newSubResponsible = this.createSubResponsibleFormGroup();

          // Popola SOLO i campi dal registro - gli altri devono essere compilati dall'utente
          newSubResponsible.patchValue({
            subResponsibleId: subResponsible.id,
            businessName: subResponsible.businessName,
            companyType: subResponsible.companyType || '',
            headquarters: subResponsible.headquarters,
            email: subResponsible.email,
            // dataProcessingLocation, dataTypes, delegatedProcessesDescription devono essere compilati dall'utente
          });

          subResponsiblesArray.push(newSubResponsible);

          this.messageService.add({
            severity: 'success',
            summary: 'Sub-Responsabile aggiunto',
            detail: `${subResponsible.businessName} è stato aggiunto. Compila i campi richiesti.`,
            life: 3000,
          });
        }

        this.showSubResponsibleDialog.set(false);
        this.currentSubResponsibleIndex = null; // Reset indice
      },
    });
  }

  /**
   * Apre SubResponsibleFormComponent come dialog per creazione
   */
  openCreateSubResponsibleDialog(): void {
    // Chiudi dialog selezione
    this.showSubResponsibleDialog.set(false);

    // Apri SubResponsibleFormComponent come dialog
    this.subResponsibleDialogRef = this.dialogService.open(SubResponsibleFormComponent, {
      header: 'Crea Nuovo Sub-Responsabile',
      width: '90vw',
      modal: true,
      closable: true,
      styleClass: 'sub-responsible-creation-dialog',
    });

    // Listener per quando il dialog viene chiuso
    if (this.subResponsibleDialogRef) {
      this.subResponsibleDialogRef.onClose.subscribe((createdSubResponsible: any | null) => {
        if (createdSubResponsible && createdSubResponsible.subResponsibleId) {
          // Il sub-responsabile è stato creato con successo
          const subResponsiblesArray = this.step2Form.get('subResponsibles') as FormArray;
          const newSubResponsible = this.createSubResponsibleFormGroup();

          // Popola con i dati ricevuti dal dialog
          newSubResponsible.patchValue({
            subResponsibleId: createdSubResponsible.subResponsibleId,
            businessName: createdSubResponsible.businessName,
            companyType: createdSubResponsible.companyType || '',
            headquarters: createdSubResponsible.headquarters,
            email: createdSubResponsible.email,
            dataProcessingLocation: '',
            dataTypes: [],
            delegatedProcessesDescription: '',
          });

          subResponsiblesArray.push(newSubResponsible);

          // Ricarica la lista sub-responsabili
          this.loadSubResponsibles();

          this.messageService.add({
            severity: 'success',
            summary: 'Sub-Responsabile creato',
            detail: `${createdSubResponsible.businessName} è stato creato e aggiunto all'elenco`,
            life: 3000,
          });
        } else {
          // Dialog chiuso senza salvare, riapri dialog selezione
          this.showSubResponsibleDialog.set(true);
        }
      });
    }
  }

  /**
   * Chiude dialog selezione sub-responsabile
   */
  closeSubResponsibleDialog(): void {
    this.showSubResponsibleDialog.set(false);
    this.currentSubResponsibleIndex = null; // Reset indice
  }

  // Step 3: Dialog per selezione Template Contratto
  openTemplateContrattoDialog(): void {
    this.showTemplateContrattoDialog.set(true);
  }

  closeTemplateContrattoDialog(): void {
    this.showTemplateContrattoDialog.set(false);
  }

  selectTemplateContratto(template: any): void {
    // template from table has {id, name}, convert to {label, value}
    const templateData = {
      label: template.name,
      value: template.id,
    };

    this.confirmDialogService.confirm({
      message: `Vuoi selezionare <strong>${escapeHtml(templateData.label)}</strong> come Template Contratto?`,
      header: 'Conferma selezione',
      icon: 'pi pi-question-circle',
      acceptLabel: 'Sì, seleziona',
      rejectLabel: 'Annulla',
      accept: () => {
        this.selectedTemplateContratto.set(templateData);

        // Auto-popola il campo del form
        this.step3Form.patchValue({
          templateContratto: templateData.value,
        });

        this.closeTemplateContrattoDialog();

        this.messageService.add({
          severity: 'success',
          summary: 'Template selezionato',
          detail: `${templateData.label} è stato selezionato come Template Contratto`,
          life: 3000,
        });
      },
    });
  }

  clearTemplateContratto(): void {
    this.confirmDialogService.confirm({
      message: 'Vuoi rimuovere il Template Contratto selezionato?',
      header: 'Conferma rimozione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sì, rimuovi',
      rejectLabel: 'Annulla',
      accept: () => {
        this.selectedTemplateContratto.set(null);
        this.step3Form.patchValue({
          templateContratto: null,
        });

        this.messageService.add({
          severity: 'info',
          summary: 'Template rimosso',
          detail: 'Il Template Contratto è stato rimosso',
          life: 3000,
        });
      },
    });
  }

  // Step 3: Dialog per selezione Template Atto di Nomina
  openTemplateAttoNominaDialog(): void {
    this.showTemplateAttoNominaDialog.set(true);
  }

  closeTemplateAttoNominaDialog(): void {
    this.showTemplateAttoNominaDialog.set(false);
  }

  selectTemplateAttoNomina(template: any): void {
    // template from table has {id, name}, convert to {label, value}
    const templateData = {
      label: template.name,
      value: template.id,
    };

    this.confirmDialogService.confirm({
      message: `Vuoi selezionare <strong>${escapeHtml(templateData.label)}</strong> come Template Atto di Nomina?`,
      header: 'Conferma selezione',
      icon: 'pi pi-question-circle',
      acceptLabel: 'Sì, seleziona',
      rejectLabel: 'Annulla',
      accept: () => {
        this.selectedTemplateAttoNomina.set(templateData);

        // Auto-popola il campo del form
        this.step3Form.patchValue({
          templateAttoNomina: templateData.value,
        });

        this.closeTemplateAttoNominaDialog();

        this.messageService.add({
          severity: 'success',
          summary: 'Template selezionato',
          detail: `${templateData.label} è stato selezionato come Template Atto di Nomina`,
          life: 3000,
        });
      },
    });
  }

  clearTemplateAttoNomina(): void {
    this.confirmDialogService.confirm({
      message: 'Vuoi rimuovere il Template Atto di Nomina selezionato?',
      header: 'Conferma rimozione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sì, rimuovi',
      rejectLabel: 'Annulla',
      accept: () => {
        this.selectedTemplateAttoNomina.set(null);
        this.step3Form.patchValue({
          templateAttoNomina: null,
        });

        this.messageService.add({
          severity: 'info',
          summary: 'Template rimosso',
          detail: 'Il Template Atto di Nomina è stato rimosso',
          life: 3000,
        });
      },
    });
  }

  // Step 1: Contratto
  saveStep1AndContinue(): void {
    // Check for special characters errors FIRST
    const specialCharsFields = this.hasSpecialCharsErrors(this.step1Form);
    if (specialCharsFields.length > 0) {
      this.step1Form.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Caratteri non permessi',
        detail: `I seguenti campi contengono caratteri speciali non permessi (< > " ' & / \\): ${specialCharsFields.join(', ')}. Correggili prima di proseguire.`,
        life: 6000,
      });
      return;
    }

    if (this.step1Form.valid) {
      // Check if GDPR changed from true to false with existing atto di nomina
      const originalValues = this.originalFormValues.get(1);
      const originalGdpr = originalValues?.gdprDataProcessing;
      const currentGdpr = this.step1Form.get('gdprDataProcessing')?.value;

      if (originalGdpr === true && currentGdpr === false && this.hasAttoDiNominaData()) {
        // Show warning dialog before saving
        this.confirmationService.confirm({
          message:
            "Disabilitando la gestione dati GDPR, tutti i dati inseriti nell'Atto di Nomina verranno persi. Continuare?",
          header: 'Attenzione',
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'Sì, continua',
          rejectLabel: 'Annulla',
          accept: () => {
            // Proceed with save and then delete atto di nomina
            this.saveStep1WithDeleteAttoDiNomina();
          },
        });
        return;
      }

      // Normal save flow
      // Build and save contract via API
      this.saveContractToBackend('FILLED', () => {
        // Step 1 completion is now handled inside saveContractToBackend

        // Check if GDPR data processing is enabled
        const gdprDataProcessing = this.step1Form.get('gdprDataProcessing')?.value;

        // Update Atto di Nomina step visibility based on GDPR setting (only after successful save)
        this.updateStepVisibility();

        // Determine next step based on context:
        // - Professional: Always go to Tariffe (step 2)
        // - Supplier: If GDPR enabled go to Atto di Nomina (step 2), else go to Gestione Documenti (step 3)
        let nextStep: number;
        if (this.isBackofficeProfessional()) {
          nextStep = this.getTariffeStepId(); // Always go to Tariffe for professional
        } else {
          nextStep = gdprDataProcessing ? this.getAttoDiNominaStepId() : this.getGestioneDocumentiStepId();
        }

        this.currentStep.set(nextStep);
        this.maxStepReached.update((max) => Math.max(max, nextStep));

        // If going to Atto di Nomina step, pre-fill processDescription with contractSubject
        if (nextStep === this.getAttoDiNominaStepId()) {
          const processDescValue = this.step2Form.get('processDescription')?.value;
          const contractSubjectValue = this.step1Form.get('contractSubject')?.value;
          if ((!processDescValue || processDescValue.trim() === '') && contractSubjectValue) {
            this.step2Form.patchValue({ processDescription: contractSubjectValue });
          }
        }

        // Salva i nuovi valori come originali per step 1
        this.originalFormValues.set(1, JSON.parse(JSON.stringify(this.step1Form.value)));
        this.step1Form.markAsPristine();

        this.messageService.add({
          severity: 'success',
          summary: 'Dati salvati',
          detail: 'Dati del contratto salvati con successo',
          life: 3000,
        });

        setTimeout(() => {
          window.scrollTo(0, 0);
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        }, 200);
      });
    } else {
      this.markFormGroupTouched(this.step1Form);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Compila tutti i campi obbligatori',
        life: 3000,
      });
    }
  }

  private saveStep1WithDeleteAttoDiNomina(): void {
    // Save contract and then delete atto di nomina
    this.saveContractToBackend('FILLED', () => {
      // Step 1 completion is now handled inside saveContractToBackend

      // Delete atto di nomina after successful save
      if (this.contractId()) {
        this.contractRegistryService.deleteAttoDiNomina(this.contractId()!).subscribe({
          next: () => {
            console.log('Atto di Nomina deleted successfully after save');
            // Reset stato atto di nomina
            this.attoDiNominaExists.set(false);
            this.attoDiNominaStatus.set(null);
            this.wizardStepService.markStepCompleted(2, false);
            // Reset form step 2
            this.step2Form.reset();

            // Update step visibility after DELETE
            this.updateStepVisibility();

            // Navigate to step 3 (skip step 2 since GDPR is now false)
            const nextStep = 3;
            this.currentStep.set(nextStep);
            this.maxStepReached.update((max) => Math.max(max, nextStep));

            // Save new values as original for step 1
            this.originalFormValues.set(1, JSON.parse(JSON.stringify(this.step1Form.value)));
            this.step1Form.markAsPristine();

            this.messageService.add({
              severity: 'success',
              summary: 'Dati salvati',
              detail: 'Dati del contratto salvati con successo. Atto di Nomina eliminato.',
              life: 3000,
            });

            setTimeout(() => {
              window.scrollTo(0, 0);
              document.documentElement.scrollTop = 0;
              document.body.scrollTop = 0;
            }, 200);
          },
          error: (err) => {
            console.error('Error deleting Atto di Nomina:', err);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: "Errore durante l'eliminazione dell'Atto di Nomina",
              life: 3000,
            });
          },
        });
      } else {
        // If no contractId (shouldn't happen), just update visibility
        this.updateStepVisibility();

        const nextStep = 3;
        this.currentStep.set(nextStep);
        this.maxStepReached.update((max) => Math.max(max, nextStep));

        this.originalFormValues.set(1, JSON.parse(JSON.stringify(this.step1Form.value)));
        this.step1Form.markAsPristine();

        this.messageService.add({
          severity: 'success',
          summary: 'Dati salvati',
          detail: 'Dati del contratto salvati con successo',
          life: 3000,
        });

        setTimeout(() => {
          window.scrollTo(0, 0);
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        }, 200);
      }
    }, false);
  }

  private saveDraftStep1WithDeleteAttoDiNomina(): void {
    this.isLoading.set(true);
    this.loadingMessage.set('Salvataggio bozza in corso...');

    const payload = this.buildContractPayload('DRAFT');

    const handleSuccessAndDelete = (response: any) => {
      if (!this.contractId()) {
        this.contractId.set(response.data);
        this.checklistService.setChecklistAvailability(true);
      }

      // Update contractSectionStatus signal and mark step 1 as not completed (DRAFT)
      this.contractSectionStatus.set('DRAFT');
      this.wizardStepService.markStepCompleted(1, false);

      // Delete atto di nomina after successful save
      if (this.contractId()) {
        this.contractRegistryService.deleteAttoDiNomina(this.contractId()!).subscribe({
          next: () => {
            console.log('Atto di Nomina deleted successfully after draft save');
            // Reset stato atto di nomina
            this.attoDiNominaExists.set(false);
            this.attoDiNominaStatus.set(null);
            this.wizardStepService.markStepCompleted(2, false);
            // Reset form step 2
            this.step2Form.reset();

            // Update step visibility after DELETE
            this.updateStepVisibility();

            // Save new values as original for step 1
            this.originalFormValues.set(1, JSON.parse(JSON.stringify(this.step1Form.value)));
            this.step1Form.markAsPristine();

            this.messageService.add({
              severity: 'success',
              summary: 'Bozza salvata',
              detail: 'La bozza è stata salvata con successo. Atto di Nomina eliminato.',
              life: 3000,
            });
            this.isLoading.set(false);
          },
          error: (err) => {
            console.error('Error deleting Atto di Nomina:', err);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: "Errore durante l'eliminazione dell'Atto di Nomina",
              life: 3000,
            });
            this.isLoading.set(false);
          },
        });
      }
    };

    const handleError = (err: any) => {
      this.isLoading.set(false);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Errore nel salvataggio della bozza',
      });
      console.error('Error saving draft:', err);
    };

    if (!this.contractId()) {
      // Prima volta: POST
      this.contractRegistryService.createContract(payload).subscribe({
        next: handleSuccessAndDelete,
        error: handleError,
      });
    } else {
      // Aggiornamento: PUT
      this.contractRegistryService.updateContract(this.contractId()!, payload).subscribe({
        next: handleSuccessAndDelete,
        error: handleError,
      });
    }
  }

  // Step 2: Atto di Nomina
  saveStep2AndContinue(): void {
    if (this.step2Form.valid) {
      // Verifica che esista un contractId (deve essere stato creato nello step 1)
      if (!this.contractId()) {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Contratto non trovato. Completare prima lo Step 1.',
          life: 3000,
        });
        return;
      }

      this.isLoading.set(true);
      this.loadingMessage.set('Salvataggio Atto di Nomina in corso...');

      // Salva solo l'Atto di Nomina usando l'endpoint specifico
      const attoDiNominaPayload = this.buildAttoDiNominaPayload('FILLED');

      // Log del payload completo per confronto con struttura BE
      console.log('===== ATTO DI NOMINA PAYLOAD (SAVE AND GO TO STEP 3) =====');
      console.log('Payload Object:', attoDiNominaPayload);
      console.log('Payload JSON:', JSON.stringify(attoDiNominaPayload, null, 2));
      console.log('==========================================================');

      // Use POST for new Atto di Nomina, PUT for updates
      const saveObservable = this.attoDiNominaExists()
        ? this.contractRegistryService.updateAttoDiNomina(this.contractId()!, attoDiNominaPayload)
        : this.contractRegistryService.createAttoDiNomina(this.contractId()!, attoDiNominaPayload);

      saveObservable.subscribe({
        next: (response) => {
          console.log('Atto di Nomina saved:', response);
          this.attoDiNominaExists.set(true); // After first save, it exists
          this.attoDiNominaStatus.set('FILLED'); // Set status to FILLED
          
          // Mark the correct Atto di Nomina step as completed (step 2 for supplier, step 3 for professional)
          const attoDiNominaStepId = this.getAttoDiNominaStepId();
          this.wizardStepService.markStepCompleted(attoDiNominaStepId, true);
          
          // Navigate to the correct Gestione Documenti step (step 3 for supplier, step 4 for professional)
          const gestioneDocumentiStepId = this.getGestioneDocumentiStepId();
          this.currentStep.set(gestioneDocumentiStepId);
          this.maxStepReached.update((max) => Math.max(max, gestioneDocumentiStepId));

          // Salva i nuovi valori come originali per step 2
          this.originalFormValues.set(2, JSON.parse(JSON.stringify(this.step2Form.value)));
          this.step2Form.markAsPristine();

          this.messageService.add({
            severity: 'success',
            summary: 'Dati salvati',
            detail: 'Atto di nomina salvato con successo',
            life: 3000,
          });

          this.isLoading.set(false);

          setTimeout(() => {
            window.scrollTo(0, 0);
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
          }, 200);
        },
        error: (err) => {
          console.error('Error saving Atto di Nomina:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: "Errore nel salvataggio dell'Atto di Nomina",
            life: 3000,
          });
          this.isLoading.set(false);
        },
      });
    } else {
      this.markFormGroupTouched(this.step2Form);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Compila tutti i campi obbligatori',
        life: 3000,
      });
    }
  }

  // Salva bozza senza validazione completa e senza cambiare step
  /**
   * Check for special characters errors in form
   * @param formGroup FormGroup to check
   * @returns Array of field names with special characters errors
   */
  private hasSpecialCharsErrors(formGroup: FormGroup): string[] {
    const fieldsWithErrors: string[] = [];
    const fieldLabels: { [key: string]: string } = {
      // Step 1 fields
      contractSubject: 'Oggetto del Contratto',
      slaKpiPerson: 'Responsabile SLA/KPI',
      rebatesNote: 'Note Rebates',
      eventualReferent: 'Eventuale Referente',
      buyer: 'Buyer',
      invoiceRecipientEmail: 'Email Destinatario Fattura',
      controlsOwner: 'Responsabile Controlli',
      paymentAuthorizer: 'Autorizzatore Pagamento',
      supplierSignatory: 'Firmatario Fornitore',
      supplierSignatoryRole: 'Ruolo Firmatario Fornitore',
      professionalSignatory: 'Firmatario Fiduciario',
      professionalSignatoryRole: 'Ruolo Firmatario Fiduciario',
      
      // Step 2 fields
      responsibleBusinessName: 'Ragione Sociale Responsabile',
      responsibleDpoName: 'Nome DPO Responsabile',
      headquarters: 'Sede',
      processedDataNotes: 'Note Dati Trattati',
      processDescription: 'Descrizione Processo',
      dataProcessingLocation: 'Luogo Trattamento Dati',
      externalSysAdminsText: 'Amministratori Sistema Esterni',
      responsibleDpoEmail: 'Email DPO Responsabile',
    };

    // Check direct form controls
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control?.hasError('specialChars') && fieldLabels[key]) {
        fieldsWithErrors.push(fieldLabels[key]);
      }
    });

    return fieldsWithErrors;
  }

  saveDraft(stepId: number): void {
    // Check for special characters errors in the current step form
    const currentForm = stepId === 1 ? this.step1Form : stepId === 2 ? this.step2Form : null;
    if (currentForm) {
      const specialCharsFields = this.hasSpecialCharsErrors(currentForm);
      if (specialCharsFields.length > 0) {
        currentForm.markAllAsTouched();
        this.messageService.add({
          severity: 'error',
          summary: 'Caratteri non permessi',
          detail: `I seguenti campi contengono caratteri speciali non permessi (< > " ' & / \\): ${specialCharsFields.join(', ')}. Correggili prima di salvare.`,
          life: 6000,
        });
        return;
      }
    }

    // Check for GDPR change from true to false with existing atto di nomina (Step 1 only)
    if (stepId === 1) {
      const originalValues = this.originalFormValues.get(1);
      const originalGdpr = originalValues?.gdprDataProcessing;
      const currentGdpr = this.step1Form.get('gdprDataProcessing')?.value;

      if (originalGdpr === true && currentGdpr === false && this.hasAttoDiNominaData()) {
        // Show warning dialog before saving
        this.confirmationService.confirm({
          message:
            "Disabilitando la gestione dati GDPR, tutti i dati inseriti nell'Atto di Nomina verranno persi. Continuare?",
          header: 'Attenzione',
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'Sì, continua',
          rejectLabel: 'Annulla',
          accept: () => {
            // Proceed with save and then delete atto di nomina
            this.saveDraftStep1WithDeleteAttoDiNomina();
          },
        });
        return;
      }
    }

    this.isLoading.set(true);
    this.loadingMessage.set(
      stepId === 3 ? 'Salvataggio dati in corso...' : 'Salvataggio bozza in corso...',
    );

    // Step 2 usa endpoint specifico per Atto di Nomina
    if (stepId === 2) {
      if (!this.contractId()) {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Contratto non trovato. Completare prima lo Step 1.',
          life: 3000,
        });
        this.isLoading.set(false);
        return;
      }

      // Verifica che almeno il responsabile sia selezionato
      const responsibleId = this.step2Form.get('responsibleId')?.value;
      const hasBackofficeRequiredData = this.hasRequiredBackofficeResponsibleDraftData();
      if (!responsibleId && !(this.isBackofficeProfessional() && hasBackofficeRequiredData)) {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: this.isBackofficeProfessional()
            ? 'Compila almeno Ragione sociale del Responsabile e Nome e cognome del DPO per salvare la bozza.'
            : 'È necessario selezionare Responsabile del trattamento per salvare la bozza.',
          life: 3000,
        });
        this.isLoading.set(false);
        return;
      }

      const attoDiNominaPayload = this.buildAttoDiNominaPayload('DRAFT');

      // Log del payload completo per confronto con struttura BE
      console.log('===== ATTO DI NOMINA PAYLOAD (SAVE DRAFT) =====');
      console.log('Payload Object:', attoDiNominaPayload);
      console.log('Payload JSON:', JSON.stringify(attoDiNominaPayload, null, 2));
      console.log('===============================================');

      // Use POST for new Atto di Nomina, PUT for updates
      const saveObservable = this.attoDiNominaExists()
        ? this.contractRegistryService.updateAttoDiNomina(this.contractId()!, attoDiNominaPayload)
        : this.contractRegistryService.createAttoDiNomina(this.contractId()!, attoDiNominaPayload);

      saveObservable.subscribe({
        next: (response) => {
          console.log('Draft Atto di Nomina saved:', response);
          this.attoDiNominaExists.set(true); // After first save, it exists
          this.attoDiNominaStatus.set('DRAFT'); // Set status to DRAFT

          // Mark Atto di Nomina step as not completed when saved as DRAFT
          this.wizardStepService.markStepCompleted(this.getAttoDiNominaStepId(), false);

          const form = this.getFormByStepId(stepId);
          if (form) {
            this.originalFormValues.set(stepId, JSON.parse(JSON.stringify(form.value)));
            form.markAsPristine();
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Bozza salvata',
            detail: "La bozza dell'Atto di Nomina è stata salvata con successo",
          });
          this.isLoading.set(false);
        },
        error: (err) => {
          this.isLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Errore nel salvataggio della bozza',
          });
          console.error('Error saving draft Atto di Nomina:', err);
        },
      });
      return;
    }

    // Step Gestione Documenti: Salva i dati dei template documenti
    if (stepId === this.getGestioneDocumentiStepId()) {
      if (!this.contractId()) {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Contratto non trovato. Completare prima gli step precedenti.',
          life: 3000,
        });
        this.isLoading.set(false);
        return;
      }

      const documentsPayload = {
        contractTemplateId: this.step3Form.get('templateContratto')?.value || null,
        attoDiNominaTemplateId: this.step3Form.get('templateAttoNomina')?.value || null,
        contractDocumentsNote: this.step3Form.get('documentNotes')?.value || null,
        status: 'DRAFT' as 'DRAFT' | 'GENERATED',
      };

      this.contractRegistryService
        .updateContractDocuments(this.contractId()!, documentsPayload)
        .subscribe({
          next: (response) => {
            console.log('Contract documents draft saved:', response);

            const form = this.getFormByStepId(stepId);
            if (form) {
              this.originalFormValues.set(stepId, JSON.parse(JSON.stringify(form.value)));
              form.markAsPristine();
            }

            // Mark step 3 as completed based on GDPR setting and templates selection
            const hasGdpr = this.shouldShowAttoDiNomina();
            const hasContractTemplateId = documentsPayload.contractTemplateId;
            const hasAttoDiNominaTemplateId = documentsPayload.attoDiNominaTemplateId;
            let isStep3Complete = false;

            if (hasGdpr) {
              // Se GDPR abilitato, step 3 completo solo se entrambi i template sono selezionati
              isStep3Complete = !!hasContractTemplateId && !!hasAttoDiNominaTemplateId;
            } else {
              // Se GDPR disabilitato, step 3 completo se template contratto è selezionato
              isStep3Complete = !!hasContractTemplateId;
            }

            if (isStep3Complete) {
              this.wizardStepService.markStepCompleted(3, true);
              console.log('✅ Step 3 marked as completed after draft save', {
                hasGdpr,
                hasContractTemplateId,
                hasAttoDiNominaTemplateId,
              });
            } else {
              this.wizardStepService.markStepCompleted(3, false);
              console.log('⚠️ Step 3 NOT marked as completed after draft save', {
                hasGdpr,
                hasContractTemplateId,
                hasAttoDiNominaTemplateId,
              });
            }

            this.messageService.add({
              severity: 'success',
              summary: 'Dati salvati',
              detail: 'Template salvati con successo',
            });
            this.isLoading.set(false);
          },
          error: (err) => {
            this.isLoading.set(false);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Errore nel salvataggio dei template',
            });
            console.error('Error saving contract documents draft:', err);
          },
        });
      return;
    }

    // Per gli altri step, usa la logica normale
    const payload = this.buildContractPayload('DRAFT');
    const previousEntityCodes = this.getInvolvedEntityCodes(
      this.originalFormValues.get(1)?.involvedEntities,
    );

    if (!this.contractId()) {
      // Prima volta: POST
      this.contractRegistryService.createContract(payload).subscribe({
        next: (response) => {
          console.log('Draft contract created:', response);
          this.contractId.set(response.data);
          this.checklistService.setChecklistAvailability(true);

          const form = this.getFormByStepId(stepId);
          if (form) {
            // Salva i valori attuali come originali (anche se non tutti validi)
            this.originalFormValues.set(stepId, JSON.parse(JSON.stringify(form.value)));
            // Marca il form come pristine
            form.markAsPristine();
          }

          // Update step visibility after saving step 1 (to enable/disable step 2 based on GDPR)
          if (stepId === 1) {
            this.updateStepVisibility();
            // Update contractSectionStatus signal and mark step as not completed
            this.contractSectionStatus.set('DRAFT');
            this.wizardStepService.markStepCompleted(1, false);
          }

          const complete = () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Bozza salvata',
              detail: 'La bozza è stata salvata con successo',
            });
            this.isLoading.set(false);
          };
          if (stepId === 1) {
            this.synchronizeAttoAfterContractSave(previousEntityCodes, complete);
          } else {
            complete();
          }
        },
        error: (err) => {
          this.isLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Errore nel salvataggio della bozza',
          });
          console.error('Error saving draft:', err);
        },
      });
    } else {
      // Aggiornamento: PUT
      this.contractRegistryService.updateContract(this.contractId()!, payload).subscribe({
        next: (response) => {
          console.log('Draft contract updated:', response);

          const form = this.getFormByStepId(stepId);
          if (form) {
            // Salva i valori attuali come originali (anche se non tutti validi)
            this.originalFormValues.set(stepId, JSON.parse(JSON.stringify(form.value)));
            // Marca il form come pristine
            form.markAsPristine();
          }

          // Update step visibility after saving step 1 (to enable/disable step 2 based on GDPR)
          if (stepId === 1) {
            this.updateStepVisibility();
            // Update contractSectionStatus signal and mark step as not completed
            this.contractSectionStatus.set('DRAFT');
            this.wizardStepService.markStepCompleted(1, false);
          }

          const complete = () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Bozza aggiornata',
              detail: 'La bozza è stata aggiornata con successo',
            });
            this.isLoading.set(false);
          };
          if (stepId === 1) {
            this.synchronizeAttoAfterContractSave(previousEntityCodes, complete);
          } else {
            complete();
          }
        },
        error: (err) => {
          this.isLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: "Errore nell'aggiornamento della bozza",
          });
          console.error('Error updating draft:', err);
        },
      });
    }
  }

  // Step 3: Gestione Documenti
  // Step 3: Salva e Genera Contratto dal Backend
  saveStep3AndGenerate(): void {
    console.log('🚀 Salva e genera contratto via Backend');

    if (!this.step3Form.valid) {
      this.markFormGroupTouched(this.step3Form);
      const hasGdpr = this.shouldShowAttoDiNomina();
      const errorMessage = hasGdpr
        ? 'Seleziona entrambi i template (Contratto e Atto di Nomina)'
        : 'Seleziona il template del Contratto';
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: errorMessage,
        life: 3000,
      });
      return;
    }

    const templateContrattoId = this.step3Form.get('templateContratto')?.value;
    const supplierId = this.step0Form.get('supplierId')?.value
      || this.selectedProfessionalId()
      || 'unknown';
    const contractId = this.contractId();

    if (!supplierId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID Fornitore mancante',
        life: 3000,
      });
      return;
    }

    if (!contractId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Contratto non trovato. Completare prima gli step precedenti.',
        life: 3000,
      });
      return;
    }

    if (!templateContrattoId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Seleziona un template per il contratto',
        life: 3000,
      });
      return;
    }

    this.isGeneratingWord.set(true);

    // Step 1: Salva con status DRAFT
    const draftPayload = {
      contractTemplateId: templateContrattoId || null,
      attoDiNominaTemplateId: this.step3Form.get('templateAttoNomina')?.value || null,
      contractDocumentsNote: this.step3Form.get('documentNotes')?.value || null,
      status: 'DRAFT' as 'DRAFT' | 'GENERATED',
    };

    this.contractRegistryService
      .updateContractDocuments(this.contractId()!, draftPayload)
      .subscribe({
        next: () => {
          console.log(
            '✅ Step 1: Template IDs saved with DRAFT status, calling backend processing...',
          );

          // Step 2: Call backend v2 renderDocx to produce the DOCX blob directly
          // POST /pal-dmn-documents/api/v2/document-templates/{businessKey}/render-docx
          // Payload: { contextId, contextType, outputFormat: 'DOCX' }
          this.documentTemplateV2Service
            .renderDocx(templateContrattoId, {
              contextId: contractId,
              contextType: 'CONTRACT',
              outputFormat: 'DOCX',
            })
            .subscribe({
              next: (blob) => {
                console.log('✅ Step 2: Backend v2 render complete, blob size:', blob.size);

                // Trigger client-side download of the DOCX blob
                const fileName = `contratto_${supplierId}_${new Date().getTime()}.docx`;
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);

                // Step 3: Re-save with status GENERATED after successful document generation
                const generatedPayload = {
                  contractTemplateId: templateContrattoId || null,
                  attoDiNominaTemplateId: this.step3Form.get('templateAttoNomina')?.value || null,
                  contractDocumentsNote: this.step3Form.get('documentNotes')?.value || null,
                  status: 'GENERATED' as 'DRAFT' | 'GENERATED',
                };

                this.contractRegistryService
                  .updateContractDocuments(this.contractId()!, generatedPayload)
                  .subscribe({
                    next: () => {
                      console.log(
                        '✅ Step 3: Contract status updated to GENERATED via PATCH /documents',
                      );

                      // Marca il form come pristine e salva i valori come "originali"
                      this.step3Form.markAsPristine();
                      this.originalFormValues.set(
                        3,
                        JSON.parse(JSON.stringify(this.step3Form.value)),
                      );

                      this.isGeneratingWord.set(false);
                      this.wizardStepService.markStepCompleted(3, true);

                      this.messageService.add({
                        severity: 'success',
                        summary: 'Successo',
                        detail: 'Documento Word generato e scaricato con successo',
                        life: 5000,
                      });

                      // Redirect to contract detail after successful generation
                      setTimeout(() => {
                        this.router.navigate([`/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry`, contractId]);
                      }, 1500);
                    },
                    error: (err: any) => {
                      console.error('❌ Error updating status to GENERATED:', err);
                      // Document was generated, but status update failed - still show success to user
                      this.isGeneratingWord.set(false);
                      this.wizardStepService.markStepCompleted(3, true);
                      this.messageService.add({
                        severity: 'warn',
                        summary: 'Attenzione',
                        detail: "Documento generato ma errore nell'aggiornamento dello stato",
                        life: 3000,
                      });
                    },
                  });
              },
              error: (error: any) => {
                console.error('❌ Backend v2 render error:', error);
                this.messageService.add({
                  severity: 'error',
                  summary: 'Errore',
                  detail: 'Errore nella generazione del documento',
                  life: 3000,
                });
                this.isGeneratingWord.set(false);
              },
            });
        },
        error: (err: any) => {
          console.error('❌ Error saving template IDs with DRAFT status:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Errore nel salvataggio dei template',
          });
          this.isGeneratingWord.set(false);
        },
      });
  }

  goToPreviousStep(): void {
    if (this.currentStep() > 0) {
      const prevStep = this.currentStep() - 1;
      this.currentStep.set(prevStep);
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 200);
    }
  }

  goToStep(stepId: number): void {
    // Non fare nulla se si clicca sullo step corrente
    if (stepId === this.currentStep()) {
      return;
    }

    const steps = this.wizardStepService.steps();
    const targetStep = steps.find((s) => s.id === stepId);

    // Verifica se lo step è disabilitato
    if (targetStep?.disabled) {
      return;
    }

    // Tutti gli step sono ora navigabili liberamente senza restrizioni (tranne se disabilitati)
    const canNavigate = targetStep !== undefined;

    if (!canNavigate) {
      return;
    }

    // Verifica se ci sono modifiche non salvate nel form corrente
    const currentForm = this.getCurrentForm();
    const hasUnsavedChanges = currentForm?.dirty || false;

    if (hasUnsavedChanges) {
      // Mostra dialog di conferma con testo standard
      this.confirmDialogService.confirmCancelEdit(() => {
        // Ripristina i valori originali del form corrente
        const currentStepId = this.currentStep();
        if (currentForm && this.originalFormValues.has(currentStepId)) {
          const originalValues = this.originalFormValues.get(currentStepId);
          currentForm.reset(originalValues);
        }
        currentForm?.markAsPristine();
        currentForm?.markAsUntouched();

        // Procedi con il cambio step
        this.performStepChange(stepId);
      });
    } else {
      // Nessuna modifica, procedi direttamente
      this.performStepChange(stepId);
    }
  }

  private performStepChange(stepId: number): void {
    this.currentStep.set(stepId);

    // Pre-compila processDescription dell'Atto di Nomina con contractSubject dello step Contratto
    if (stepId === this.getAttoDiNominaStepId()) {
      const processDescValue = this.step2Form.get('processDescription')?.value;
      const contractSubjectValue = this.step1Form.get('contractSubject')?.value;
      if ((!processDescValue || processDescValue.trim() === '') && contractSubjectValue) {
        this.step2Form.patchValue({ processDescription: contractSubjectValue });
      }
    }

    // Salva i valori originali del nuovo step quando ci entri (per poterli ripristinare se necessario)
    setTimeout(() => {
      const targetForm = this.getFormByStepId(stepId);
      if (targetForm && !this.originalFormValues.has(stepId)) {
        // Salva una copia profonda dei valori solo la prima volta che entri nello step
        this.originalFormValues.set(stepId, JSON.parse(JSON.stringify(targetForm.value)));
      }

      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 200);
  }

  private getFormByStepId(stepId: number): FormGroup | null {
    switch (stepId) {
      case 0:
        return this.step0Form;
      case 1:
        return this.step1Form;
      case 2:
        return this.step2Form;
      case 3:
        return this.step3Form;
      default:
        return null;
    }
  }

  private getCurrentForm(): FormGroup | null {
    switch (this.currentStep()) {
      case 0:
        return this.step0Form;
      case 1:
        return this.step1Form;
      case 2:
        return this.step2Form;
      case 3:
        return this.step3Form;
      default:
        return null;
    }
  }

  navigateToRegister(): void {
    const contractsRegistryRoute = `/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry`;

    // Verifica se ci sono modifiche non salvate
    const currentForm = this.getCurrentForm();
    const hasUnsavedChanges = currentForm?.dirty || false;

    if (hasUnsavedChanges) {
      // Mostra dialog di conferma
      this.confirmDialogService.confirmNavigateToRegister(contractsRegistryRoute);
    } else {
      // Nessuna modifica, naviga direttamente
      this.router.navigate([contractsRegistryRoute]);
    }
  }

  openChecklist(): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Checklist',
      detail: 'Apertura checklist - funzionalità in sviluppo',
      life: 3000,
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      // Ricorsione per FormGroup annidati
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }

      // Ricorsione per FormArray (es. sub-responsabili, controllers)
      if (control instanceof FormArray) {
        control.controls.forEach((arrayControl) => {
          arrayControl.markAsTouched();
          if (arrayControl instanceof FormGroup) {
            this.markFormGroupTouched(arrayControl);
          }
        });
      }
    });
  }

  async onDocumentUpload(event: any): Promise<void> {
    const file = event.files[0];
    const title = this.currentDocumentTitle();

    if (!title.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Inserisci un titolo per il documento',
        life: 3000,
      });
      event.files = [];
      return;
    }

    // Check if contract is saved
    const contractId = this.contractId();
    if (!contractId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Salva il contratto prima di caricare documenti',
        life: 3000,
      });
      event.files = [];
      return;
    }

    // 🔒 Validazione completa del file con FileValidationService
    // Include: size, MIME type, extension, magic numbers (file signature)
    // Supporta: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX
    const validation = await this.fileValidationService.validateAttachment(file);
    if (!validation.valid) {
      this.messageService.add({
        severity: 'error',
        summary: '🔒 Validazione File Fallita',
        detail: validation.error || 'Il file non ha superato i controlli di sicurezza',
        life: 6000,
      });

      // Log dettagli per debugging (solo in sviluppo)
      if (validation.details) {
        console.warn('File validation failed:', validation.details);
      }

      event.files = [];
      return;
    }

    this.isUploadingAttachment.set(true);

    // Call upload API (nuova API per attachments)
    this.contractRegistryService
      .uploadContractAttachment(contractId, file, title)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          // Reload attachments from server to get the complete list with IDs
          this.loadAttachments(contractId);

          this.messageService.add({
            severity: 'success',
            summary: 'Documento caricato',
            detail: `Documento "${title}" caricato con successo`,
            life: 3000,
          });

          // Close dialog and reset
          this.closeUploadDialog();
          event.files = [];
        },
        error: (error: any) => {
          console.error("Errore durante l'upload del documento:", error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: error?.error?.message || 'Errore durante il caricamento del documento',
            life: 5000,
          });
          this.isUploadingAttachment.set(false);
          event.files = [];
        },
      });
  }

  removeDocument(doc: any): void {
    const contractId = this.contractId();
    if (!contractId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'ID contratto non disponibile',
        life: 3000,
      });
      return;
    }

    // If document has documentId, it's on S3 and needs API deletion
    if (doc.documentId) {
      this.confirmDialogService.confirm({
        message: `Sei sicuro di voler eliminare "${escapeHtml(doc.title)}"?`,
        header: 'Conferma eliminazione',
        acceptLabel: 'Sì, elimina',
        rejectLabel: 'Annulla',
        accept: () => {
          this.contractRegistryService
            .deleteAttachment(contractId, doc.documentId.toString())
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Documento eliminato',
                  detail: `"${doc.title}" eliminato con successo`,
                  life: 3000,
                });

                // Reload attachments list
                this.loadAttachments(contractId);
              },
              error: (error: any) => {
                console.error('Error deleting document:', error);
                this.messageService.add({
                  severity: 'error',
                  summary: 'Errore',
                  detail: error?.error?.message || "Errore durante l'eliminazione del documento",
                  life: 5000,
                });
              },
            });
        },
      });
    } else {
      // Local file not yet on S3, just remove from array
      const docs = this.uploadedDocuments();
      this.uploadedDocuments.set(docs.filter((d) => d !== doc));

      this.messageService.add({
        severity: 'info',
        summary: 'Documento rimosso',
        detail: `Documento "${doc.title}" rimosso`,
        life: 3000,
      });
    }
  }

  // Helper method to build Atto di Nomina payload from step2Form
  private buildAttoDiNominaPayload(status: 'DRAFT' | 'FILLED' = 'FILLED'): any {
    const step2Values = this.step2Form.getRawValue();
    const step3Values = this.step3Form.value;

    // Debug: Log processedDataTypes before building payload
    console.log('processedDataTypes from form:', step2Values.processedDataTypes);
    console.log(
      'processedDataTypes type:',
      Array.isArray(step2Values.processedDataTypes)
        ? 'array'
        : typeof step2Values.processedDataTypes,
    );

    // Extract controllers array - only include if not empty
    const controllers = (step2Values.controllers || []).map((ctrl: any) => {
      const controllerPayload: any = {
        controllerEntity: ctrl.controllerEntity || null,
        controllerDpoEmail: ctrl.controllerDpoEmail || null,
        controllerOwnershipType: ctrl.controllerOwnershipType || null, // Always same field name for both contexts
      };
      // Include id only if it exists (for updates)
      if (ctrl.id) {
        controllerPayload.id = ctrl.id;
      }
      return controllerPayload;
    });

    // Extract sub-responsibles array if present and has data
    const subResponsibles = step2Values.usesSubResponsibles
      ? (step2Values.subResponsibles || []).map((subResp: any) => {
          const subRespPayload: any = {
            subResponsibleId: subResp.subResponsibleId,
            businessName: subResp.businessName || null,
            companyType: subResp.companyType || null,
            headquarters: subResp.headquarters || null,
            email: subResp.email || null,
            dataProcessingLocation: subResp.dataProcessingLocation || null,
            dataProcessingCountry: subResp.dataProcessingCountry || null, // Incluso sempre
            nonEuSafeguard:
              subResp.dataProcessingLocation === 'EXTRA_UE' ? subResp.nonEuSafeguard || null : null,
            dataTypes: subResp.dataTypes || [], // Array
            dataTypesNotes: subResp.dataTypesNotes || null,
            delegatedProcessesDescription: subResp.delegatedProcessesDescription || null,
            notes: subResp.notes || null,
          };
          // Include id only if it exists (for updates)
          if (subResp.id) {
            subRespPayload.id = subResp.id;
          }
          return subRespPayload;
        })
      : [];

    const payload: any = {
      // Status of Atto di Nomina
      status: status,

      // Controllers array - only include if not empty
      ...(controllers.length > 0 && { controllers }),

      // Responsible object (nested structure)
      responsible: {
        responsibleId: step2Values.responsibleId || null,
        responsibleBusinessName: step2Values.responsibleBusinessName || null,
        companyType: step2Values.companyType || null,
        responsibleDpoName: step2Values.responsibleDpoName || null,
        ...(this.isSelectedEntityProfessional() && {
          responsibleDpoSurname: step2Values.responsibleDpoSurname || null,
        }),
        headquarters: step2Values.headquarters || null,
        processedDataTypes: step2Values.processedDataTypes || [], // Array
        processedDataNotes: step2Values.processedDataNotes || null,
        processDescription: step2Values.processDescription || null,
        responsibleType: step2Values.responsibleType || null,
        dataProcessingLocation: step2Values.dataProcessingLocation || null,
        externalSysAdminsText: step2Values.externalSysAdminsText || null,
        responsibleDpoEmail: step2Values.responsibleDpoEmail || null,
        transferOutsideEu: step2Values.transferOutsideEu,
        extraEuLegalBasis: step2Values.transferOutsideEu
          ? step2Values.extraEuLegalBasis || null
          : null,
        thirdCountry: step2Values.transferOutsideEu ? step2Values.thirdCountry || null : null,
        tiaDone: step2Values.transferOutsideEu ? step2Values.tiaDone : null,
        tiaOutcome: step2Values.tiaDone ? step2Values.tiaOutcome || null : null,
        dataDeletionProgram: step2Values.dataDeletionProgram,
        criticalSupplierQualification: step2Values.criticalSupplierQualification || null,
        usesSubResponsibles: step2Values.usesSubResponsibles,
        notes: step2Values.notes || null,
        // Only include subResponsibles if array is not empty
        ...(subResponsibles.length > 0 && { subResponsibles }),
      },

      // Root-level fields
      measure: step2Values.measure || null,
      activityDescription: this.isSelectedEntityProfessional()
        ? (Array.isArray(step2Values.activityDescription) && step2Values.activityDescription.length > 0
            ? step2Values.activityDescription
            : null)
        : (Array.isArray(step2Values.activityDescription)
            ? (step2Values.activityDescription.length > 0 ? step2Values.activityDescription : null)
            : (step2Values.activityDescription ? [step2Values.activityDescription] : null)),
      mainPurpose: this.resolveMainPurposeValue(step2Values),
      recipientCategory: this.resolveRecipientCategoryValue(step2Values.recipientCategory),
      recipientCategoryOther: this.normalizeStringArrayValue(step2Values.recipientCategory).includes('OTHER')
        ? step2Values.recipientCategoryOther?.trim() || null
        : null,
      adminsText: step2Values.adminsText || null,
      otherTools: step2Values.otherTools || null,
      category: this.normalizeStringArrayValue(step2Values.category),
      categoryOther: this.normalizeStringArrayValue(step2Values.category).includes('OTHER')
        ? step2Values.categoryOther?.trim() || null
        : null,
      retentionPeriod: step2Values.retentionPeriod?.toString() || null,
      retentionCriteria: step2Values.retentionCriteria || null,
      deletionMethod: step2Values.deletionMethod || null,
      usesCloud: step2Values.usesCloud || false,
      cloudInfo: step2Values.cloudInfo || null,

      // Template ID from step 3 to preserve it
      attoDiNominaTemplateId: step3Values.templateAttoNomina || null,
    };

    // Include contractId only if it exists
    if (this.contractId()) {
      payload.contractId = this.contractId();
    }

    // Include responsible.id only if it exists (for updates)
    // Note: typically not present in creation, auto-generated by backend

    // Debug: Log processedDataTypes in payload
    console.log(
      'processedDataTypes in payload.responsible:',
      payload.responsible?.processedDataTypes,
    );
    console.log('Full responsible object in payload:', payload.responsible);

    return payload;
  }

  // Helper method to build ContractRequest payload from forms
  private buildContractPayload(
    contractSectionStatus: 'DRAFT' | 'FILLED' = 'DRAFT',
  ): ContractRequest {
    const step1Values = this.step1Form.value;
    const step2Values = this.step2Form.value;

    // Convert dates to ISO format (YYYY-MM-DD) or null
    const formatDate = (date: Date | string | null): string | null =>
      this.contractRegistryService.formatContractDateForApi(date);

    // Helper to check if frameworkAgreement has any data
    const hasFrameworkData =
      step1Values.frameworkAgreement &&
      (step1Values.frameworkStartDate ||
        step1Values.frameworkEndDate ||
        step1Values.frameworkAutomaticRenewal !== null ||
        step1Values.frameworkRenewalNoticeDays !== null);

    // Get step 3 values to preserve document template information
    const step3Values = this.step3Form.value;

    const selectedEntityId = this.step0Form.get('supplierId')?.value || null;

    const payload: any = {
      generalInfo: {
        supplierId: this.isSelectedEntityProfessional() ? null : selectedEntityId,
        professionalId: this.isSelectedEntityProfessional() ? selectedEntityId : null,
        contractType: step1Values.contractType || null,
        serviceType: step1Values.serviceType || null,
        contractSubject: step1Values.contractSubject || null,
        outsourcedActivity: step1Values.outsourcedActivity || null,
        slaKpiPerson: step1Values.slaKpiPerson || null,
        startDate: formatDate(step1Values.startDate),
        endDate: formatDate(step1Values.endDate),
        numberOfRenewals: this.isNumberOfRenewalsRequired() ? step1Values.numberOfRenewals : null,
        automaticRenewal: step1Values.automaticRenewal || false,
        terminationNoticeDays: step1Values.terminationNoticeDays || null,
        renewalNoticeDays: step1Values.renewalNoticeDays || null,
        contractSectionStatus: contractSectionStatus,
        status: step1Values.status || 'DRAFT',
        involvedEntities: (step1Values.involvedEntities || []).map((entity: ContractInvolvedEntity) => ({
          entityCode: entity.entityCode || '',
          signatoryName: entity.signatoryName || '',
          signatoryRole: entity.signatoryRole || '',
        })),
        synergies: step1Values.synergies || false,
        synergiesCompanies: step1Values.synergiesCompanies || null,
        involvedSc: step1Values.involvedSc || null,
        supplierType: step1Values.supplierType || null,
        managementTools: this.isSelectedEntityProfessional() ? (step1Values.managementTools || []) : [],
        network: this.isSelectedEntityProfessional() ? (step1Values.network || []) : [],
        contractInNumber: step1Values.contractInNumber || null,
        // Include step 3 document fields to preserve them (attoDiNominaTemplateId goes via PATCH /documents only)
        contractTemplateId: step3Values.templateContratto || null,
        contractDocumentsNote: step3Values.documentNotes || null,
      },
      economics: {
        expectedFee: step1Values.expectedFee || false,
        rebates: step1Values.rebates || false,
        rebatesNote: step1Values.rebatesNote || null,
        invoiceOnSol: step1Values.invoiceOnSol || false,
        technicalAccount: step1Values.technicalAccount || false,
        allocationMatrix: step1Values.allocationMatrix || null,
        expenseAccount: step1Values.expenseAccount || null,
        currency: step1Values.currency || 'EUR',
        totalContractValue: step1Values.totalContractValue || null,
        annualValue: step1Values.annualValue || null,
        invoiceFrequency: step1Values.invoiceFrequency || null,
      },
      administration: {
        managingArea: step1Values.managingArea || null,
        claimsAreaReference: step1Values.claimsAreaReference || null,
        eventualReferent: step1Values.eventualReferent || null,
        buyer: step1Values.buyer || null,
        involvedStructures: step1Values.involvedStructures || [],
        invoiceRecipientEmail: step1Values.invoiceRecipientEmail || null,
        controlsOwner: step1Values.controlsOwner || null,
        paymentAuthorizer: step1Values.paymentAuthorizer || null,
        documentationAtIP: step1Values.documentationAtIP || false,
        supplierSignatory: step1Values.supplierSignatory || null,
        supplierSignatoryRole: step1Values.supplierSignatoryRole || null,
        professionalSignatory: step1Values.professionalSignatory || null,
        professionalSignatoryRole: step1Values.professionalSignatoryRole || null,
      },
      dataProcessing: {
        vrfUseCase: step1Values.vrfUseCase || null,
        gdprDataProcessing: step1Values.gdprDataProcessing || false,
        privacyResponsibleFunction: step1Values.gdprDataProcessing
          ? step1Values.privacyResponsibleFunction
          : null,
        dataTransferOutsideSee: step1Values.dataTransferOutsideSee || false,
      },
    };

    // Only include frameworkAgreement if it has data
    if (hasFrameworkData) {
      payload.frameworkAgreement = {
        frameworkAgreement: step1Values.frameworkAgreement || false,
        frameworkStartDate: formatDate(step1Values.frameworkStartDate),
        frameworkEndDate: formatDate(step1Values.frameworkEndDate),
        automaticRenewal: step1Values.frameworkAutomaticRenewal,
        renewalNoticeDays: step1Values.frameworkRenewalNoticeDays,
      };
    }

    return payload as ContractRequest;
  }

  // Helper method to save contract to backend (POST or PUT)
  private synchronizeAttoAfterContractSave(previousEntityCodes: string[], onComplete: () => void): void {
    const currentEntityCodes = this.getInvolvedEntityCodes(this.involvedEntitiesArray.value);
    const previousEntitySet = new Set(previousEntityCodes);
    const currentEntitySet = new Set(currentEntityCodes);
    const controllerEntitySet = new Set(
      this.controllersArray.value
        .map((controller: any) => controller?.controllerEntity)
        .filter((entityCode: unknown): entityCode is string => !!entityCode),
    );
    const entitiesChanged =
      previousEntitySet.size !== currentEntitySet.size ||
      [...previousEntitySet].some((entityCode) => !currentEntitySet.has(entityCode));
    const controllersOutOfSync =
      controllerEntitySet.size !== currentEntitySet.size ||
      [...currentEntitySet].some((entityCode) => !controllerEntitySet.has(entityCode));

    if ((!entitiesChanged && !controllersOutOfSync) || !this.contractId()) {
      onComplete();
      return;
    }

    this.reconcileControllersWithInvolvedEntities();

    if (!this.attoDiNominaExists()) {
      onComplete();
      return;
    }

    const payload = this.buildAttoDiNominaPayload('DRAFT');
    this.contractRegistryService.updateAttoDiNomina(this.contractId()!, payload).subscribe({
      next: () => {
        this.attoDiNominaStatus.set('DRAFT');
        this.wizardStepService.markStepCompleted(this.getAttoDiNominaStepId(), false);
        onComplete();
      },
      error: (err) => {
        console.error('Error synchronizing Atto di Nomina controllers:', err);
        this.messageService.add({
          severity: 'warn',
          summary: 'Atto di Nomina non aggiornato',
          detail: 'Il Contratto è stato salvato, ma la sincronizzazione dei titolari non è riuscita.',
          life: 5000,
        });
        onComplete();
      },
    });
  }

  private saveContractToBackend(
    contractSectionStatus: 'DRAFT' | 'FILLED' = 'DRAFT',
    onSuccess?: () => void,
    synchronizeAtto = true,
  ): void {
    const payload = this.buildContractPayload(contractSectionStatus);
    const previousEntityCodes = this.getInvolvedEntityCodes(
      this.originalFormValues.get(1)?.involvedEntities,
    );
    this.isLoading.set(true);
    this.loadingMessage.set('Salvataggio contratto in corso...');

    // Update contractSectionStatus signal
    this.contractSectionStatus.set(contractSectionStatus);

    // Update step 1 completion status based on contractSectionStatus
    // FILLED = completed, DRAFT = not completed
    const isStep1Completed = contractSectionStatus === 'FILLED';
    this.wizardStepService.markStepCompleted(1, isStep1Completed);

    if (!this.contractId()) {
      // First time: POST
      this.contractRegistryService.createContract(payload).subscribe({
        next: (response) => {
          console.log('Contract created:', response);
          // Response structure: { status: true, message: "...", data: contractId }
          this.contractId.set(response.data);
          this.checklistService.setChecklistAvailability(true);
          this.hasSuccessfullySaved.set(true);
          this.step1Form.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: response.message || 'Contratto salvato con successo',
          });
          const complete = () => {
            this.isLoading.set(false);
            if (onSuccess) onSuccess();
          };
          if (synchronizeAtto) {
            this.synchronizeAttoAfterContractSave(previousEntityCodes, complete);
          } else {
            complete();
          }
        },
        error: (err) => {
          this.isLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Errore nel salvataggio del contratto',
          });
          console.error('Error saving contract:', err);
        },
      });
    } else {
      // Update: PUT
      this.contractRegistryService.updateContract(this.contractId()!, payload).subscribe({
        next: (response) => {
          console.log('Contract updated:', response);
          this.hasSuccessfullySaved.set(true);
          this.step1Form.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: 'Contratto aggiornato con successo',
          });
          const complete = () => {
            this.isLoading.set(false);
            if (onSuccess) onSuccess();
          };
          if (synchronizeAtto) {
            this.synchronizeAttoAfterContractSave(previousEntityCodes, complete);
          } else {
            complete();
          }
        },
        error: (err) => {
          this.isLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: "Errore nell'aggiornamento del contratto",
          });
          console.error('Error updating contract:', err);
        },
      });
    }
  }

  // =====================================================================
  // CHECKLIST MANAGEMENT
  // =====================================================================

  /**
   * Open checklist dialog and load checklist data
   */
  openChecklistDialog(): void {
    const contractId = this.contractId();

    if (!contractId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Contratto non ancora salvato. Completa prima i dati del contratto.',
        life: 3000,
      });
      return;
    }

    this.checklistHasUnsavedChanges.set(false);
    this.showChecklistDialog.set(true);
    this.loadChecklist();
  }

  /**
   * Load checklist data from backend
   */
  private loadChecklist(): void {
    const contractId = this.contractId();
    if (!contractId) return;

    this.loadingChecklist.set(true);

    this.checklistService.getChecklist(contractId).subscribe({
      next: (response) => {
        console.log('✅ Checklist loaded:', response);
        if (response.status && response.data) {
          // Deep clone to preserve original state
          const clonedData = JSON.parse(JSON.stringify(response.data));
          this.checklistData.set(response.data);
          this.originalChecklistData.set(clonedData);
          this.checklistHasUnsavedChanges.set(false);
        }
        this.loadingChecklist.set(false);
      },
      error: (error) => {
        console.error('❌ Error loading checklist:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare la checklist',
          life: 3000,
        });
        this.loadingChecklist.set(false);
      },
    });
  }

  /**
   * Close checklist dialog (called by Cancel button)
   */
  closeChecklistDialog(): void {
    if (this.checklistHasUnsavedChanges()) {
      this.confirmDialogService.confirmCancelEdit(
        () => {
          // Accept: close the dialog
          this.resetChecklistState();
        },
        // Reject: do nothing, dialog stays open
      );
    } else {
      this.resetChecklistState();
    }
  }

  /**
   * Handle dialog hide event (when dialog is closed)
   */
  onChecklistDialogHide(): void {
    // If there are unsaved changes, ask for confirmation
    if (this.checklistHasUnsavedChanges()) {
      // Re-open the dialog immediately to prevent it from closing
      this.showChecklistDialog.set(true);

      // Show confirmation dialog
      this.confirmDialogService.confirmCancelEdit(
        () => {
          // Accept: close the dialog
          this.resetChecklistState();
        },
        // Reject: dialog is already re-opened, do nothing
      );
    } else {
      // No unsaved changes, safe to close
      this.resetChecklistState();
    }
  }

  /**
   * Reset checklist state
   */
  private resetChecklistState(): void {
    this.showChecklistDialog.set(false);
    this.checklistData.set(null);
    this.originalChecklistData.set(null);
    this.checklistHasUnsavedChanges.set(false);
  }

  /**
   * Toggle checkbox completion
   */
  toggleChecklistRow(row: ChecklistRow): void {
    // Mark as having unsaved changes
    this.checklistHasUnsavedChanges.set(true);
  }

  /**
   * Save checklist changes
   */
  saveChecklist(): void {
    const contractId = this.contractId();
    const checklist = this.checklistData();

    if (!contractId || !checklist) return;

    this.loadingChecklist.set(true);

    const payload = {
      rows: checklist.rows.map((row) => ({
        rowId: row.rowId,
        completed: row.completed,
      })),
    };

    this.checklistService.updateChecklist(contractId, checklist.id, payload).subscribe({
      next: (response) => {
        console.log('✅ Checklist saved:', response);
        if (response.status && response.data) {
          this.checklistData.set(response.data);
          this.originalChecklistData.set(JSON.parse(JSON.stringify(response.data)));
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Checklist aggiornata con successo',
          life: 3000,
        });
        this.loadingChecklist.set(false);
        this.resetChecklistState();
      },
      error: (error) => {
        console.error('❌ Error saving checklist:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore nel salvataggio della checklist',
          life: 3000,
        });
        this.loadingChecklist.set(false);
      },
    });
  }

  // ==================== DOCUMENTS MANAGEMENT ====================

  getFileExtension(fileName: string): string {
    if (!fileName) return '';
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
  }

  getFileIcon(fileName: string): string {
    const extension = this.getFileExtension(fileName);
    switch (extension) {
      case 'PDF':
        return 'pi pi-file-pdf';
      case 'XLSX':
      case 'XLS':
        return 'pi pi-file-excel';
      case 'DOCX':
      case 'DOC':
        return 'pi pi-file-word';
      case 'PPTX':
      case 'PPT':
        return 'pi pi-file';
      default:
        return 'pi pi-file';
    }
  }

  getFileTypeSeverity(fileName: string): 'danger' | 'success' | 'info' | 'warn' {
    const extension = this.getFileExtension(fileName);
    switch (extension) {
      case 'PDF':
        return 'danger';
      case 'XLSX':
      case 'XLS':
        return 'success';
      case 'DOCX':
      case 'DOC':
        return 'info';
      case 'PPTX':
      case 'PPT':
        return 'warn';
      default:
        return 'info';
    }
  }

  openUploadDialog(): void {
    this.showUploadDialog.set(true);
    this.currentDocumentTitle.set('');
  }

  closeUploadDialog(): void {
    this.showUploadDialog.set(false);
    this.currentDocumentTitle.set('');
    this.isUploadingAttachment.set(false);
  }

  onDownloadDocument(doc: any): void {
    // For documents not yet on S3 (File objects), create a temporary download link
    if (doc.file && !doc.s3Url) {
      const url = window.URL.createObjectURL(doc.file);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      this.messageService.add({
        severity: 'success',
        summary: 'Download completato',
        detail: `"${doc.file.name}" scaricato con successo`,
        life: 3000,
      });
      return;
    }

    // For documents on S3, download from backend
    const contractId = this.contractId();
    if (!contractId || !doc.s3Url) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Documento non disponibile per il download',
        life: 3000,
      });
      return;
    }

    this.contractRegistryService
      .downloadAttachment(contractId, doc.documentId.toString())
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = doc.file.name || doc.title || 'documento.pdf';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          this.messageService.add({
            severity: 'success',
            summary: 'Download completato',
            detail: `"${doc.file.name || doc.title}" scaricato con successo`,
            life: 3000,
          });
        },
        error: (error: any) => {
          console.error('Error downloading document:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: error?.error?.message || 'Errore durante il download del documento',
            life: 5000,
          });
        },
      });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  formatUploadDate(date?: Date): string {
    if (!date) return 'Locale';
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    };
    return date.toLocaleDateString('it-IT', options);
  }

  /**
   * Load attachments for the contract when in edit mode
   */
  private loadAttachments(contractId: string): void {
    this.contractRegistryService
      .getAttachments(contractId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            // Convert backend attachments to uploadedDocuments format
            const attachments = response.data.map((att: any) => ({
              title: att.description || att.fileName,
              file: new File([], att.fileName), // Placeholder file object
              s3Url: att.s3FileUrl,
              uploadedAt: att.uploadedAt ? new Date(att.uploadedAt) : undefined,
              documentId: att.id, // Store ID for deletion
            }));
            this.uploadedDocuments.set(attachments);
          }
        },
        error: (error: any) => {
          console.error('Error loading attachments:', error);
        },
      });
  }

  /**
   * Handler for payment rates status change
   * Updates the tariffe step completion based on sectionStatus
   */
  onTariffeStatusChanged(status: 'DRAFT' | 'FILLED' | null): void {
    this.tariffeStatus.set(status);
    const isTariffeCompleted = status === 'FILLED';
    const tariffeStepId = this.getTariffeStepId();
    this.wizardStepService.markStepCompleted(tariffeStepId, isTariffeCompleted);
  }

  onTariffeSaveAndProceed(): void {
    // Naviga allo step successivo: Atto di Nomina (se necessario) o Gestione Documenti
    const nextStep = this.shouldShowAttoDiNomina() ? 3 : 4;
    this.goToStep(nextStep);
  }
}
